import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../../api/axios';
import Banner from '../../ui/Banner';
import Toast from '../../ui/Toast';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/KeycloakContext';
import NotificationBell from '../../layout/NotificationBell';
import countriesData from '../../data/countriesData.json';
import PipelineStageModal from './PipelineStageModal';
import {
  meetingsApi,
  isMeetingPast,
  isMeetingInProgress,
  getEffectiveMeetingStatus,
  canJoinMeeting,
  getMsUntilMeetingAccessOpen,
  formatMeetingCountdown,
} from '../meetings/api/meetingsApi';
import { LockFieldModal, EvidencePanel } from '../evidence';
import './CustomerManagementSystem.css';

// test
const FILTERS = [
  {
    label: 'Stages',
    key: 'stages',
    options: [
      'All stages',
      'New',
      'Contacted',
      'Qualified',
      'Proposal',
      'Negotiation',
      'Won',
      'Lost',
    ],
  },
  { label: 'Companies', key: 'companies', options: [] },
  { label: 'Industries', key: 'industries', options: [] },
  { label: 'Owners', key: 'owners', options: [] },
  {
    label: 'Sources',
    key: 'sources',
    options: ['All sources', 'Referral', 'Website', 'LinkedIn', 'Email'],
  },
  {
    label: 'Countries',
    key: 'countries',
    options: [],
  },
  {
    label: 'Lead score',
    key: 'leadScore',
    options: ['Any lead score', '0-20', '21-40', '41-60', '61-80', '81-100'],
  },
  {
    label: 'Created date',
    key: 'createdDate',
    options: [],
  },
  {
    label: 'Close date',
    key: 'closeDate',
    options: [
      'Any close date',
      'Overdue',
      'This week',
      'This month',
      'This quarter',
      'This year',
      'No date set',
    ],
  },
  {
    label: 'Outcome',
    key: 'outcome',
    options: ['Any outcome', 'Won', 'Lost', 'In progress'],
  },
  {
    label: 'Follow-up',
    key: 'followUp',
    options: ['Any follow-up', 'Overdue', 'Today', 'This week', 'Next week', 'No follow-up'],
  },
];

const SORT_OPTIONS = [
  { label: 'Created date', key: 'createdDate', icon: 'fas fa-calendar-plus' },
  { label: 'Updated date', key: 'updatedDate', icon: 'fas fa-calendar-alt' },
  { label: 'Expected closing date', key: 'expectedCloseDate', icon: 'fas fa-calendar-check' },
  { label: 'Last activity date', key: 'lastActivityDate', icon: 'fas fa-clock' },
  { label: 'Next follow-up date', key: 'nextFollowUpDate', icon: 'fas fa-bell' },
  { label: 'Lead score', key: 'leadScore', icon: 'fas fa-star' },
  { label: 'Deal value', key: 'dealValue', icon: 'fas fa-dollar-sign' },
];

const DEFAULT_STAGES = ['New', 'Contacted', 'Qualified', 'Proposal', 'Negotiation', 'Won', 'Lost'];

const COUNTRY_ALIASES = {
  us: 'united states',
  usa: 'united states',
  uk: 'united kingdom',
};

const cleanCompany = (value) => {
  if (!value || value === 'Unknown Company') return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    return trimmed;
  }
  if (typeof value === 'object') {
    if (typeof value.name === 'string' && value.name.trim()) return value.name.trim();
    if (typeof value.companyName === 'string' && value.companyName.trim())
      return value.companyName.trim();
    if (typeof value.company_name === 'string' && value.company_name.trim())
      return value.company_name.trim();
    if (value.company && typeof value.company === 'string' && value.company.trim())
      return value.company.trim();
    if (value.company && typeof value.company.name === 'string' && value.company.name.trim()) {
      return value.company.name.trim();
    }
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return String(value);
};

// Coerce contact fields (email/phone) into a plain display string. Values can be
// a string, an array of strings, or an array of objects (e.g. {email, emailType,
// emailConfidence}) depending on the provider that created the prospect.
const contactToString = (value, objectKeys) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    for (const item of value) {
      const s = contactToString(item, objectKeys);
      if (s) return s;
    }
    return '';
  }
  if (typeof value === 'object') {
    for (const key of objectKeys) {
      const s = contactToString(value[key], objectKeys);
      if (s) return s;
    }
  }
  return '';
};

const emailToString = (value) => contactToString(value, ['email']);
const phoneToString = (value) => contactToString(value, ['phone', 'number']);

const extractPhoneList = (value) => {
  if (!value) return [''];
  if (Array.isArray(value)) {
    const list = [];
    for (const item of value) {
      if (!item) continue;
      if (typeof item === 'string') {
        const trimmed = item.trim();
        if (trimmed && trimmed !== '[]' && trimmed !== '{}') list.push(trimmed);
      } else if (typeof item === 'object') {
        const num = item.number || item.phone || item.value || '';
        if (num && String(num).trim()) list.push(String(num).trim());
      }
    }
    return list.length > 0 ? list.slice(0, 3) : [''];
  }
  if (typeof value === 'object') {
    const num = value.number || value.phone || value.value || '';
    return num ? [String(num).trim()] : [''];
  }
  const str = String(value).trim();
  if (!str || str === '[]' || str === '{}' || str === '[""]') return [''];
  if (str.startsWith('[') && str.endsWith(']')) {
    try {
      const parsed = JSON.parse(str);
      return extractPhoneList(parsed);
    } catch (e) {
      // fallback
    }
  }
  if (str.includes(',')) {
    const split = str
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (split.length > 0) return split.slice(0, 3);
  }
  return [str];
};

const extractEmailList = (value) => {
  if (!value) return [''];
  if (Array.isArray(value)) {
    const list = [];
    for (const item of value) {
      if (!item) continue;
      if (typeof item === 'string') {
        const trimmed = item.trim();
        if (trimmed && trimmed !== '[]' && trimmed !== '{}') list.push(trimmed);
      } else if (typeof item === 'object') {
        const mail = item.email || item.value || item.address || '';
        if (mail && String(mail).trim()) list.push(String(mail).trim());
      }
    }
    return list.length > 0 ? list.slice(0, 3) : [''];
  }
  if (typeof value === 'object') {
    const mail = value.email || value.value || value.address || '';
    return mail ? [String(mail).trim()] : [''];
  }
  const str = String(value).trim();
  if (!str || str === '[]' || str === '{}' || str === '[""]') return [''];
  if (str.startsWith('[') && str.endsWith(']')) {
    try {
      const parsed = JSON.parse(str);
      return extractEmailList(parsed);
    } catch (e) {
      // fallback
    }
  }
  if (str.includes(',')) {
    const split = str
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (split.length > 0) return split.slice(0, 3);
  }
  return [str];
};

// When the server echoes the full note list back after a POST/DELETE/PATCH it
// is the database snapshot at the moment that request ran. That snapshot may be
// missing notes that were added optimistically while the request was still in
// flight — replacing the local list with it blindly can make just-added notes
// vanish ("note added but not shown"). Merge instead: keep the server list and
// append any pending optimistic notes (for this card) that are not already in it.
const noteKey = (n) => `${n.createdAt}|${n.createdBy}|${n.text}`;
const reconcileNotes = (serverNotes, fallback, pending, cardId) => {
  const base = Array.isArray(serverNotes) ? serverNotes : fallback;
  if (!Array.isArray(base)) return [];
  const keys = new Set(base.map(noteKey));
  const extras = [];
  if (pending instanceof Map) {
    pending.forEach((entry) => {
      if (entry && entry.cardId !== cardId) return;
      if (!keys.has(noteKey(entry.note))) {
        extras.push(entry.note);
        keys.add(noteKey(entry.note));
      }
    });
  }
  return extras.length ? [...base, ...extras] : base;
};

const normalizeSource = (src) => {
  if (!src) return 'Website';
  const s = String(src).trim().toLowerCase();
  if (s === 'prospect finder' || s === 'prospectfinder' || s === 'prospect_finder')
    return 'Prospect Finder';
  if (s === 'landing page' || s === 'landingpage' || s === 'landing_page') return 'Landing Page';
  if (s === 'linkedin') return 'LinkedIn';
  if (s === 'email') return 'Email';
  if (s === 'website' || s === 'web') return 'Website';
  return src.charAt(0).toUpperCase() + src.slice(1);
};

const isLandingPageLead = (card) => {
  if (!card) return false;
  if (card.provider === 'landing_page' || card.crm?.provider === 'landing_page') return true;
  const src = (card.source || card.crm?.source || '').trim().toLowerCase();
  if (src === 'landing page' || src === 'landingpage' || src === 'landing_page') {
    return true;
  }
  const ext = String(card.external_id || card.lusha_id || card.provider_id || '');
  if (ext.startsWith('lpsub-')) return true;
  return false;
};

const isProspectFinderLead = (card) => {
  if (isLandingPageLead(card)) return false;
  if (!card) return false;
  // Manual leads are NEVER locked
  if (card.provider === 'manual' || card.crm?.provider === 'manual') return false;
  const src = (card.source || card.crm?.source || '').toLowerCase();
  if (src === 'prospect finder' || src === 'prospectfinder' || src === 'prospect_finder') {
    return (card.provider || card.crm?.provider) !== 'manual';
  }
  if (card.provider && card.provider !== 'manual') return true;
  if (card.sourceProspectId || card.provider_id || card.external_id || card.lusha_id) {
    return card.provider !== 'manual';
  }
  return false;
};

const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  CAD: 'C$',
  JPY: '¥',
  CNY: '¥',
  HKD: 'HK$',
  SGD: 'S$',
  CHF: 'CHF ',
  INR: '₹',
  MYR: 'RM',
  NZD: 'NZ$',
};

const CURRENCIES = [
  'USD',
  'EUR',
  'GBP',
  'AUD',
  'CAD',
  'JPY',
  'CNY',
  'HKD',
  'SGD',
  'CHF',
  'INR',
  'MYR',
  'NZD',
];

const CURRENCY_NAMES = {
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'British Pound',
  AUD: 'Australian Dollar',
  CAD: 'Canadian Dollar',
  JPY: 'Japanese Yen',
  CNY: 'Chinese Yuan',
  HKD: 'Hong Kong Dollar',
  SGD: 'Singapore Dollar',
  CHF: 'Swiss Franc',
  INR: 'Indian Rupee',
  MYR: 'Malaysian Ringgit',
  NZD: 'New Zealand Dollar',
};

const CURRENCY_FLAGS = {
  USD: '🇺🇸',
  EUR: '🇪🇺',
  GBP: '🇬🇧',
  AUD: '🇦🇺',
  CAD: '🇨🇦',
  JPY: '🇯🇵',
  CNY: '🇨🇳',
  HKD: '🇭🇰',
  SGD: '🇸🇬',
  CHF: '🇨🇭',
  INR: '🇮🇳',
  MYR: '🇲🇾',
  NZD: '🇳🇿',
};

const CURRENCY_COUNTRY_CODES = {
  USD: 'us',
  EUR: 'eu',
  GBP: 'gb',
  AUD: 'au',
  CAD: 'ca',
  JPY: 'jp',
  CNY: 'cn',
  HKD: 'hk',
  SGD: 'sg',
  CHF: 'ch',
  INR: 'in',
  MYR: 'my',
  NZD: 'nz',
};

// Default fallback FX rates relative to USD (used before live API rates load or if offline)
const DEFAULT_FALLBACK_FX_RATES = {
  USD: 1.0,
  EUR: 0.92,
  GBP: 0.79,
  AUD: 1.52,
  CAD: 1.36,
  JPY: 154.0,
  CNY: 7.23,
  HKD: 7.82,
  SGD: 1.35,
  CHF: 0.91,
  INR: 83.5,
  MYR: 4.45,
  NZD: 1.66,
};

const convertCurrency = (
  amount,
  fromCurrency = 'USD',
  toCurrency = 'USD',
  ratesTable = DEFAULT_FALLBACK_FX_RATES
) => {
  const num = parseFloat(amount);
  if (isNaN(num) || num === 0) return 0;
  const from = String(fromCurrency || 'USD').toUpperCase();
  const to = String(toCurrency || 'USD').toUpperCase();
  if (from === to) return num;

  const rates = ratesTable || DEFAULT_FALLBACK_FX_RATES;
  const fromRate = rates[from] || 1.0;
  const toRate = rates[to] || 1.0;

  // Convert fromCurrency -> USD -> toCurrency
  const inUSD = num / fromRate;
  return inUSD * toRate;
};

const formatCurrency = (value, currency) => {
  if (value === undefined || value === null || value === '') return '';
  const symbol = CURRENCY_SYMBOLS[String(currency || '').toUpperCase()] || `${currency || ''} `;
  return `${symbol}${value}`;
};

const parseFlexibleDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const str = String(value).trim();
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(str)) {
    const [day, month, year] = str.split('/').map(Number);
    const d = new Date(year, month - 1, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [year, month, day] = str.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const raw = str.includes('T') ? str : `${str}T00:00:00`;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (value) => {
  const date = parseFlexibleDate(value);
  if (!date) return '-';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateShort = (value) => {
  const date = parseFlexibleDate(value);
  if (!date) return '';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
};

const getTaskDeadlineDate = (value) => {
  const date = parseFlexibleDate(value);
  if (!date) return null;
  const str = String(value || '').trim();
  const hasSpecificTime =
    str.includes('T') &&
    !str.endsWith('T00:00:00') &&
    !str.endsWith('T00:00:00.000Z') &&
    !str.endsWith('T00:00:00Z');

  if (hasSpecificTime) {
    return date;
  }
  // Date-only (e.g. 2 Sep): deadline is end of that day (23:59:59.999 local time)
  const endOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  return endOfDay;
};

const isFollowUpOverdue = (value) => {
  const deadline = getTaskDeadlineDate(value);
  if (!deadline) return false;
  const now = new Date();
  return now.getTime() > deadline.getTime();
};

const isFollowUpDueSoon = (value) => {
  const deadline = getTaskDeadlineDate(value);
  if (!deadline) return false;
  if (isFollowUpOverdue(value)) return false;

  const now = new Date();
  const diffMs = deadline.getTime() - now.getTime();

  // Due soon: within the 24 hours (86,400,000 ms) before the deadline (23:59:59)
  return diffMs > 0 && diffMs <= 24 * 60 * 60 * 1000;
};

const getFollowUpRemainingHours = (value) => {
  const deadline = getTaskDeadlineDate(value);
  if (!deadline) return null;
  const now = new Date();
  const diffMs = deadline.getTime() - now.getTime();
  if (diffMs <= 0) return 0;
  return Math.max(1, Math.round(diffMs / (60 * 60 * 1000)));
};

function getCardFollowUpDate(card) {
  if (card?.nextFollowUpDate || card?.next_follow_up_date) {
    return card.nextFollowUpDate || card.next_follow_up_date;
  }
  const tasks = Array.isArray(card?.tasks)
    ? card.tasks
    : Array.isArray(card?.crm?.tasks)
      ? card.crm.tasks
      : [];
  const pending = tasks.filter((t) => {
    const status = String(t?.status || (t?.completed ? 'completed' : 'pending')).toLowerCase();
    return !['completed', 'cancelled', 'done'].includes(status);
  });
  let earliestDate = null;
  let earliestRaw = null;
  for (const task of pending) {
    const raw = task?.dueDate || task?.due_date || task?.due;
    if (!raw) continue;
    const d = parseFlexibleDate(raw);
    if (d && (!earliestDate || d.getTime() < earliestDate.getTime())) {
      earliestDate = d;
      earliestRaw = raw;
    }
  }
  return earliestRaw || null;
}

function getCardCloseDateInfo(card) {
  const closeDateVal = card?.closeDate || card?.expectedCloseDate || card?.close_date;
  if (!closeDateVal) return null;
  const targetDate = parseFlexibleDate(closeDateVal);
  if (!targetDate) return null;

  const stage = String(card?.stage || '').toLowerCase();
  if (stage === 'won') {
    return {
      status: 'won',
      text: `Won (${formatDateShort(card.wonDate || closeDateVal)})`,
      tooltip: `🏆 Deal Won on ${formatDate(card.wonDate || closeDateVal)}`,
      icon: 'fas fa-trophy',
    };
  }
  if (stage === 'lost') {
    return {
      status: 'lost',
      text: `Closed Lost`,
      tooltip: `❌ Closed Lost on ${formatDate(closeDateVal)}`,
      icon: 'fas fa-times-circle',
    };
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
  const diffDays = Math.round((targetDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    const absDays = Math.abs(diffDays);
    return {
      status: 'overdue',
      text: `Close overdue (${formatDateShort(closeDateVal)})`,
      tooltip: `⚠️ Expected close date was ${formatDate(closeDateVal)} (${absDays} day${absDays > 1 ? 's' : ''} overdue)`,
      icon: 'fas fa-exclamation-circle',
    };
  }
  if (diffDays === 0) {
    return {
      status: 'due-today',
      text: `Closes today`,
      tooltip: `⏰ Expected to close today (${formatDate(closeDateVal)})`,
      icon: 'fas fa-clock',
    };
  }
  if (diffDays <= 3) {
    return {
      status: 'due-soon',
      text: `Closes in ${diffDays}d (${formatDateShort(closeDateVal)})`,
      tooltip: `⏳ Expected to close in ${diffDays} days (${formatDate(closeDateVal)})`,
      icon: 'fas fa-hourglass-half',
    };
  }
  return {
    status: 'normal',
    text: `Closes: ${formatDateShort(closeDateVal)}`,
    tooltip: `📅 Expected close date: ${formatDate(closeDateVal)}`,
    icon: 'fas fa-calendar-check',
  };
}

/**
 * Automated Lead Scoring Algorithm (0 - 100)
 *
 * A. Pipeline Stage (Max 30 pts)
 *    - Dynamic formula based on N active progression stages: round(30 * (i / N)^1.35)
 *      where i = 1-indexed stage position and N = total progression stages (excl. Won/Lost).
 *    - Default 5 Stages (N=5):
 *        * Stage 1 (New): +3 pts
 *        * Stage 2 (Contacted): +9 pts
 *        * Stage 3 (Qualified): +15 pts
 *        * Stage 4 (Proposal): +22 pts
 *        * Stage 5 (Negotiation): +30 pts
 *    - Won: 100 pts (immediately overrides total to 100)
 *    - Lost: 0 pts (immediately overrides total to 0)
 *
 * B. Seniority & Decision-Maker Fit (Max 25 pts)
 *    - C-Suite / Founder / VP: +25 pts
 *    - Director / Head of Department: +18 pts
 *    - Manager / Lead: +15 pts
 *    - Senior: +10 pts
 *    - Other / Individual Contributor: +5 pts
 *
 * C. Engagement & Follow-up Tasks (Max 25 pts)
 *    - Completed Follow-up Tasks: +5 pts each (up to max +15 pts for 3+ completed tasks)
 *    - Active Next Follow-up Scheduled: +6 pts
 *    - Timely / High-Priority Follow-up: +4 pts (within 7 days or marked high-priority)
 *
 * D. Contact Completeness (Max 10 pts)
 *    - Work Email Verified / Available: +5 pts
 *    - Phone Number Available: +3 pts
 *    - LinkedIn Profile Linked: +2 pts
 *
 * E. Commercial Intent (Max 10 pts)
 *    - Deal Value Specified (> $0): +5 pts
 *    - Product Interest / Proposal Reference Filled: +5 pts
 */
export function calculateDynamicStageScore(stage, allStages = []) {
  const normStage = String(stage || 'New')
    .trim()
    .toLowerCase();
  if (normStage === 'won') return { score: 100, label: 'Won (+100 pts)', isWon: true };
  if (normStage === 'lost') return { score: 0, label: 'Lost (0 pts)', isLost: true };

  const progressionStages =
    Array.isArray(allStages) && allStages.length > 0
      ? allStages.filter((s) => {
          const name = String(typeof s === 'string' ? s : s.name || '')
            .trim()
            .toLowerCase();
          return name !== 'won' && name !== 'lost';
        })
      : ['New', 'Contacted', 'Qualified', 'Proposal', 'Negotiation'];

  const N = progressionStages.length || 1;
  const stageIndex = progressionStages.findIndex((s) => {
    const name = String(typeof s === 'string' ? s : s.name || '')
      .trim()
      .toLowerCase();
    return name === normStage;
  });

  let stageScore = 5;
  if (stageIndex !== -1) {
    const i = stageIndex + 1; // 1-indexed
    stageScore = Math.max(1, Math.min(30, Math.round(30 * Math.pow(i / N, 1.35))));
  } else {
    // Fallback based on default progression
    const defaultProg = ['new', 'contacted', 'qualified', 'proposal', 'negotiation'];
    const defIdx = defaultProg.indexOf(normStage);
    if (defIdx !== -1) {
      const idx = defIdx + 1;
      stageScore = Math.max(
        1,
        Math.min(30, Math.round(30 * Math.pow(idx / defaultProg.length, 1.35)))
      );
    } else {
      stageScore = 5;
    }
  }
  const displayName = typeof stage === 'string' && stage ? stage : 'New';
  return { score: stageScore, label: `${displayName} (+${stageScore} pts)` };
}

function calculateLeadScore(lead, customStages = []) {
  if (!lead) return { totalScore: 0, breakdown: {} };

  const stage = String(lead.stage || 'New').trim();

  // Special overrides
  if (stage.toLowerCase() === 'won') {
    return {
      totalScore: 100,
      breakdown: {
        stageScore: 100,
        seniorityScore: 0,
        taskScore: 0,
        contactScore: 0,
        intentScore: 0,
        stageLabel: 'Won (+100 pts)',
      },
    };
  }

  if (stage.toLowerCase() === 'lost') {
    return {
      totalScore: 0,
      breakdown: {
        stageScore: 0,
        seniorityScore: 0,
        taskScore: 0,
        contactScore: 0,
        intentScore: 0,
        stageLabel: 'Lost (0 pts)',
      },
    };
  }

  // A. Dynamic Pipeline Stage Progression (Max 30 pts)
  const allStages =
    Array.isArray(customStages) && customStages.length > 0
      ? customStages
      : Array.isArray(lead.allStages) && lead.allStages.length > 0
        ? lead.allStages
        : [];

  const stageInfo = calculateDynamicStageScore(stage, allStages);
  const stageScore = stageInfo.score;
  const stageLabel = stageInfo.label;

  // B. Seniority & Decision-Maker Fit (Max 25 pts)
  const rawJobTitle =
    lead.jobTitle !== undefined && lead.jobTitle !== null
      ? String(lead.jobTitle)
      : lead.job_title !== undefined && lead.job_title !== null
        ? String(lead.job_title)
        : '';
  const jobTitle = rawJobTitle.trim().toLowerCase();
  let seniorityScore = 0;
  let seniorityTitle = 'None';

  if (jobTitle) {
    if (
      /\b(ceo|cto|cfo|coo|cio|cmo|cro|cpo|cso|c-level|chief|founder|co-founder|cofounder|president|vp|vice president|partner|owner)\b/i.test(
        jobTitle
      )
    ) {
      seniorityScore = 25;
      seniorityTitle = 'C-Suite / Founder / VP';
    } else if (/\b(director|head of|head|managing director|executive director)\b/i.test(jobTitle)) {
      seniorityScore = 18;
      seniorityTitle = 'Director / Head of Dept';
    } else if (/\b(manager|lead|team lead|supervisor|team leader)\b/i.test(jobTitle)) {
      seniorityScore = 15;
      seniorityTitle = 'Manager / Lead';
    } else if (
      /\b(senior|sr|sr\.|specialist|expert|consultant|architect|principal)\b/i.test(jobTitle)
    ) {
      seniorityScore = 10;
      seniorityTitle = 'Senior';
    } else {
      seniorityScore = 5;
      seniorityTitle = 'Individual Contributor';
    }
  }
  const seniorityLabel = `${seniorityTitle} (+${seniorityScore} pts)`;

  // C. Engagement & Follow-up Tasks (Max 25 pts)
  const tasks = Array.isArray(lead.tasks)
    ? lead.tasks
    : Array.isArray(lead.crm?.tasks)
      ? lead.crm.tasks
      : [];

  const completedCount = tasks.filter((t) => {
    const status = String(t?.status || (t?.completed ? 'completed' : '')).toLowerCase();
    return status === 'completed' || status === 'done';
  }).length;

  const completedPoints = Math.min(15, completedCount * 5);

  const followUpDateStr =
    lead.nextFollowUpDate !== undefined && lead.nextFollowUpDate !== null
      ? String(lead.nextFollowUpDate)
      : lead.next_follow_up_date !== undefined && lead.next_follow_up_date !== null
        ? String(lead.next_follow_up_date)
        : lead.follow_up_date !== undefined && lead.follow_up_date !== null
          ? String(lead.follow_up_date)
          : lead.followUpDate !== undefined && lead.followUpDate !== null
            ? String(lead.followUpDate)
            : getCardFollowUpDate(lead) || '';

  const hasInProgressTask = tasks.some((t) => {
    const status = String(t?.status || '').toLowerCase();
    return status === 'in_progress' || status === 'in progress' || status === 'inprogress';
  });

  let activeFollowUpPoints = 0;
  let timelyFollowUpPoints = 0;

  if (hasInProgressTask) {
    activeFollowUpPoints = 6;
  }

  if (followUpDateStr) {
    const fDate = parseFlexibleDate(followUpDateStr);
    if (fDate && !isNaN(fDate.getTime())) {
      activeFollowUpPoints = 6;

      const now = new Date();
      const diffMs = fDate.getTime() - now.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);

      // Within next 7 days or today/tomorrow
      if (diffDays >= -1 && diffDays <= 7) {
        timelyFollowUpPoints = 4;
      }
    }
  }

  // Check if any high priority task is pending
  if (timelyFollowUpPoints === 0) {
    const hasHighPriority = tasks.some((t) => {
      const p = String(t?.priority || '').toLowerCase();
      const status = String(t?.status || (t?.completed ? 'completed' : '')).toLowerCase();
      return p === 'high' && !['completed', 'done', 'cancelled'].includes(status);
    });
    if (hasHighPriority) {
      timelyFollowUpPoints = 4;
    }
  }

  const taskScore = completedPoints + activeFollowUpPoints + timelyFollowUpPoints;

  // D. Contact Completeness (Max 10 pts)
  const extractCleanStr = (val) => {
    if (!val) return '';
    if (Array.isArray(val)) {
      return val
        .map((item) => {
          if (!item) return '';
          if (typeof item === 'object') {
            return item.email || item.phone || item.number || item.value || item.address || '';
          }
          const s = String(item).trim();
          return s === '[]' || s === '{}' || s === '[""]' ? '' : s;
        })
        .filter(Boolean)
        .join('');
    }
    if (typeof val === 'object') {
      const field = val.email || val.phone || val.number || val.value || val.address || '';
      if (field) return String(field).trim();
      const s = JSON.stringify(val);
      return s === '{}' || s === '[]' ? '' : s;
    }
    const s = String(val).trim();
    return s === '[]' || s === '{}' || s === '[""]' ? '' : s;
  };

  const emailStr =
    extractCleanStr(lead.email) ||
    extractCleanStr(lead.emails) ||
    extractCleanStr(lead.email_addresses) ||
    extractCleanStr(lead.crm?.email) ||
    extractCleanStr(lead.crm?.emails) ||
    '';

  const phoneStr =
    extractCleanStr(lead.phone) ||
    extractCleanStr(lead.phones) ||
    extractCleanStr(lead.phone_numbers) ||
    extractCleanStr(lead.phoneNumber) ||
    extractCleanStr(lead.phone_number) ||
    extractCleanStr(lead.crm?.phone) ||
    extractCleanStr(lead.crm?.phones) ||
    '';

  const linkedinStr =
    extractCleanStr(lead.linkedinUrl) ||
    extractCleanStr(lead.linkedin_url) ||
    extractCleanStr(lead.social?.linkedin) ||
    extractCleanStr(lead.social_links?.linkedin) ||
    extractCleanStr(lead.crm?.linkedinUrl) ||
    extractCleanStr(lead.crm?.linkedin_url) ||
    '';

  const hasEmail = Boolean(
    emailStr && emailStr !== '[]' && emailStr !== '[""]' && emailStr !== '{}'
  );
  const hasPhone = Boolean(
    phoneStr && phoneStr !== '[]' && phoneStr !== '[""]' && phoneStr !== '{}'
  );
  const hasLinkedin = Boolean(
    linkedinStr &&
    linkedinStr !== '{}' &&
    linkedinStr !== '[]' &&
    linkedinStr !== '""' &&
    linkedinStr !== 'null'
  );

  let contactScore = 0;
  if (hasEmail) contactScore += 5;
  if (hasPhone) contactScore += 3;
  if (hasLinkedin) contactScore += 2;

  // E. Commercial Intent (Max 10 pts)
  let intentScore = 0;
  const rawDealVal =
    lead.dealValue !== undefined && lead.dealValue !== null
      ? String(lead.dealValue)
      : lead.deal_value !== undefined && lead.deal_value !== null
        ? String(lead.deal_value)
        : lead.crm?.dealValue !== undefined && lead.crm?.dealValue !== null
          ? String(lead.crm.dealValue)
          : lead.crm?.deal_value !== undefined && lead.crm?.deal_value !== null
            ? String(lead.crm.deal_value)
            : '0';

  const cleanDealVal = rawDealVal.replace(/[^0-9.]/g, '');
  const dealVal = parseFloat(cleanDealVal) || 0;
  if (dealVal > 0) {
    intentScore += 5;
  }

  const productInterest =
    lead.productInterest !== undefined && lead.productInterest !== null
      ? String(lead.productInterest).trim()
      : lead.product_interest !== undefined && lead.product_interest !== null
        ? String(lead.product_interest).trim()
        : lead.proposalReference !== undefined && lead.proposalReference !== null
          ? String(lead.proposalReference).trim()
          : lead.proposal_reference !== undefined && lead.proposal_reference !== null
            ? String(lead.proposal_reference).trim()
            : lead.crm?.productInterest !== undefined && lead.crm?.productInterest !== null
              ? String(lead.crm.productInterest).trim()
              : lead.crm?.proposalReference !== undefined && lead.crm?.proposalReference !== null
                ? String(lead.crm.proposalReference).trim()
                : '';

  if (productInterest) {
    intentScore += 5;
  }

  const total = Math.min(
    100,
    Math.max(0, stageScore + seniorityScore + taskScore + contactScore + intentScore)
  );

  return {
    totalScore: total,
    breakdown: {
      stageScore,
      stageLabel,
      seniorityScore,
      seniorityLabel,
      seniorityTitle,
      taskScore,
      completedCount,
      completedPoints,
      activeFollowUpPoints,
      timelyFollowUpPoints,
      hasInProgressTask,
      contactScore,
      hasEmail,
      hasPhone,
      hasLinkedin,
      intentScore,
      hasDealValue: dealVal > 0,
      hasProductInterest: !!productInterest,
    },
  };
}

// ─── Palette for deterministic avatar colors (matching ProspectListView) ─────
const AV_FILLS = ['#E6F1FB', '#EAF3DE', '#FAEEDA', '#FBEAF0', '#E1F5EE', '#EEEDFE', '#FAECE7'];
const AV_TEXT = ['#0C447C', '#27500A', '#633806', '#72243E', '#085041', '#3C3489', '#712B13'];

const extractDomainFromEmail = (emailStr) => {
  if (!emailStr) return null;
  const str = String(emailStr).trim();
  const match = str.match(/@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
  return match && match[1] ? match[1].toLowerCase() : null;
};

const extractDomainFromUrl = (urlStr) => {
  if (!urlStr) return null;
  try {
    const raw = urlStr.startsWith('http') ? urlStr : `https://${urlStr}`;
    const parsed = new URL(raw);
    return parsed.hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return null;
  }
};

/**
 * Zero-hardcoded dynamic extraction of the core brand name from a domain.
 * e.g. "hilti.com.my" -> "hilti", "google.com" -> "google", "sunwaymedical.com" -> "sunwaymedical"
 */
const extractCoreDomainName = (urlStr) => {
  if (!urlStr) return '';
  const domain = extractDomainFromUrl(urlStr);
  if (!domain) return '';

  const parts = domain.split('.');
  if (parts.length === 1) return parts[0].toLowerCase();

  // Dynamic compound TLD recognition (.com.my, .co.uk, .com.sg, .edu.my, etc.)
  const lastPart = parts[parts.length - 1];
  const secondLast = parts[parts.length - 2];
  const isCompoundTld =
    parts.length > 2 &&
    ['com', 'co', 'org', 'net', 'edu', 'gov', 'ac', 'biz', 'info'].includes(secondLast) &&
    lastPart.length <= 3;

  const coreParts = isCompoundTld ? parts.slice(0, -2) : parts.slice(0, -1);
  const mainToken = coreParts[coreParts.length - 1] || coreParts[0] || '';
  return mainToken.toLowerCase().replace(/[^a-z0-9]/g, '');
};

/**
 * Zero-hardcoded extraction of significant words from a company name.
 * Filters out common generic business suffixes across all jurisdictions.
 */
const extractCompanyTokens = (companyName) => {
  if (!companyName) return [];
  const clean = String(companyName).toLowerCase();
  const stopWords = new Set([
    'sdn',
    'bhd',
    'berhad',
    'inc',
    'corp',
    'corporation',
    'llc',
    'ltd',
    'limited',
    'group',
    'holdings',
    'holding',
    'co',
    'company',
    'technologies',
    'technology',
    'tech',
    'global',
    'international',
    'services',
    'solutions',
    'enterprises',
    'enterprise',
    'venture',
    'ventures',
    'pte',
    'gmbh',
    'plc',
    'sa',
    'nv',
    'ag',
    'the',
    'and',
    'of',
    'in',
    'for',
    'center',
    'centre',
  ]);

  const rawWords = clean
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  const significant = rawWords.filter((w) => w.length >= 2 && !stopWords.has(w));
  return significant.length > 0 ? significant : rawWords;
};

/**
 * Dynamic Bigram character similarity calculation (Dice coefficient).
 */
const calculateStringSimilarity = (str1, str2) => {
  if (!str1 || !str2) return 0;
  const s1 = String(str1)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  const s2 = String(str2)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  if (!s1 || !s2) return 0;
  if (s1 === s2) return 1.0;
  if (s1.includes(s2) || s2.includes(s1)) return 0.85;

  if (s1.length < 2 || s2.length < 2) return 0;
  const getBigrams = (str) => {
    const bigrams = new Set();
    for (let i = 0; i < str.length - 1; i++) {
      bigrams.add(str.slice(i, i + 2));
    }
    return bigrams;
  };
  const bg1 = getBigrams(s1);
  const bg2 = getBigrams(s2);
  let intersection = 0;
  bg1.forEach((b) => {
    if (bg2.has(b)) intersection++;
  });
  return (2 * intersection) / (bg1.size + bg2.size);
};

/**
 * Pure dynamic, zero-hardcoded verification that a website domain matches the company name.
 * Prevents assigning Google's URL to Meta, or FootfallCam's URL to Hilti, etc.
 */
const isCompanyDomainMatching = (companyName, websiteUrl) => {
  if (!companyName || !websiteUrl) return true;
  const coreDomain = extractCoreDomainName(websiteUrl);
  if (!coreDomain || coreDomain.length < 2) return true;

  const rawCompany = String(companyName).toLowerCase().trim();
  const cleanCo = rawCompany.replace(/[^a-z0-9]/g, '');
  if (!cleanCo) return true;

  // 1. Direct substring matching:
  if (cleanCo.includes(coreDomain) || coreDomain.includes(cleanCo)) {
    return true;
  }

  const tokens = extractCompanyTokens(companyName);
  const combinedTokens = tokens.join('');

  // 2. Token combination matching:
  if (
    combinedTokens &&
    (combinedTokens.includes(coreDomain) || coreDomain.includes(combinedTokens))
  ) {
    return true;
  }

  // 3. Individual significant token match:
  for (const token of tokens) {
    if (token.length >= 3) {
      if (coreDomain.includes(token) || (token.length >= 4 && token.includes(coreDomain))) {
        return true;
      }
    }
  }

  // 4. Dynamic Multi-word Acronym & Institution Abbreviation Matching:
  // (e.g. "Asia Pacific University of Technology and Innovation" -> "apu", "Touch n Go" -> "tng")
  const allWordList = rawCompany
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (allWordList.length >= 2) {
    const allInitials = allWordList.map((w) => w[0]).join('');
    const minorStopWords = new Set([
      'and',
      'of',
      'the',
      'in',
      'for',
      'a',
      'an',
      'to',
      'at',
      'by',
      '&',
    ]);
    const sigWords = allWordList.filter((w) => !minorStopWords.has(w));
    const sigInitials = sigWords.map((w) => w[0]).join('');

    const acronymCandidates = [
      allInitials,
      sigInitials,
      allInitials.slice(0, 2),
      allInitials.slice(0, 3),
      allInitials.slice(0, 4),
      sigInitials.slice(0, 2),
      sigInitials.slice(0, 3),
      sigInitials.slice(0, 4),
      tokens.map((t) => t[0]).join(''),
    ];

    for (const acr of acronymCandidates) {
      if (acr && acr.length >= 2) {
        if (coreDomain === acr) return true;
        if (coreDomain.startsWith(acr) || coreDomain.includes(acr)) return true;
        if (acr.startsWith(coreDomain)) return true;
      }
    }
  }

  // 5. Dynamic fuzzy similarity threshold:
  const simWithClean = calculateStringSimilarity(cleanCo, coreDomain);
  const simWithCombined = calculateStringSimilarity(combinedTokens, coreDomain);
  if (simWithClean >= 0.35 || simWithCombined >= 0.35) {
    return true;
  }

  // Mismatch detected dynamically without any hardcoding!
  return false;
};

const getSourceIcon = (source = '') => {
  const s = String(source).toLowerCase();
  if (s.includes('prospect')) return 'fas fa-search';
  if (s.includes('linkedin')) return 'fab fa-linkedin';
  if (s.includes('website') || s.includes('web')) return 'fas fa-globe';
  if (s.includes('referral')) return 'fas fa-user-friends';
  if (s.includes('email') || s.includes('outreach')) return 'fas fa-envelope';
  if (s.includes('event') || s.includes('conference')) return 'fas fa-calendar-check';
  return 'fas fa-tag';
};

function hashColor(str = '') {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) % AV_FILLS.length;
  }
  return [AV_FILLS[h], AV_TEXT[h]];
}

function getCompanyLogoUrl(companyName, details, email, website) {
  // 1. Dynamic extraction from company website URL (e.g. "hilti.com.my", "https://grab.com")
  const webDomain = extractDomainFromUrl(website);
  if (webDomain) {
    return `https://www.google.com/s2/favicons?domain=${webDomain}&sz=64`;
  }

  // 2. Direct explicit logo URL from enrichment database if provided and matching current company
  const coNameStr = String(companyName || '')
    .trim()
    .toLowerCase();
  const detailsCoName = String(details?.companyName || details?.name || '')
    .trim()
    .toLowerCase();
  const isDetailsMatch = !detailsCoName || !coNameStr || detailsCoName === coNameStr;

  if (isDetailsMatch) {
    const direct =
      details?.logoUrl ||
      details?.logo_url ||
      details?.logo ||
      details?.companyLogo ||
      details?.company?.logoUrl;
    if (direct && typeof direct === 'string' && direct.startsWith('http')) {
      return direct;
    }
  }

  // 3. Dynamic extraction from email domain (e.g. "test@hilti.com.my" -> "hilti.com.my")
  const emailDomain = extractDomainFromEmail(email);
  if (emailDomain) {
    return `https://www.google.com/s2/favicons?domain=${emailDomain}&sz=64`;
  }

  // 4. Dynamic extraction from company name
  const coName = String(companyName || details?.companyName || details?.name || '')
    .toLowerCase()
    .trim();

  if (coName.includes('.')) {
    const directDomain = coName.replace(/[^a-z0-9.-]/g, '');
    if (directDomain.length > 3) {
      return `https://www.google.com/s2/favicons?domain=${directDomain}&sz=64`;
    }
  }

  const cleanName = coName.replace(/[^a-z0-9]/g, '');
  if (cleanName && cleanName.length > 2) {
    return `https://www.google.com/s2/favicons?domain=${cleanName}.com&sz=64`;
  }

  return null;
}

function getCompany(card) {
  if (!card) return { name: 'Unknown', logo: null };
  let details = card.company_details ?? card.companyDetails ?? null;
  if (typeof details === 'string') {
    try {
      details = JSON.parse(details);
    } catch {
      details = null;
    }
  }
  const name =
    card.crm?.company ||
    card.crm?.companyName ||
    card.crm?.company_name ||
    card.company ||
    card.company_name ||
    card.companyName ||
    details?.companyName ||
    details?.name ||
    details?.company?.name ||
    'Unknown';

  const email = typeof card.email === 'string' ? card.email : emailToString(card.email);
  const website =
    card.crm?.companyWebsite ||
    card.crm?.company_website ||
    card.companyWebsite ||
    card.company_website ||
    card.website;

  const detailsCoName = String(details?.companyName || details?.name || '')
    .trim()
    .toLowerCase();
  const isStaleDetails = detailsCoName && name && detailsCoName !== name.toLowerCase();
  const validDetails = isStaleDetails ? null : details;

  const logo = getCompanyLogoUrl(name, validDetails, email, website);

  return { name, logo };
}

function CompanyMark({ name = '', logoUrl = null, size = 18 }) {
  const [bg, fg] = hashColor(name);
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => {
    setImgErr(false);
  }, [logoUrl, name]);

  if (logoUrl && !imgErr) {
    return (
      <div className="crm-co-mark" style={{ width: size, height: size }}>
        <img
          key={`${name}-${logoUrl}`}
          src={logoUrl}
          alt={name}
          onError={() => setImgErr(true)}
          loading="eager"
        />
      </div>
    );
  }
  return (
    <div
      className="crm-co-mark"
      style={{
        width: size,
        height: size,
        background: bg,
        color: fg,
        fontSize: Math.max(9, Math.floor(size * 0.52)),
        fontWeight: 700,
      }}
    >
      {(name || 'U').charAt(0).toUpperCase()}
    </div>
  );
}

function initials(name = '') {
  return (
    String(name)
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase() || 'U'
  );
}

function ContactAvatar({ name = '', size = 32 }) {
  const [bg, fg] = hashColor(name);
  return (
    <div
      className="crm-contact-avatar"
      style={{
        width: size,
        height: size,
        minWidth: size,
        background: bg,
        color: fg,
        fontSize: Math.floor(size * 0.38),
      }}
      aria-hidden
    >
      {initials(name)}
    </div>
  );
}

// Format an activity's previous/new value for display in the Activity tab.
// Values can be primitives (score, stage, owner, date, ...) or objects (the
// aggregated generic-field snapshot or the Won/Lost payload). Nested objects
// are JSON-stringified; empty values collapse to an em dash.
const formatActivityValue = (value) => {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'object') {
    const entries = Object.entries(value).filter(
      ([k, v]) => k !== 'timestamp' && v !== undefined && v !== null && v !== ''
    );
    if (entries.length === 0) return '—';
    return entries
      .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
      .join(', ');
  }
  return String(value);
};

// Follow-up task activities (TASK_CREATED / TASK_COMPLETED / TASK_CANCELLED /
// TASK_REMOVED) are rendered as labelled rows (Prev / New / Priority / Date)
// instead of the generic object dump.
const isTaskActivity = (type) => /^TASK_/.test(String(type || ''));

// Pull the task snapshot out of an activity. TASK_CREATED stores it as
// newValue.task; TASK_COMPLETED / TASK_CANCELLED carry it on a.task.
const taskFromActivity = (activity) => {
  if (!activity) return null;
  if (activity.task && typeof activity.task === 'object') return activity.task;
  const nv = activity.newValue;
  if (nv && typeof nv === 'object' && nv.task && typeof nv.task === 'object') return nv.task;
  if (nv && typeof nv === 'object' && nv.id && nv.title) return nv;
  return null;
};

const formatActivityDate = (value) => {
  if (!value) return '—';
  const date = new Date(String(value).includes('T') ? value : `${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const COUNTRY_DIAL_CODES = {
  AF: '+93',
  AL: '+355',
  DZ: '+213',
  AD: '+376',
  AO: '+244',
  AG: '+1268',
  AR: '+54',
  AM: '+374',
  AU: '+61',
  AT: '+43',
  AZ: '+994',
  BS: '+1242',
  BH: '+973',
  BD: '+880',
  BB: '+1246',
  BY: '+375',
  BE: '+32',
  BZ: '+501',
  BJ: '+229',
  BT: '+975',
  BO: '+591',
  BA: '+387',
  BW: '+267',
  BR: '+55',
  BN: '+673',
  BG: '+359',
  BF: '+226',
  BI: '+257',
  KH: '+855',
  CM: '+237',
  CA: '+1',
  CV: '+238',
  CF: '+236',
  TD: '+235',
  CL: '+56',
  CN: '+86',
  CO: '+57',
  KM: '+269',
  CG: '+242',
  CD: '+243',
  CR: '+506',
  CI: '+225',
  HR: '+385',
  CU: '+53',
  CY: '+357',
  CZ: '+420',
  DK: '+45',
  DJ: '+253',
  DM: '+1767',
  DO: '+1809',
  EC: '+593',
  EG: '+20',
  SV: '+503',
  GQ: '+240',
  ER: '+291',
  EE: '+372',
  ET: '+251',
  FJ: '+679',
  FI: '+358',
  FR: '+33',
  GA: '+241',
  GM: '+220',
  GE: '+995',
  DE: '+49',
  GH: '+233',
  GR: '+30',
  GD: '+1473',
  GT: '+502',
  GN: '+224',
  GW: '+245',
  GY: '+592',
  HT: '+509',
  HN: '+504',
  HU: '+36',
  IS: '+354',
  IN: '+91',
  ID: '+62',
  IR: '+98',
  IQ: '+964',
  IE: '+353',
  IL: '+972',
  IT: '+39',
  JM: '+1876',
  JP: '+81',
  JO: '+962',
  KZ: '+7',
  KE: '+254',
  KI: '+686',
  KP: '+850',
  KR: '+82',
  KW: '+965',
  KG: '+996',
  LA: '+856',
  LV: '+371',
  LB: '+961',
  LS: '+266',
  LR: '+231',
  LY: '+218',
  LI: '+423',
  LT: '+370',
  LU: '+352',
  MK: '+389',
  MG: '+261',
  MW: '+265',
  MY: '+60',
  MV: '+960',
  ML: '+223',
  MT: '+356',
  MH: '+692',
  MR: '+222',
  MU: '+230',
  MX: '+52',
  FM: '+691',
  MD: '+373',
  MC: '+377',
  MN: '+976',
  ME: '+382',
  MA: '+212',
  MZ: '+258',
  MM: '+95',
  NA: '+264',
  NR: '+674',
  NP: '+977',
  NL: '+31',
  NZ: '+64',
  NI: '+505',
  NE: '+227',
  NG: '+234',
  NO: '+47',
  OM: '+968',
  PK: '+92',
  PW: '+680',
  PA: '+507',
  PG: '+675',
  PY: '+595',
  PE: '+51',
  PH: '+63',
  PL: '+48',
  PT: '+351',
  QA: '+974',
  RO: '+40',
  RU: '+7',
  RW: '+250',
  KN: '+1869',
  LC: '+1758',
  VC: '+1784',
  WS: '+685',
  SM: '+378',
  ST: '+239',
  SA: '+966',
  SN: '+221',
  RS: '+381',
  SC: '+248',
  SL: '+232',
  SG: '+65',
  SK: '+421',
  SI: '+386',
  SB: '+677',
  SO: '+252',
  ZA: '+27',
  SS: '+211',
  ES: '+34',
  LK: '+94',
  SD: '+249',
  SR: '+597',
  SZ: '+268',
  SE: '+46',
  CH: '+41',
  SY: '+963',
  TW: '+886',
  TJ: '+992',
  TZ: '+255',
  TH: '+66',
  TL: '+670',
  TG: '+228',
  TO: '+676',
  TT: '+1868',
  TN: '+216',
  TR: '+90',
  TM: '+993',
  TV: '+688',
  UG: '+256',
  UA: '+380',
  AE: '+971',
  GB: '+44',
  US: '+1',
  UY: '+598',
  UZ: '+998',
  VU: '+678',
  VA: '+379',
  VE: '+58',
  VN: '+84',
  YE: '+967',
  ZM: '+260',
  ZW: '+263',
  HK: '+852',
  MO: '+853',
  GL: '+299',
  GI: '+350',
  FO: '+298',
  AX: '+358',
  PR: '+1787',
  GU: '+1671',
  VI: '+1340',
  AS: '+1684',
  MP: '+1670',
  PN: '+64',
  NC: '+687',
  PF: '+689',
  GF: '+594',
  GP: '+590',
  MQ: '+596',
  RE: '+262',
  YT: '+262',
  CC: '+61',
  CX: '+61',
  NF: '+672',
  CK: '+682',
  NU: '+683',
  TK: '+690',
  WF: '+681',
  PM: '+508',
  BL: '+590',
  MF: '+590',
  SH: '+290',
  AQ: '+672',
  IO: '+246',
  BV: '+47',
  GS: '+500',
  HM: '+61',
  FK: '+500',
  MS: '+1664',
  TC: '+1649',
  KY: '+1345',
  AW: '+297',
  BM: '+1441',
  VG: '+1284',
};

const countryDialCode = (code) => {
  if (!code) return '';
  if (code.startsWith('US-') || code.startsWith('CA-')) return '+1';
  if (COUNTRY_DIAL_CODES[code]) return COUNTRY_DIAL_CODES[code];
  const baseIso = code.includes('-') ? code.split('-')[0] : code;
  return COUNTRY_DIAL_CODES[baseIso] || '';
};

const COUNTRY_NATIONAL_LENGTH = {
  MY: [9, 10],
  SG: [8, 8],
  ID: [9, 11],
  TH: [8, 9],
  PH: [10, 10],
  VN: [9, 10],
  MM: [8, 10],
  KH: [8, 9],
  LA: [8, 11],
  BN: [7, 7],
  GB: [10, 10],
  US: [10, 10],
  CA: [10, 10],
  MX: [10, 10],
  AU: [9, 9],
  NZ: [8, 10],
  CN: [11, 11],
  HK: [8, 8],
  TW: [9, 9],
  JP: [10, 10],
  KR: [9, 10],
  IN: [10, 10],
  PK: [10, 10],
  BD: [10, 10],
  LK: [9, 9],
  NP: [8, 10],
  DE: [10, 11],
  FR: [9, 9],
  ES: [9, 9],
  IT: [9, 11],
  PT: [9, 9],
  NL: [9, 9],
  BE: [8, 9],
  CH: [9, 9],
  AT: [10, 11],
  IE: [7, 9],
  DK: [8, 8],
  SE: [8, 10],
  NO: [8, 8],
  FI: [8, 10],
  PL: [9, 9],
  TR: [10, 10],
  GR: [10, 10],
  AE: [9, 9],
  SA: [9, 9],
  QA: [8, 8],
  ZA: [9, 9],
  EG: [9, 10],
  NG: [10, 11],
  KE: [9, 10],
  BR: [10, 11],
  AR: [10, 10],
  CL: [9, 9],
  CO: [10, 10],
  PE: [9, 9],
  RU: [10, 10],
  UA: [9, 10],
  CZ: [9, 9],
  SK: [9, 9],
  HU: [9, 9],
  RO: [9, 10],
  BG: [8, 9],
  HR: [8, 9],
  RS: [8, 9],
  IL: [8, 9],
};

const nationalLength = (code) => {
  if (code && code.startsWith('US-')) return COUNTRY_NATIONAL_LENGTH.US;
  if (code && code.startsWith('CA-')) return COUNTRY_NATIONAL_LENGTH.CA;
  return COUNTRY_NATIONAL_LENGTH[code] || [6, 15];
};

const nationalDigitsOf = (raw, dialCode) => {
  let cleaned = (raw || '').replace(/\D+/g, '');
  const dc = (dialCode || '').replace(/\D/g, '');
  if (dc && cleaned.startsWith(dc)) {
    cleaned = cleaned.slice(dc.length);
  }
  // Strip leading trunk zero if present (e.g. 0123456789 -> 123456789)
  if (dc && cleaned.startsWith('0')) {
    cleaned = cleaned.replace(/^0+/, '');
  }
  return cleaned;
};

const isValidPhone = (raw, dialCode, isoCode) => {
  if (!raw || !String(raw).trim()) return true;
  const cleaned = String(raw).replace(/\D+/g, '');
  if (!cleaned) return false;
  const dc = (dialCode || '').replace(/\D/g, '');
  if (dc && !cleaned.startsWith(dc)) {
    const nationalOnly = cleaned.startsWith('0') ? cleaned.replace(/^0+/, '') : cleaned;
    const [min, max] = nationalLength(isoCode);
    return nationalOnly.length >= min && nationalOnly.length <= max;
  }
  const national = nationalDigitsOf(raw, dialCode);
  const [min, max] = nationalLength(isoCode);
  return national.length >= min && national.length <= max;
};

const formatPhone = (raw, dialCode, isoCode) => {
  if (!raw) return '';
  const cleaned = String(raw).replace(/\D+/g, '');
  if (!cleaned) return '';
  const [, max] = nationalLength(isoCode);
  const dc = (dialCode || '').replace(/\D/g, '');
  let national = '';
  if (dc && cleaned.startsWith(dc)) {
    let digits = cleaned.slice(dc.length);
    if (digits.startsWith('0')) {
      digits = digits.replace(/^0+/, '');
    }
    national = digits.slice(0, max);
    const grouped = national.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
    return `${dialCode}${grouped ? ` ${grouped}` : ''}`;
  }
  if (dc && cleaned.startsWith('0')) {
    national = cleaned.replace(/^0+/, '').slice(0, max);
    const grouped = national.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
    return `${dialCode}${grouped ? ` ${grouped}` : ''}`;
  }
  national = cleaned.slice(0, max);
  return national.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
};

const COUNTRY_PHONE_PLACEHOLDERS = {
  us: '555 123 4567',
  ca: '555 123 4567',
  my: '12 345 6789',
  sg: '8123 4567',
  gb: '7911 123456',
  au: '412 345 678',
  nz: '21 123 4567',
  in: '98765 43210',
  cn: '138 0013 8000',
  hk: '9123 4567',
  tw: '912 345 678',
  jp: '90 1234 5678',
  kr: '10 1234 5678',
  id: '812 3456 7890',
  th: '81 234 5678',
  ph: '917 123 4567',
  vn: '91 234 5678',
  de: '151 12345678',
  fr: '6 12 34 56 78',
  es: '612 34 56 78',
  it: '312 345 6789',
  nl: '6 12345678',
  ae: '50 123 4567',
  sa: '50 123 4567',
  br: '11 91234 5678',
};

const getPhonePlaceholder = (isoCode, dialCode) => {
  if (!dialCode) return 'Enter phone number';
  const base = (
    isoCode ? (isoCode.includes('-') ? isoCode.split('-')[0] : isoCode) : ''
  ).toLowerCase();
  const example = COUNTRY_PHONE_PLACEHOLDERS[base];
  return example ? `e.g. ${example}` : 'Enter phone number';
};

const getFlagEmoji = (code) => {
  if (!code) return '🌐';
  const base = (code.includes('-') ? code.split('-')[0] : code).toUpperCase();
  if (base.length !== 2) return '🌐';
  const codePoints = base.split('').map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const CountryFlag = ({ code, name, className = '' }) => {
  const [imgError, setImgError] = useState(false);
  const iso2 = (code ? (code.includes('-') ? code.split('-')[0] : code) : '').toUpperCase();

  useEffect(() => {
    setImgError(false);
  }, [iso2]);

  if (!iso2 || iso2.length !== 2) {
    return <span className={`crm-country-flag-emoji ${className}`}>🌐</span>;
  }

  const flagUrl = `https://flagcdn.com/w40/${iso2.toLowerCase()}.png`;

  return (
    <span className={`crm-country-flag-wrap ${className}`}>
      {!imgError ? (
        <img
          src={flagUrl}
          srcSet={`https://flagcdn.com/w80/${iso2.toLowerCase()}.png 2x`}
          alt={name || iso2}
          className="crm-country-flag-img"
          loading="lazy"
          onError={() => setImgError(true)}
        />
      ) : (
        <span className="crm-country-flag-emoji">{getFlagEmoji(iso2)}</span>
      )}
    </span>
  );
};

const findCountryByDialCodeOrName = (phone, countryName) => {
  const trimmedPhone = String(phone || '').trim();
  const trimmedName = String(countryName || '').trim();

  // 1. If countryName is explicitly provided, match it first!
  if (trimmedName) {
    const lowerName = trimmedName.toLowerCase();
    const foundByName = countriesData.find(
      (c) =>
        c.name.toLowerCase() === lowerName ||
        c.code.toLowerCase() === lowerName ||
        (lowerName === 'usa' && (c.code === 'US' || c.name === 'United States')) ||
        (lowerName === 'us' && (c.code === 'US' || c.name === 'United States')) ||
        (lowerName === 'uk' && (c.code === 'GB' || c.name === 'United Kingdom')) ||
        (lowerName === 'uae' && (c.code === 'AE' || c.name === 'United Arab Emirates'))
    );
    if (foundByName) {
      const expectedDc = countryDialCode(foundByName.code);
      if (
        !trimmedPhone ||
        !expectedDc ||
        trimmedPhone.startsWith(expectedDc) ||
        !trimmedPhone.startsWith('+')
      ) {
        return foundByName;
      }
    }
  }

  // 2. If phone has a dial code starting with +
  if (trimmedPhone.startsWith('+')) {
    // Check specific priority defaults for shared country codes
    if (
      trimmedPhone.startsWith('+1') &&
      (!trimmedName || !trimmedName.toLowerCase().includes('canada'))
    ) {
      const us = countriesData.find((c) => c.code === 'US' || c.name === 'United States');
      if (us) return us;
    }
    if (
      trimmedPhone.startsWith('+7') &&
      (!trimmedName || !trimmedName.toLowerCase().includes('kazakhstan'))
    ) {
      const ru = countriesData.find(
        (c) => c.code === 'RU' || c.name === 'Russia' || c.name === 'Russian Federation'
      );
      if (ru) return ru;
    }
    if (trimmedPhone.startsWith('+44')) {
      const gb = countriesData.find((c) => c.code === 'GB' || c.name === 'United Kingdom');
      if (gb) return gb;
    }

    // Sort by longest dial code match first so e.g. +1242 (Bahamas) matches before +1
    const sortedCodes = Object.entries(COUNTRY_DIAL_CODES).sort(
      (a, b) => b[1].length - a[1].length
    );
    for (const [iso, dc] of sortedCodes) {
      if (trimmedPhone.startsWith(dc)) {
        const found = countriesData.find((c) => c.code === iso || c.code.startsWith(`${iso}-`));
        if (found) return found;
      }
    }
  }

  // 3. Fallback: match by partial or flexible countryName
  if (trimmedName) {
    const lowerName = trimmedName.toLowerCase();
    const found = countriesData.find(
      (c) => c.name.toLowerCase().includes(lowerName) || lowerName.includes(c.name.toLowerCase())
    );
    if (found) return found;
  }

  return null;
};

const CountryPhoneField = ({ value, country, onPhone, onCountry, error, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  const selectedEntry = useMemo(() => {
    return findCountryByDialCodeOrName(value, country);
  }, [value, country]);
  const selectedDialCode = selectedEntry ? countryDialCode(selectedEntry.code) : '';
  const selectedIsoCode = selectedEntry ? selectedEntry.code : '';

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleCountrySelect = (c) => {
    if (onCountry) onCountry(c.name);
    const dialCode = countryDialCode(c.code);
    if (dialCode) {
      const existing = (value || '').trim();
      const withoutCode = existing.replace(/^\+\d{1,4}\s?/, '');
      onPhone(formatPhone(`${dialCode} ${withoutCode}`, dialCode, c.code));
    }
    setIsOpen(false);
    setSearchQuery('');
  };

  const filteredCountries = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return countriesData;
    return countriesData.filter((c) => {
      const nameMatch = c.name.toLowerCase().includes(q);
      const codeMatch = c.code.toLowerCase().includes(q);
      const dial = countryDialCode(c.code).toLowerCase();
      const dialMatch = dial.includes(q) || dial.replace('+', '').includes(q);
      return nameMatch || codeMatch || dialMatch;
    });
  }, [searchQuery]);

  // The national (country-code free) portion shown in the editable input. The
  // dial code itself is rendered as a fixed, non-editable prefix so the user
  // cannot type or remove it — it is always re-attached before saving.
  const nationalValue = (() => {
    if (!selectedDialCode) return value || '';
    const formatted = formatPhone(value, selectedDialCode, selectedIsoCode);
    if (!formatted) return '';
    return formatted.replace(selectedDialCode, '').replace(/^\s+/, '');
  })();

  const handlePhoneChange = (e) => {
    const typed = e.target.value;
    onPhone(
      selectedDialCode
        ? formatPhone(`${selectedDialCode} ${typed}`, selectedDialCode, selectedIsoCode)
        : formatPhone(typed, '', selectedIsoCode)
    );
  };

  return (
    <div className="crm-phone-country-wrapper">
      <div className="crm-phone-country">
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          className={`crm-phone-country-btn ${isOpen ? 'active' : ''}`}
          onClick={() => {
            if (disabled) return;
            setIsOpen((prev) => !prev);
          }}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          title={selectedEntry ? `${selectedEntry.name} (${selectedDialCode})` : 'Select country'}
        >
          <CountryFlag code={selectedIsoCode} name={selectedEntry?.name} />
          <i className={`fas fa-chevron-down crm-phone-chevron ${isOpen ? 'open' : ''}`} />
        </button>

        {selectedDialCode && <span className="crm-phone-country-code">{selectedDialCode}</span>}

        <input
          type="text"
          disabled={disabled}
          placeholder={getPhonePlaceholder(selectedIsoCode, selectedDialCode)}
          value={nationalValue}
          onChange={handlePhoneChange}
        />

        {isOpen && (
          <div className="crm-country-dropdown" ref={dropdownRef} role="listbox">
            <div className="crm-country-search-wrap">
              <i className="fas fa-search crm-country-search-icon" />
              <input
                type="text"
                className="crm-country-search-input"
                placeholder="Search country or code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searchQuery && (
                <button
                  type="button"
                  className="crm-country-search-clear"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </div>

            <div className="crm-country-list">
              {filteredCountries.length === 0 ? (
                <div className="crm-country-no-results">No countries found</div>
              ) : (
                filteredCountries.map((c) => {
                  const dialCode = countryDialCode(c.code);
                  const isSelected = selectedEntry?.name === c.name;
                  return (
                    <button
                      key={`${c.code}-${c.name}`}
                      type="button"
                      className={`crm-country-option ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleCountrySelect(c)}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <CountryFlag
                        code={c.code}
                        name={c.name}
                        className="crm-country-option-flag"
                      />
                      <span className="crm-country-option-name">{c.name}</span>
                      <span className="crm-country-option-dial">{dialCode || ''}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
      {error && <em className="crm-field-error">{error}</em>}
    </div>
  );
};

const MultiCountryPhoneField = ({
  phones = [''],
  country = '',
  onChange,
  onCountryChange,
  errors = {},
  disabled = false,
}) => {
  const phoneList = Array.isArray(phones) && phones.length > 0 ? phones : [''];

  const handlePhoneChange = (index, value) => {
    const next = [...phoneList];
    next[index] = value;
    onChange(next);
  };

  const handleAddRow = () => {
    if (phoneList.length >= 3) return;
    onChange([...phoneList, '']);
  };

  const handleRemoveRow = (index) => {
    if (phoneList.length <= 1) {
      onChange(['']);
      return;
    }
    const next = phoneList.filter((_, i) => i !== index);
    onChange(next);
  };

  return (
    <div className="crm-phone-multi-wrapper">
      {phoneList.map((phone, idx) => {
        const errorMsg =
          idx === 0 ? errors.phone || errors[`phone_${idx}`] : errors[`phone_${idx}`];
        return (
          <div key={idx} className="crm-phone-row">
            <CountryPhoneField
              value={phone}
              country={country}
              onPhone={(v) => handlePhoneChange(idx, v)}
              onCountry={idx === 0 ? onCountryChange : undefined}
              error={errorMsg}
              disabled={disabled}
            />
            {phoneList.length > 1 && !disabled && (
              <button
                type="button"
                className="crm-phone-remove-btn"
                onClick={() => handleRemoveRow(idx)}
                title="Remove phone number"
                aria-label="Remove phone number"
              >
                <i className="fas fa-trash-alt" />
              </button>
            )}
          </div>
        );
      })}
      {phoneList.length < 3 && !disabled && (
        <button type="button" className="crm-add-phone-btn" onClick={handleAddRow}>
          <i className="fas fa-plus" /> Add Phone Number
        </button>
      )}
    </div>
  );
};

const MultiEmailField = ({
  emails = [''],
  onChange,
  errors = {},
  disabled = false,
  placeholder = 'name@company.com',
}) => {
  const emailList = Array.isArray(emails) && emails.length > 0 ? emails : [''];

  const handleEmailChange = (index, value) => {
    const next = [...emailList];
    next[index] = value;
    onChange(next);
  };

  const handleAddRow = () => {
    if (emailList.length >= 3) return;
    onChange([...emailList, '']);
  };

  const handleRemoveRow = (index) => {
    if (emailList.length <= 1) {
      onChange(['']);
      return;
    }
    const next = emailList.filter((_, i) => i !== index);
    onChange(next);
  };

  return (
    <div className="crm-email-multi-wrapper">
      {emailList.map((email, idx) => {
        const errorMsg =
          idx === 0 ? errors.email || errors[`email_${idx}`] : errors[`email_${idx}`];
        return (
          <div key={idx} className="crm-email-row-wrapper">
            <div className="crm-email-row">
              <div className="crm-email-input-wrapper">
                <input
                  type="email"
                  className={errorMsg ? 'crm-input-error' : ''}
                  value={email}
                  placeholder={placeholder}
                  onChange={(e) => handleEmailChange(idx, e.target.value)}
                  disabled={disabled}
                />
              </div>
              {emailList.length > 1 && !disabled && (
                <button
                  type="button"
                  className="crm-email-remove-btn"
                  onClick={() => handleRemoveRow(idx)}
                  title="Remove email address"
                  aria-label="Remove email address"
                >
                  <i className="fas fa-trash-alt" />
                </button>
              )}
            </div>
            {errorMsg && <em className="crm-field-error">{errorMsg}</em>}
          </div>
        );
      })}
      {emailList.length < 3 && !disabled && (
        <button type="button" className="crm-add-email-btn" onClick={handleAddRow}>
          <i className="fas fa-plus" /> Add Email Address
        </button>
      )}
    </div>
  );
};

const CustomerManagementSystem = () => {
  const {
    isOrgMember,
    workspaceId,
    workspaceType,
    isOwner,
    isOrganizationAdmin,
    isOrganizationWorkspace,
    isSuperadmin,
    isPersonalUser,
    isPersonalWorkspace,
    workspaceRole,
    isSalesManager,
    isSalesMember,
    isResearcher,
    isViewer,
    username: contextUsername,
  } = useWorkspace();
  const { keycloak } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUserId = keycloak?.tokenParsed?.sub;
  const currentUsername =
    contextUsername ||
    keycloak?.tokenParsed?.username ||
    keycloak?.tokenParsed?.preferred_username ||
    '';

  const isPersonalAccount =
    Boolean(isPersonalWorkspace) ||
    Boolean(isPersonalUser) ||
    workspaceType === 'personal' ||
    (!isOrganizationWorkspace && !isOrgMember);

  // Role checks - strictly based on the user's workspace role inside this organization workspace
  const isEffectiveOwner =
    isOwner || workspaceRole === 'org_owner' || isPersonalUser || !workspaceRole;
  const isEffectiveAdmin = isEffectiveOwner || isOrganizationAdmin || workspaceRole === 'org_admin';
  const isEffectiveSalesManager =
    !isEffectiveAdmin && (isSalesManager || workspaceRole === 'org_sales_manager');
  const isEffectiveResearcher =
    !isEffectiveAdmin &&
    !isEffectiveSalesManager &&
    (isResearcher || workspaceRole === 'org_researcher');
  const isEffectiveViewer =
    !isEffectiveAdmin &&
    !isEffectiveSalesManager &&
    !isEffectiveResearcher &&
    (isViewer || workspaceRole === 'org_viewer');
  const isEffectiveSalesMember =
    !isEffectiveAdmin && !isEffectiveSalesManager && !isEffectiveResearcher && !isEffectiveViewer;

  // Granular action permissions
  const canCreateLead = isEffectiveAdmin || isEffectiveSalesManager || isEffectiveResearcher;
  const canMarkWon = isEffectiveAdmin || isEffectiveSalesManager || isEffectiveSalesMember;
  const canAssignLead = !isPersonalAccount && (isEffectiveAdmin || isEffectiveSalesManager);
  const canDeleteLead = isEffectiveAdmin || isEffectiveSalesManager;
  const canEditLeadAll = isEffectiveAdmin || isEffectiveSalesManager;

  // Owners/admins/managers (and superadmins / personal users) may confirm a possible duplicate
  const canConfirmDuplicate =
    isEffectiveAdmin || isEffectiveSalesManager || (!isEffectiveViewer && !isEffectiveSalesMember);

  const todayMin = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;
  })();

  const canEdit = !isEffectiveViewer && !isEffectiveResearcher;

  // A user may edit/drag only cards they have permission for:
  // - Admin, Sales Manager: all workspace leads
  // - Sales Member: leads assigned to them or created by them
  // - Researcher, Viewer: none (read-only for existing pipeline leads)
  const canEditCard = (card) => {
    if (isEffectiveViewer || isEffectiveResearcher) return false;
    if (canEditLeadAll) return true;
    if (!card) return false;
    const userEmail = keycloak?.tokenParsed?.email;
    const isAssigned =
      card.assignee === currentUserId ||
      (currentUsername &&
        String(card.assignee || '').toLowerCase() === String(currentUsername).toLowerCase()) ||
      (userEmail && String(card.assignee || '').toLowerCase() === String(userEmail).toLowerCase());
    const isCreator = card.user_id && String(card.user_id) === String(currentUserId);
    const cardTasks = Array.isArray(card.tasks)
      ? card.tasks
      : Array.isArray(card.crm?.tasks)
        ? card.crm.tasks
        : [];
    const hasAssignedTask = cardTasks.some(
      (t) =>
        t &&
        (String(t.assignee) === String(currentUserId) ||
          (currentUsername &&
            String(t.assignee).toLowerCase() === String(currentUsername).toLowerCase()) ||
          (userEmail && String(t.assignee).toLowerCase() === String(userEmail).toLowerCase()))
    );
    return isAssigned || isCreator || hasAssignedTask;
  };

  const roleAccessBanner = useMemo(() => {
    if (isEffectiveOwner) return null;
    if (isEffectiveAdmin) {
      return {
        icon: 'fas fa-shield-alt',
        title: 'Admin access:',
        desc: 'You have Administrator permissions in this workspace.',
      };
    }
    if (isEffectiveSalesManager) {
      return {
        icon: 'fas fa-user-tie',
        title: 'Sales Manager access:',
        desc: 'You can assign leads and update all team leads in this workspace.',
      };
    }
    if (isEffectiveSalesMember) {
      return {
        icon: 'fas fa-user-tag',
        title: 'Sales Member access:',
        desc: 'You can view pipeline leads and update leads assigned to you.',
      };
    }
    if (isEffectiveResearcher) {
      return {
        icon: 'fas fa-binoculars',
        title: 'Researcher access:',
        desc: 'You can add prospects and view pipeline information in read-only mode.',
      };
    }
    if (isEffectiveViewer) {
      return {
        icon: 'fas fa-eye',
        title: 'Viewer access (Read-Only):',
        desc: 'You can only view leads that are assigned to you in read-only mode.',
      };
    }
    return null;
  }, [
    isEffectiveOwner,
    isEffectiveAdmin,
    isEffectiveSalesManager,
    isEffectiveSalesMember,
    isEffectiveResearcher,
    isEffectiveViewer,
  ]);
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('kanban');
  const [customStages, setCustomStages] = useState([]);
  const [showStageModal, setShowStageModal] = useState(false);

  const STAGES = useMemo(() => {
    if (Array.isArray(customStages) && customStages.length > 0) {
      return customStages.map((s) => (typeof s === 'string' ? s : s.name));
    }
    return DEFAULT_STAGES;
  }, [customStages]);

  const [showArchived, setShowArchived] = useState(false);
  const [openFilter, setOpenFilter] = useState(null);
  const [filters, setFilters] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activeFilterField, setActiveFilterField] = useState('stages');
  const [filterSearchQuery, setFilterSearchQuery] = useState('');
  const filterMenuRef = useRef(null);
  const [sortOpen, setSortOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: '', dir: 'desc' });
  const [customerMenuOpen, setCustomerMenuOpen] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importSearchTerm, setImportSearchTerm] = useState('');
  const [savedProspects, setSavedProspects] = useState([]);
  const [selectedImportIds, setSelectedImportIds] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [importConflicts, setImportConflicts] = useState([]);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [confirmingImport, setConfirmingImport] = useState(false);
  const [pendingAddCustomer, setPendingAddCustomer] = useState(null);
  const [cards, setCards] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [noteInput, setNoteInput] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [noteError, setNoteError] = useState('');
  const [highlightNoteId, setHighlightNoteId] = useState(null);
  // Expanded note row (click-to-toggle) in the detail modal's note list.
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [unaddedNoteWarning, setUnaddedNoteWarning] = useState(false);
  const [unaddedTaskWarning, setUnaddedTaskWarning] = useState(false);
  const noteSectionRef = useRef(null);
  const taskSectionRef = useRef(null);
  const noteInputRef = useRef(null);
  const taskInputRef = useRef(null);
  const [taskInput, setTaskInput] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskAssignee, setTaskAssignee] = useState('');
  const [taskPriority, setTaskPriority] = useState('Medium');
  const [activeDetailTab, setActiveDetailTab] = useState('details');
  const [leadMeetings, setLeadMeetings] = useState([]);
  const leadMeetingsRef = useRef([]);
  const [isLoadingLeadMeetings, setIsLoadingLeadMeetings] = useState(false);
  const [leadMeetingFilter, setLeadMeetingFilter] = useState('all');
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [copiedMeetingId, setCopiedMeetingId] = useState(null);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Field-level evidence & locking state
  const [isLockModalOpen, setIsLockModalOpen] = useState(false);
  const [evidenceRefreshKey, setEvidenceRefreshKey] = useState(0);
  const [lockModalTarget, setLockModalTarget] = useState({
    fieldName: '',
    fieldLabel: '',
    currentValue: '',
  });
  const [evidenceDrawerTab, setEvidenceDrawerTab] = useState('fields');
  const [evidenceHighlightField, setEvidenceHighlightField] = useState(null);

  const handleOpenLockModal = useCallback((fieldName, fieldLabel, currentValue) => {
    setLockModalTarget({ fieldName, fieldLabel, currentValue });
    setIsLockModalOpen(true);
  }, []);

  const handleConfirmLock = useCallback(
    async (fieldName, reason, currentValue) => {
      if (!selectedCard?.id) return;
      const res = await axios.post(`/lead-pipeline/${selectedCard.id}/evidence/lock`, {
        fieldName,
        reason,
        currentValue,
      });
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Failed to lock field');
      }
      const lockData = res.data.data;
      const normField =
        String(fieldName).toLowerCase() === 'emails'
          ? 'email'
          : String(fieldName).toLowerCase() === 'phones'
            ? 'phone'
            : ['full_name', 'fullname', 'contact_name', 'contactname'].includes(
                  String(fieldName).toLowerCase()
                )
              ? 'name'
              : ['job_title', 'jobtitle', 'title', 'role'].includes(String(fieldName).toLowerCase())
                ? 'jobTitle'
                : ['company_name', 'companyname'].includes(String(fieldName).toLowerCase())
                  ? 'company'
                  : fieldName;

      setSelectedCard((prev) => {
        if (!prev) return prev;
        const nextLocks = { ...(prev.field_locks || {}) };
        nextLocks[fieldName] = lockData;
        nextLocks[normField] = lockData;
        const nextSummary = { ...(prev.provenance_summary || {}) };
        nextSummary[fieldName] = {
          provenanceClass: 'user_verified',
          source: 'User Verified',
          confidence: 100,
          lastVerifiedAt: lockData.lockedAt,
        };
        nextSummary[normField] = nextSummary[fieldName];
        return {
          ...prev,
          field_locks: nextLocks,
          provenance_summary: nextSummary,
        };
      });
      setFormData((prev) => {
        if (!prev) return prev;
        const nextLocks = { ...(prev.field_locks || {}) };
        nextLocks[fieldName] = lockData;
        nextLocks[normField] = lockData;
        const nextSummary = { ...(prev.provenance_summary || {}) };
        nextSummary[fieldName] = {
          provenanceClass: 'user_verified',
          source: 'User Verified',
          confidence: 100,
          lastVerifiedAt: lockData.lockedAt,
        };
        nextSummary[normField] = nextSummary[fieldName];
        return {
          ...prev,
          field_locks: nextLocks,
          provenance_summary: nextSummary,
        };
      });
      setCards((prevCards) =>
        prevCards.map((c) => {
          if (c.id === selectedCard.id) {
            const nextLocks = { ...(c.field_locks || {}) };
            nextLocks[fieldName] = lockData;
            nextLocks[normField] = lockData;
            return { ...c, field_locks: nextLocks };
          }
          return c;
        })
      );
      setEvidenceRefreshKey((k) => k + 1);
      setToast({
        id: Date.now(),
        type: 'success',
        message: `Locked ${lockModalTarget.fieldLabel || fieldName} successfully`,
      });
    },
    [selectedCard?.id, lockModalTarget.fieldLabel]
  );

  const handleUnlockField = useCallback(
    async (fieldName) => {
      if (!selectedCard?.id) return;
      const res = await axios.post(`/lead-pipeline/${selectedCard.id}/evidence/unlock`, {
        fieldName,
      });
      if (res.data?.success) {
        const normField =
          String(fieldName).toLowerCase() === 'emails'
            ? 'email'
            : String(fieldName).toLowerCase() === 'phones'
              ? 'phone'
              : fieldName;
        setSelectedCard((prev) => {
          if (!prev) return prev;
          const nextLocks = { ...(prev.field_locks || {}) };
          delete nextLocks[fieldName];
          delete nextLocks[normField];
          return {
            ...prev,
            field_locks: nextLocks,
          };
        });
        setFormData((prev) => {
          if (!prev) return prev;
          const nextLocks = { ...(prev.field_locks || {}) };
          delete nextLocks[fieldName];
          delete nextLocks[normField];
          return {
            ...prev,
            field_locks: nextLocks,
          };
        });
        setCards((prevCards) =>
          prevCards.map((c) => {
            if (c.id === selectedCard.id) {
              const nextLocks = { ...(c.field_locks || {}) };
              delete nextLocks[fieldName];
              delete nextLocks[normField];
              return { ...c, field_locks: nextLocks };
            }
            return c;
          })
        );
        setToast({
          id: Date.now(),
          type: 'info',
          message: `Unlocked ${fieldName}`,
        });
      }
    },
    [selectedCard?.id]
  );

  const handleOpenEvidenceTab = useCallback((subTab = 'fields', highlightField = null) => {
    setActiveDetailTab('evidence');
    setEvidenceDrawerTab(subTab);
    setEvidenceHighlightField(highlightField);
  }, []);

  useEffect(() => {
    if (!showFormModal || activeDetailTab !== 'meetings') return;
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, [showFormModal, activeDetailTab]);

  const fetchLeadMeetings = useCallback(
    async (leadId, leadEmail, leadName, allEmails = [], allIds = []) => {
      if (!workspaceId) return;
      if (!leadMeetingsRef.current || leadMeetingsRef.current.length === 0) {
        setIsLoadingLeadMeetings(true);
      }
      try {
        let rawMeetings = [];
        let typesRes = null;

        const [meetingsRes, fetchedTypes] = await Promise.all([
          meetingsApi.getMeetings(workspaceId, {
            leadId: leadId || undefined,
            leadEmail: leadEmail || undefined,
            leadName: leadName || undefined,
          }),
          meetingTypes.length === 0
            ? meetingsApi.getMeetingTypes(workspaceId)
            : Promise.resolve(null),
        ]);

        typesRes = fetchedTypes;
        if (Array.isArray(meetingsRes) && meetingsRes.length > 0) {
          rawMeetings = meetingsRes;
        } else {
          // If filtered query yielded no rows, fetch all workspace meetings as a safe fallback
          try {
            const allRes = await meetingsApi.getMeetings(workspaceId);
            if (Array.isArray(allRes)) {
              rawMeetings = allRes;
            }
          } catch (_) {}
        }

        if (Array.isArray(rawMeetings) && rawMeetings.length > 0) {
          const cleanLeadName = String(leadName || '')
            .trim()
            .toLowerCase();
          const cleanEmails = [leadEmail, ...(Array.isArray(allEmails) ? allEmails : [])]
            .filter(Boolean)
            .map((e) => String(e).trim().toLowerCase());

          const targetIds = [leadId, ...(Array.isArray(allIds) ? allIds : [])]
            .filter(Boolean)
            .map((id) => String(id).trim().toLowerCase());

          const matched = rawMeetings.filter((m) => {
            // 1. Direct ID match
            const mLeadId = String(
              m.leadId || m.lead_id || m.preMeetingBrief?.leadId || m.preMeetingBrief?.lead_id || ''
            )
              .trim()
              .toLowerCase();

            if (
              targetIds.length > 0 &&
              targetIds.some(
                (tid) =>
                  tid &&
                  (mLeadId === tid ||
                    mLeadId.includes(tid) ||
                    tid.includes(mLeadId) ||
                    String(m.guestNotes || '')
                      .toLowerCase()
                      .includes(tid))
              )
            ) {
              return true;
            }

            // 1b. Check m.leadIds array (e.g. Group Meetings)
            if (Array.isArray(m.leadIds) && m.leadIds.length > 0 && targetIds.length > 0) {
              const strLeadIds = m.leadIds.map((id) => String(id).trim().toLowerCase());
              if (targetIds.some((tid) => tid && strLeadIds.includes(tid))) {
                return true;
              }
            }

            // 1c. Check m.attendees or m.preMeetingBrief.attendees array
            const attList =
              Array.isArray(m.attendees) && m.attendees.length > 0
                ? m.attendees
                : Array.isArray(m.preMeetingBrief?.attendees) &&
                    m.preMeetingBrief.attendees.length > 0
                  ? m.preMeetingBrief.attendees
                  : [];

            if (attList.length > 0) {
              for (const att of attList) {
                const attId = String(att.id || att.crmId || '')
                  .trim()
                  .toLowerCase();
                const attEmail = String(att.email || '')
                  .trim()
                  .toLowerCase();
                const attName = String(att.name || '')
                  .trim()
                  .toLowerCase();

                if (
                  targetIds.length > 0 &&
                  attId &&
                  targetIds.some((tid) => tid && (tid === attId || attId.includes(tid)))
                ) {
                  return true;
                }
                if (
                  cleanEmails.length > 0 &&
                  attEmail &&
                  cleanEmails.some((em) => em && (em === attEmail || attEmail.includes(em)))
                ) {
                  return true;
                }
                if (
                  cleanLeadName &&
                  attName &&
                  (attName === cleanLeadName ||
                    attName.includes(cleanLeadName) ||
                    cleanLeadName.includes(attName))
                ) {
                  return true;
                }
              }
            }

            // 2. Email match
            const mGuestEmail = String(m.guestEmail || m.guest_email || '')
              .trim()
              .toLowerCase();
            if (
              mGuestEmail &&
              cleanEmails.some(
                (em) =>
                  em && (em === mGuestEmail || mGuestEmail.includes(em) || em.includes(mGuestEmail))
              )
            ) {
              return true;
            }

            // 3. Guest Name match
            const mGuestName = String(
              m.guestName || m.guest_name || m.preMeetingBrief?.leadName || ''
            )
              .trim()
              .toLowerCase();
            if (cleanLeadName && mGuestName) {
              if (
                mGuestName === cleanLeadName ||
                mGuestName.includes(cleanLeadName) ||
                cleanLeadName.includes(mGuestName)
              ) {
                return true;
              }
            }

            // 4. Meeting Title contains Lead Name (e.g. "Technical Review: Teck Ann", "Product Demo: Teck Ann")
            const mTitle = String(m.title || '')
              .trim()
              .toLowerCase();
            if (cleanLeadName && cleanLeadName.length >= 2) {
              if (mTitle.includes(cleanLeadName)) {
                return true;
              }
              // Check multi-word name tokens (e.g. "Teck Ann")
              const nameTokens = cleanLeadName.split(/\s+/).filter((t) => t.length >= 3);
              if (nameTokens.length > 1 && nameTokens.every((token) => mTitle.includes(token))) {
                return true;
              }
            }

            return false;
          });

          setLeadMeetings(matched);
          leadMeetingsRef.current = matched;
        } else {
          setLeadMeetings([]);
          leadMeetingsRef.current = [];
        }

        if (Array.isArray(typesRes) && typesRes.length > 0) {
          setMeetingTypes(typesRes);
        }
      } catch (err) {
        console.warn('Failed to load meetings for lead:', err);
        setLeadMeetings([]);
        leadMeetingsRef.current = [];
      } finally {
        setIsLoadingLeadMeetings(false);
      }
    },
    [workspaceId, meetingTypes.length]
  );

  const categorizedLeadMeetings = useMemo(() => {
    const getMeetingDateKey = (m) => {
      const dtStr = m.startTime || m.start_time || m.createdAt;
      if (!dtStr) return '';
      const dt = new Date(dtStr);
      if (isNaN(dt.getTime())) return '';
      const y = dt.getFullYear();
      const mth = String(dt.getMonth() + 1).padStart(2, '0');
      const d = String(dt.getDate()).padStart(2, '0');
      return `${y}-${mth}-${d}`;
    };

    const getStatusPriority = (meeting) => {
      const status = getEffectiveMeetingStatus(meeting);
      if (status === 'in_progress') return 1;
      if (status === 'upcoming' || status === 'confirmed') return 2;
      if (status === 'completed') return 3;
      if (status === 'cancelled') return 4;
      return 5;
    };

    const sortMeetingsList = (list) => {
      return [...list].sort((a, b) => {
        // 1. Follow the Date (latest date at the top)
        const dateKeyA = getMeetingDateKey(a);
        const dateKeyB = getMeetingDateKey(b);
        if (dateKeyA !== dateKeyB) {
          return dateKeyB.localeCompare(dateKeyA); // Latest date first
        }

        // 2. Follow the Status (In Progress -> Upcoming -> Completed -> Cancelled)
        const pA = getStatusPriority(a);
        const pB = getStatusPriority(b);
        if (pA !== pB) {
          return pA - pB;
        }

        // 3. Within same date & same status:
        const timeA = new Date(a.startTime || a.start_time || a.createdAt || 0).getTime();
        const timeB = new Date(b.startTime || b.start_time || b.createdAt || 0).getTime();
        if (pA <= 2) {
          // For upcoming / in_progress on same day: earlier upcoming first (e.g. 12:00 PM before 12:30 PM)
          if (timeA !== timeB) return timeA - timeB;
          // When start times are equal, whichever ends first appears at the front
          const endA =
            a.endTime || a.end_time
              ? new Date(a.endTime || a.end_time).getTime()
              : timeA + (Number(a.durationMinutes || a.duration_minutes) || 30) * 60000;
          const endB =
            b.endTime || b.end_time
              ? new Date(b.endTime || b.end_time).getTime()
              : timeB + (Number(b.durationMinutes || b.duration_minutes) || 30) * 60000;
          if (endA !== endB) return endA - endB;
          return (a.title || '').localeCompare(b.title || '');
        }
        if (timeA !== timeB) return timeB - timeA;
        return (a.title || '').localeCompare(b.title || '');
      });
    };

    const upcoming = [];
    const inProgress = [];
    const completed = [];
    const cancelled = [];

    (leadMeetings || []).forEach((m) => {
      const status = getEffectiveMeetingStatus(m);
      if (status === 'cancelled') {
        cancelled.push(m);
      } else if (status === 'in_progress') {
        inProgress.push(m);
      } else if (status === 'completed') {
        completed.push(m);
      } else {
        upcoming.push(m);
      }
    });

    return {
      all: sortMeetingsList(leadMeetings || []),
      upcoming: sortMeetingsList(upcoming),
      in_progress: sortMeetingsList(inProgress),
      completed: sortMeetingsList(completed),
      cancelled: sortMeetingsList(cancelled),
    };
  }, [leadMeetings]);

  const handleCopyMeetingLink = (mId, url) => {
    if (!url) return;
    try {
      navigator.clipboard.writeText(url);
      setCopiedMeetingId(mId);
      setTimeout(() => {
        setCopiedMeetingId(null);
      }, 2000);
    } catch (_) {}
  };

  const formatMeetingDate = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return String(isoStr);
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatMeetingTimeRange = (startIso, endIso, durationMins = 30) => {
    if (!startIso) return '';
    const start = new Date(startIso);
    let end = endIso ? new Date(endIso) : new Date(start.getTime() + (durationMins || 30) * 60000);
    if (isNaN(start.getTime())) return '';
    const startStr = start.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    const endStr = end.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    const mins = Number(durationMins) || 30;
    return `${startStr} – ${endStr} (${mins} min)`;
  };

  const handleOpenMeetingsTab = () => {
    setActiveDetailTab('meetings');
    if (selectedCard && (!leadMeetingsRef.current || leadMeetingsRef.current.length === 0)) {
      const leadEmail =
        formData.email || (formData.emails && formData.emails[0]) || selectedCard.email || '';
      const leadName = formData.name || selectedCard.name || '';
      const allEmails = formData.emails || (formData.email ? [formData.email] : []);
      const allIds = [
        selectedCard.id,
        selectedCard.shared_item_id,
        selectedCard.lead_id,
        formData.id,
      ].filter(Boolean);
      fetchLeadMeetings(selectedCard.id, leadEmail, leadName, allEmails, allIds);
    }
  };

  const handleScheduleMeetingForLead = () => {
    if (!selectedCard) return;
    const leadName = formData.name || selectedCard.name || selectedCard.full_name || '';
    const leadEmail =
      formData.email || (formData.emails && formData.emails[0]) || selectedCard.email || '';
    const leadCompany = formData.company || selectedCard.company || selectedCard.company_name || '';
    const leadJobTitle =
      formData.jobTitle ||
      formData.job_title ||
      selectedCard.jobTitle ||
      selectedCard.job_title ||
      selectedCard.title ||
      '';
    const leadStage = formData.stage || selectedCard.stage || 'New';
    const leadDealVal =
      formData.dealValue ||
      formData.deal_value ||
      selectedCard.dealValue ||
      selectedCard.deal_value ||
      '';
    const leadCurrency = formData.currency || selectedCard.currency || 'USD';
    const leadScore = formData.leadScore ?? selectedCard.leadScore ?? selectedCard.lead_score ?? 75;

    const leadPayload = {
      id: selectedCard.id,
      lead_id: selectedCard.lead_id || selectedCard.shared_item_id || selectedCard.id,
      name: leadName,
      full_name: leadName,
      email: leadEmail,
      company: leadCompany,
      company_name: leadCompany,
      job_title: leadJobTitle,
      title: leadJobTitle,
      stage: leadStage,
      deal_value: leadDealVal,
      dealValue: leadDealVal,
      currency: leadCurrency,
      lead_score: leadScore,
      leadScore: leadScore,
      crm_meta: {
        ...(selectedCard.crm_meta || {}),
        name: leadName,
        email: leadEmail,
        company: leadCompany,
        jobTitle: leadJobTitle,
        stage: leadStage,
        dealValue: leadDealVal,
        deal_value: leadDealVal,
        currency: leadCurrency,
        lead_score: leadScore,
      },
    };

    navigate('/meetings', {
      state: {
        openSchedule: true,
        meetingType: 'one_on_one',
        lead: leadPayload,
      },
    });
  };

  // Id of the expanded activity entry in the Activity tab (prev → new diff).
  const [activeActivityId, setActiveActivityId] = useState(null);
  const draggedCardIdRef = useRef(null);
  const isDraggingRef = useRef(false);
  // Optimistic notes that are still being saved to the server. key = optimistic
  // note id, value = { note, cardId }. Used to prevent a server snapshot from
  // dropping notes that were added while another request was in flight.
  const pendingNotesRef = useRef(new Map());
  const selectedCardIdRef = useRef(null);
  const customerMenuRef = useRef(null);
  const sortMenuRef = useRef(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [trashOver, setTrashOver] = useState(false);
  const [pendingDeleteCard, setPendingDeleteCard] = useState(null);
  const [tablePage, setTablePage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const [pendingBulkAssign, setPendingBulkAssign] = useState(false);
  const [pendingBulkStage, setPendingBulkStage] = useState(false);
  const [pendingBulkFollowUp, setPendingBulkFollowUp] = useState(false);
  const [bulkAssignTo, setBulkAssignTo] = useState('');
  const [bulkFollowUpDate, setBulkFollowUpDate] = useState('');
  const [bulkFollowUpTask, setBulkFollowUpTask] = useState('');
  const [bulkFollowUpPriority, setBulkFollowUpPriority] = useState('Medium');
  const [bulkStage, setBulkStage] = useState('');
  const [bulkStageModal, setBulkStageModal] = useState(''); // '', 'won' | 'lost'
  const [errors, setErrors] = useState({});
  const [showLostModal, setShowLostModal] = useState(false);
  const [showWonModal, setShowWonModal] = useState(false);
  const [showNoteHistory, setShowNoteHistory] = useState(false);
  const [deletedNotes, setDeletedNotes] = useState([]);
  const [noteHistoryLoading, setNoteHistoryLoading] = useState(false);
  const [lostForm, setLostForm] = useState({ reason: '', context: '', lostDate: '' });
  const [lostError, setLostError] = useState('');
  const [wonForm, setWonForm] = useState({ value: '', wonDate: '' });
  const [wonError, setWonError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const salesMemberOptions = useMemo(() => {
    return workspaceMembers.filter((m) => {
      if (!m.user_id || m.is_active === false) return false;
      const r = (m.workspace_role || '').toLowerCase();
      return (
        r === 'org_sales_member' ||
        r === 'sales_member' ||
        r === 'org_member' ||
        r === 'org_viewer' ||
        r === 'viewer'
      );
    });
  }, [workspaceMembers]);
  const [pipelineCurrency, setPipelineCurrency] = useState('USD');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [currencySearch, setCurrencySearch] = useState('');
  const currencyDropdownRef = useRef(null);
  const [fxRates, setFxRates] = useState(DEFAULT_FALLBACK_FX_RATES);
  const [fxRateMeta, setFxRateMeta] = useState(null);

  // Close currency popover when clicking outside
  useEffect(() => {
    const handleCurrencyOutsideClick = (e) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(e.target)) {
        setShowCurrencyDropdown(false);
      }
    };
    if (showCurrencyDropdown) {
      document.addEventListener('mousedown', handleCurrencyOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleCurrencyOutsideClick);
    };
  }, [showCurrencyDropdown]);

  // Fetch live market FX rates from Frankfurter / ECB on mount
  useEffect(() => {
    let isMounted = true;
    const fetchLiveRates = async () => {
      try {
        const res = await axios.get('/currency/rates');
        if (res.data?.success && res.data?.data?.rates && isMounted) {
          setFxRates(res.data.data.rates);
          setFxRateMeta(res.data.data);
        }
      } catch (err) {
        console.warn('[CRM] Could not fetch live FX rates, using standard baseline:', err.message);
      }
    };
    fetchLiveRates();
    return () => {
      isMounted = false;
    };
  }, []);
  const [formData, setFormData] = useState({
    id: null,
    leadId: '',
    workspaceId: '',
    sourceProspectId: '',
    name: '',
    jobTitle: '',
    company: '',
    email: '',
    emails: [''],
    phone: '',
    phones: [''],
    linkedinUrl: '',
    companyWebsite: '',
    industry: '',
    country: '',
    source: 'LinkedIn',
    stage: 'New',
    owner: currentUsername,
    assignee: '',
    leadScore: 0,
    dealName: '',
    dealValue: '',
    dealProbability: '',
    currency: 'USD',
    closeDate: '',
    productInterest: '',
    proposalReference: '',
    wonValue: '',
    lastActivityDate: '',
    nextFollowUpDate: '',
    wonDate: '',
    lostDate: '',
    lostReason: '',
    lostContext: '',
    createdBy: '',
    createdDate: '',
    updatedDate: '',
    notes: [],
    tasks: [],
    activities: [],
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeTag = document.activeElement?.tagName;
      if (
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeTag) &&
        !document.activeElement?.classList?.contains('crm-filter-search-input')
      ) {
        return;
      }
      if (e.shiftKey && (e.key === 'F' || e.key === 'f')) {
        e.preventDefault();
        setFiltersOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) {
        setFiltersOpen(false);
      }
    };
    if (filtersOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filtersOpen]);

  const toggleFilterOption = (fieldKey, optionId) => {
    setFilters((prev) => {
      const currentList = Array.isArray(prev[fieldKey])
        ? prev[fieldKey]
        : prev[fieldKey]
          ? [prev[fieldKey]]
          : [];
      const exists = currentList.includes(optionId);
      const nextList = exists
        ? currentList.filter((item) => item !== optionId)
        : [...currentList, optionId];

      if (nextList.length === 0) {
        const nextFilters = { ...prev };
        delete nextFilters[fieldKey];
        return nextFilters;
      }
      return { ...prev, [fieldKey]: nextList };
    });
  };

  const clearFieldFilter = (fieldKey) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  };

  const toggleSort = (key) => {
    setSortConfig((current) =>
      current.key === key
        ? { key, dir: current.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: 'desc' }
    );
  };

  const selectFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setOpenFilter(null);
  };

  const clearAllFilters = () => {
    setFilters({});
    setFilterSearchQuery('');
    setOpenFilter(null);
  };

  const isFieldLocked = useCallback(
    (fieldName) => {
      const locks = {
        ...(selectedCard?.field_locks || {}),
        ...(formData?.field_locks || {}),
      };
      if (!locks || typeof locks !== 'object') return false;
      const checkLock = (val) => {
        if (!val) return false;
        if (val === true) return true;
        if (typeof val === 'object') {
          if (val.isLocked !== undefined) return Boolean(val.isLocked);
          if (val.is_locked !== undefined) return Boolean(val.is_locked);
          if (val.lockedAt || val.locked_at || val.reason || val.lockReason) return true;
        }
        return false;
      };

      const norm = String(fieldName || '')
        .trim()
        .toLowerCase();
      if (checkLock(locks[fieldName])) return true;
      if (checkLock(locks[norm])) return true;

      if (['email', 'emails'].includes(norm)) {
        return Boolean(checkLock(locks.email) || checkLock(locks.emails));
      }
      if (['phone', 'phones', 'phone_number', 'phonenumber'].includes(norm)) {
        return Boolean(
          checkLock(locks.phone) ||
          checkLock(locks.phones) ||
          checkLock(locks.phone_number) ||
          checkLock(locks.phoneNumber)
        );
      }
      if (['company', 'company_name', 'companyname'].includes(norm)) {
        return Boolean(
          checkLock(locks.company) || checkLock(locks.company_name) || checkLock(locks.companyName)
        );
      }
      if (['jobtitle', 'job_title', 'title', 'role'].includes(norm)) {
        return Boolean(
          checkLock(locks.jobTitle) ||
          checkLock(locks.job_title) ||
          checkLock(locks.title) ||
          checkLock(locks.role)
        );
      }
      if (['name', 'full_name', 'fullname', 'contact_name', 'contactname'].includes(norm)) {
        return Boolean(
          checkLock(locks.name) ||
          checkLock(locks.full_name) ||
          checkLock(locks.fullName) ||
          checkLock(locks.contact_name) ||
          checkLock(locks.contactName)
        );
      }
      return false;
    },
    [selectedCard?.field_locks, formData?.field_locks]
  );

  const updateFormField = (key, value) => {
    setFormData((current) => {
      const next = { ...current, [key]: value };
      if (key === 'emails') {
        const valid = (Array.isArray(value) ? value : [value])
          .map((e) => String(e || '').trim())
          .filter(Boolean);
        next.email = valid[0] || '';
      }
      if (key === 'phones') {
        const valid = (Array.isArray(value) ? value : [value])
          .map((p) => String(p || '').trim())
          .filter(Boolean);
        next.phone = valid[0] || '';
      }
      if (key === 'stage' && value !== 'Won') {
        next.wonDate = '';
      }
      next.leadScore = calculateLeadScore(next, customStages).totalScore;
      return next;
    });
    setErrors((current) => {
      const next = { ...current };
      delete next[key];
      delete next[`${key}_0`];
      delete next[`${key}_1`];
      delete next[`${key}_2`];
      if (key === 'source') {
        delete next.linkedinUrl;
        delete next.companyWebsite;
        delete next.email;
        delete next.emails;
      }
      if (key === 'company') {
        const origCompany = cleanCompany(
          selectedCard?.company ||
            selectedCard?.company_name ||
            selectedCard?.companyName ||
            selectedCard?.company_details?.companyName ||
            ''
        ).trim();
        const origWebsite = String(
          selectedCard?.companyWebsite ||
            selectedCard?.company_website ||
            selectedCard?.website ||
            ''
        ).trim();
        const curCompany = cleanCompany(value || '').trim();
        const curWebsite = String(formData.companyWebsite || '').trim();
        const origDomain = extractDomainFromUrl(origWebsite);
        const curDomain = extractDomainFromUrl(curWebsite);

        if (
          selectedCard &&
          origCompany &&
          curCompany &&
          curCompany.toLowerCase() !== origCompany.toLowerCase()
        ) {
          if (origWebsite) {
            if (!curWebsite) {
              next.companyWebsite = 'Company website is required when changing company name.';
            } else if (origDomain && curDomain && origDomain === curDomain) {
              next.companyWebsite = 'Please update the company website URL for the new company.';
            } else if (curWebsite && !isCompanyDomainMatching(value, curWebsite)) {
              next.companyWebsite = `Website domain (${curDomain}) does not match company "${value}".`;
            } else {
              delete next.companyWebsite;
            }
          } else if (curWebsite && !isCompanyDomainMatching(value, curWebsite)) {
            next.companyWebsite = `Website domain (${curDomain}) does not match company "${value}".`;
          } else {
            delete next.companyWebsite;
          }
        } else if (curWebsite && value && !isCompanyDomainMatching(value, curWebsite)) {
          next.companyWebsite = `Website domain (${curDomain}) does not match company "${value}".`;
        } else {
          delete next.companyWebsite;
        }
      }
      if (key === 'companyWebsite') {
        const origWebsite = String(
          selectedCard?.companyWebsite ||
            selectedCard?.company_website ||
            selectedCard?.website ||
            ''
        ).trim();
        const origDomain = extractDomainFromUrl(origWebsite);
        const curDomain = extractDomainFromUrl(value || '');
        const currentCompany = String(formData.company || '').trim();

        if (value && currentCompany && !isCompanyDomainMatching(currentCompany, value)) {
          next.companyWebsite = `Website domain (${curDomain}) does not match company "${formData.company}".`;
        } else if (value && (!origDomain || curDomain !== origDomain)) {
          delete next.companyWebsite;
        }
      }
      return next;
    });
  };

  const fetchCards = async (showLoadingSpinner = false) => {
    if (showLoadingSpinner) {
      setIsLoading(true);
    }
    try {
      const response = await axios.get('/lead-pipeline');
      const fetchedCards = response.data?.data || [];
      setCards(
        fetchedCards.map((card) => {
          const rawScore =
            card.leadScore ?? card.lead_score ?? card.crm?.leadScore ?? card.crm?.lead_score;
          const resolvedEmails = extractEmailList(
            (Array.isArray(card.crm?.emails) && card.crm.emails.length > 0
              ? card.crm.emails
              : null) ??
              (Array.isArray(card.email_addresses) && card.email_addresses.length > 0
                ? card.email_addresses
                : null) ??
              (Array.isArray(card.emails) && card.emails.length > 0 ? card.emails : null) ??
              card.crm?.email ??
              card.email_addresses ??
              card.email
          );
          const primaryEmail =
            resolvedEmails[0] ||
            emailToString(card.crm?.email ?? card.email ?? card.email_addresses);

          const resolvedPhones = extractPhoneList(
            (Array.isArray(card.crm?.phones) && card.crm.phones.length > 0
              ? card.crm.phones
              : null) ??
              (Array.isArray(card.phone_numbers) && card.phone_numbers.length > 0
                ? card.phone_numbers
                : null) ??
              (Array.isArray(card.phones) && card.phones.length > 0 ? card.phones : null) ??
              (Array.isArray(card.phone_number) && card.phone_number.length > 0
                ? card.phone_number
                : null) ??
              card.crm?.phone ??
              card.phone_numbers ??
              card.phone_number ??
              card.phone
          );
          const primaryPhone =
            resolvedPhones[0] ||
            phoneToString(card.crm?.phone ?? card.phone ?? card.phone_number ?? card.phone_numbers);

          const crm = card.crm || {};
          const resolvedName = crm.name || card.name || card.full_name || '';
          const resolvedJobTitle = crm.jobTitle || card.job_title || card.jobTitle || '';
          const resolvedCompany = cleanCompany(
            crm.company ||
              crm.companyName ||
              crm.company_name ||
              card.company ||
              card.company_name ||
              card.companyName ||
              card.companyDetails?.name ||
              card.company_details?.name ||
              card.company_details?.companyName ||
              card.company_data?.name ||
              card.companyData?.name
          );
          const resolvedLinkedinUrl =
            crm.linkedinUrl || crm.linkedin_url || card.linkedin_url || card.linkedinUrl || '';
          const resolvedCompanyWebsite =
            crm.companyWebsite ||
            crm.company_website ||
            card.companyWebsite ||
            card.company_website ||
            '';
          const resolvedIndustry =
            crm.industry ||
            card.industry ||
            card.company_industry ||
            card.companyDetails?.mainIndustry ||
            '';
          const resolvedCountry = crm.country || card.country || card.location?.country || '';
          const rawStage = card.stage || crm.stage || 'New';
          const resolvedStage =
            STAGES.find((s) => s.toLowerCase() === String(rawStage).toLowerCase()) || rawStage;

          const parsedCard = {
            ...card,
            ...crm,
            name: resolvedName,
            jobTitle: resolvedJobTitle,
            company: resolvedCompany,
            company_name: resolvedCompany,
            companyName: resolvedCompany,
            stage: resolvedStage,
            email: primaryEmail,
            emails: resolvedEmails,
            phone: primaryPhone,
            phones: resolvedPhones,
            linkedinUrl: resolvedLinkedinUrl,
            companyWebsite: resolvedCompanyWebsite,
            industry: resolvedIndustry,
            country: resolvedCountry,
            dealValue: crm.dealValue || crm.deal_value || card.dealValue || card.deal_value || '0',
            productInterest:
              crm.productInterest ||
              crm.product_interest ||
              card.productInterest ||
              card.product_interest ||
              '',
            tasks: Array.isArray(card.tasks)
              ? card.tasks
              : Array.isArray(crm.tasks)
                ? crm.tasks
                : [],
          };
          const calculatedScore = calculateLeadScore(parsedCard, customStages).totalScore;
          const leadScore = calculatedScore;

          return {
            ...parsedCard,
            id: card.id || card.prospect_id || card.leadId || Date.now(),
            leadId: card.leadId || card.lead_id || '',
            workspaceId: card.workspaceId || card.workspace_id || '',
            sourceProspectId:
              card.sourceProspectId || card.provider_id || card.external_id || card.lusha_id || '',
            name: resolvedName,
            jobTitle: resolvedJobTitle,
            company: resolvedCompany,
            company_name: resolvedCompany,
            companyName: resolvedCompany,
            company_details: card.company_details || card.companyDetails || null,
            companyDetails: card.company_details || card.companyDetails || null,
            companyLogo:
              card.companyLogo ||
              card.company_logo ||
              card.logoUrl ||
              card.logo_url ||
              card.companyDetails?.logoUrl ||
              card.companyDetails?.logo_url ||
              card.company_details?.logoUrl ||
              card.company_details?.logo_url ||
              card.company_details?.logo ||
              card.company_details?.companyLogo ||
              card.company?.logoUrl ||
              card.company?.logo_url ||
              getCompanyLogoUrl(
                resolvedCompany,
                card.company_details || card.companyDetails,
                emailToString(crm.email || card.email),
                resolvedCompanyWebsite
              ) ||
              null,
            email: emailToString(crm.email || card.email),
            emails: resolvedEmails,
            phone: primaryPhone,
            phones: resolvedPhones,
            linkedinUrl: resolvedLinkedinUrl,
            companyWebsite: resolvedCompanyWebsite,
            industry: resolvedIndustry,
            country: resolvedCountry,
            provider: card.provider || crm.provider || 'manual',
            source:
              card.provider === 'landing_page' ||
              crm.provider === 'landing_page' ||
              isLandingPageLead(card) ||
              isLandingPageLead(crm)
                ? 'Landing Page'
                : normalizeSource(
                    crm.source ||
                      card.source ||
                      (card.provider === 'manual' ? 'Website' : 'Prospect Finder')
                  ),
            owner: crm.owner || card.owner || card.creator_username || card.creator || '',
            assignee: crm.assignee || card.assignee || '',
            leadScore,
            dealName: crm.dealName || crm.deal_name || card.dealName || card.deal_name || '',
            dealValue: crm.dealValue || crm.deal_value || card.dealValue || card.deal_value || '0',
            dealProbability:
              crm.dealProbability ||
              crm.deal_probability ||
              card.dealProbability ||
              card.deal_probability ||
              '',
            currency: crm.currency || card.currency || 'USD',
            closeDate: crm.closeDate || crm.close_date || card.closeDate || card.close_date || '',
            productInterest:
              crm.productInterest ||
              crm.product_interest ||
              card.productInterest ||
              card.product_interest ||
              '',
            proposalReference:
              crm.proposalReference ||
              crm.proposal_reference ||
              card.proposalReference ||
              card.proposal_reference ||
              '',
            wonValue: crm.wonValue || crm.won_value || card.wonValue || card.won_value || '',
            lastActivityDate:
              crm.lastActivityDate || card.lastActivityDate || card.last_activity_date || '',
            nextFollowUpDate:
              crm.nextFollowUpDate || card.nextFollowUpDate || card.next_follow_up_date || '',
            wonDate: crm.wonDate || card.wonDate || card.won_date || '',
            lostDate: crm.lostDate || card.lostDate || card.lost_date || '',
            lostReason: crm.lostReason || card.lostReason || card.lost_reason || '',
            lostContext: crm.lostContext || card.lostContext || card.lost_context || '',
            createdBy: crm.createdBy || card.createdBy || card.created_by || '',
            createdDate: crm.createdDate || card.createdDate || card.created_date || '',
            updatedDate: crm.updatedDate || card.updatedDate || card.updated_date || '',
            tasks: Array.isArray(card.tasks)
              ? card.tasks
              : Array.isArray(crm.tasks)
                ? crm.tasks
                : [],
            notes: Array.isArray(card.notes)
              ? card.notes
              : Array.isArray(crm.notes)
                ? crm.notes
                : [],
            activities: Array.isArray(card.activities)
              ? card.activities
              : Array.isArray(crm.activities)
                ? crm.activities
                : [],
            field_locks: card.field_locks || crm.field_locks || {},
            provenance_summary: card.provenance_summary || crm.provenance_summary || {},
          };
        })
      );
      setSelectedCard((current) => {
        if (!current) return null;
        const matching = fetchedCards.find(
          (c) => String(c.id || c.prospect_id || c.leadId) === String(current.id)
        );
        return matching ? { ...current, ...matching } : current;
      });
    } catch (error) {
      console.error('Error fetching CRM cards:', error);
    } finally {
      if (showLoadingSpinner) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchCards(true);
  }, []);

  // Real-time live database synchronization: when a lead is created, updated, or
  // deleted in this workspace (by any collaborator), refresh cards without requiring
  // a manual page reload.
  useEffect(() => {
    let timer = null;
    const handleLeadChanged = (e) => {
      const { workspaceId: eventWsId, actorId } = e.detail || {};
      // Verify workspace match if workspaceId is present
      if (eventWsId && workspaceId && String(eventWsId) !== String(workspaceId)) return;
      // Skip live re-fetch if this client was the actor who initiated the change,
      // avoiding race conditions that overwrite optimistic UI state
      if (actorId && currentUserId && String(actorId) === String(currentUserId)) return;

      // Debounce fetchCards (300ms) to avoid request flooding during batch actions
      clearTimeout(timer);
      timer = setTimeout(() => {
        fetchCards(false);
      }, 300);
    };

    window.addEventListener('lead-changed', handleLeadChanged);
    return () => {
      window.removeEventListener('lead-changed', handleLeadChanged);
      clearTimeout(timer);
    };
  }, [workspaceId, currentUserId]);

  const fetchWorkspaceStages = useCallback(async () => {
    try {
      const res = await axios.get('/lead-pipeline/stages');
      if (res.data?.success && Array.isArray(res.data?.data) && res.data.data.length > 0) {
        setCustomStages(res.data.data);
      }
    } catch (err) {
      console.warn('Could not fetch pipeline stages, using defaults:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchWorkspaceStages();
  }, [fetchWorkspaceStages, workspaceId]);

  useEffect(() => {
    const handleStagesEvent = (e) => {
      if (e.detail?.stages && Array.isArray(e.detail.stages)) {
        setCustomStages(e.detail.stages);
      } else {
        fetchWorkspaceStages();
      }
      fetchCards(false);
    };
    window.addEventListener('crm-pipeline-stages-updated', handleStagesEvent);
    return () => {
      window.removeEventListener('crm-pipeline-stages-updated', handleStagesEvent);
    };
  }, [fetchWorkspaceStages]);

  // Auto-open lead detail from URL params (e.g. from notification click)
  useEffect(() => {
    const leadId = searchParams.get('leadId');
    const noteId = searchParams.get('noteId');
    if (!leadId || cards.length === 0) return;
    const card = cards.find((c) => String(c.id) === String(leadId));
    if (card && !selectedCard) {
      openCardDetails(card);
      if (noteId) setHighlightNoteId(noteId);
      // Clear URL params after opening
      setSearchParams({}, { replace: true });
    }
  }, [cards, searchParams.get('leadId'), searchParams.get('noteId')]);

  // Reset list pagination whenever the active filters or search change
  useEffect(() => {
    setTablePage(1);
  }, [searchTerm, filters]);

  // Close the customer actions dropdown when clicking outside of it
  useEffect(() => {
    if (!customerMenuOpen) return;
    const handleClickOutside = (event) => {
      if (customerMenuRef.current && !customerMenuRef.current.contains(event.target)) {
        setCustomerMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [customerMenuOpen]);

  // Close the sort dropdown when clicking outside of it
  useEffect(() => {
    if (!sortOpen) return;
    const handleClickOutside = (event) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target)) {
        setSortOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sortOpen]);

  useEffect(() => {
    if (!workspaceId) return;
    let active = true;
    axios
      .get(`/workspace/${workspaceId}/members`)
      .then((response) => {
        if (active && response.data?.success) {
          setWorkspaceMembers(response.data.members || []);
        }
      })
      .catch((error) => {
        console.warn('Could not load workspace members:', error);
      });
    return () => {
      active = false;
    };
  }, [workspaceId]);

  const resetFormData = () => {
    setFormData({
      id: null,
      leadId: '',
      workspaceId: '',
      sourceProspectId: '',
      name: '',
      jobTitle: '',
      company: '',
      email: '',
      emails: [''],
      phone: '',
      phones: [''],
      linkedinUrl: '',
      companyWebsite: '',
      industry: '',
      country: '',
      source: 'LinkedIn',
      stage: 'New',
      owner: currentUsername,
      assignee: '',
      leadScore: 0,
      dealName: '',
      dealValue: '',
      dealProbability: '',
      currency: 'USD',
      closeDate: '',
      productInterest: '',
      proposalReference: '',
      wonValue: '',
      lastActivityDate: '',
      nextFollowUpDate: '',
      wonDate: '',
      lostDate: '',
      lostReason: '',
      lostContext: '',
      createdBy: '',
      createdDate: '',
      updatedDate: '',
      notes: [],
      tasks: [],
      activities: [],
    });
    setSubmitError('');
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');

    // Check if user typed a note or task but forgot to click "+ Add note" or "+ Add"
    const hasUnaddedNote = Boolean(noteInput && noteInput.trim().length > 0);
    const hasUnaddedTask = Boolean(taskInput && taskInput.trim().length > 0);

    if (hasUnaddedNote || hasUnaddedTask) {
      setActiveDetailTab('details');
      setUnaddedNoteWarning(hasUnaddedNote);
      setUnaddedTaskWarning(hasUnaddedTask);

      const reminderMsg =
        hasUnaddedNote && hasUnaddedTask
          ? 'You have an unadded note and task. Please click "+ Add note" and "+ Add" (or clear the text) before saving.'
          : hasUnaddedNote
            ? 'You have an unadded note. Please click "+ Add note" (or clear the text) before saving.'
            : 'You have an unadded task. Please click "+ Add" (or clear the text) before saving.';

      setToast({
        show: true,
        message: reminderMsg,
        type: 'warning',
      });

      setTimeout(() => {
        const targetSection = hasUnaddedNote ? noteSectionRef.current : taskSectionRef.current;
        const targetInput = hasUnaddedNote ? noteInputRef.current : taskInputRef.current;
        if (targetSection) {
          targetSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        if (targetInput) {
          targetInput.focus();
          targetInput.classList.add('crm-input-jitter');
          setTimeout(() => targetInput.classList.remove('crm-input-jitter'), 600);
        }
      }, 50);
      return;
    }

    const nextErrors = {};
    if (!formData.name || !String(formData.name).trim()) nextErrors.name = 'Name is required.';
    if (!formData.company || !String(formData.company).trim())
      nextErrors.company = 'Company is required.';

    // Company Name -> Company Website Validation
    if (selectedCard) {
      const origCompany = cleanCompany(
        selectedCard.company ||
          selectedCard.company_name ||
          selectedCard.companyName ||
          selectedCard.company_details?.companyName ||
          selectedCard.company_details?.name ||
          selectedCard.companyDetails?.name ||
          selectedCard.crm?.company ||
          selectedCard.crm?.companyName ||
          ''
      ).trim();
      const origWebsite = String(
        selectedCard.companyWebsite ||
          selectedCard.company_website ||
          selectedCard.website ||
          selectedCard.crm?.companyWebsite ||
          selectedCard.company_details?.website ||
          selectedCard.company_details?.domain ||
          selectedCard.companyDetails?.website ||
          ''
      ).trim();

      const curCompany = cleanCompany(formData.company || '').trim();
      const curWebsite = String(formData.companyWebsite || '').trim();

      const origDomain = extractDomainFromUrl(origWebsite);
      const curDomain = extractDomainFromUrl(curWebsite);

      // If company name was changed:
      if (origCompany && curCompany && curCompany.toLowerCase() !== origCompany.toLowerCase()) {
        // Only require website update if the lead originally had a website
        if (origWebsite) {
          if (!curWebsite) {
            nextErrors.companyWebsite = 'Company website is required when changing company name.';
          } else if (origDomain && curDomain && origDomain === curDomain) {
            nextErrors.companyWebsite =
              'Please update the company website URL for the new company.';
          }
        }
      }
    }

    // Dynamic Company Name vs Website Domain Match Detection (Zero Hardcoding)
    if (formData.company && formData.companyWebsite && String(formData.companyWebsite).trim()) {
      const isMatch = isCompanyDomainMatching(formData.company, formData.companyWebsite);
      if (!isMatch) {
        const cleanDom = extractDomainFromUrl(formData.companyWebsite);
        nextErrors.companyWebsite = `The website domain (${cleanDom}) does not match company name "${formData.company}". Please enter the official website for ${formData.company}.`;
      }
    }

    const isLandingPage = isLandingPageLead(selectedCard) || isLandingPageLead(formData);
    const isImportedLead = isProspectFinderLead(selectedCard);
    const activeSource = isLandingPage
      ? 'Landing Page'
      : isImportedLead
        ? 'Prospect Finder'
        : formData.source === 'Prospect Finder' || formData.source === 'Landing Page'
          ? 'LinkedIn'
          : formData.source || 'LinkedIn';

    const emailList =
      Array.isArray(formData.emails) && formData.emails.length > 0
        ? formData.emails
        : formData.email
          ? [formData.email]
          : [];
    const validEmails = emailList.map((e) => String(e || '').trim()).filter(Boolean);
    const primaryEmail = validEmails[0] || (formData.email ? String(formData.email).trim() : '');

    if (activeSource === 'LinkedIn') {
      if (!formData.linkedinUrl || !String(formData.linkedinUrl).trim()) {
        nextErrors.linkedinUrl = 'LinkedIn URL is required when source is LinkedIn.';
      }
    } else if (activeSource === 'Website') {
      if (!formData.companyWebsite || !String(formData.companyWebsite).trim()) {
        nextErrors.companyWebsite = 'Company website is required when source is Website.';
      }
    } else if (activeSource === 'Email') {
      if (!primaryEmail) {
        nextErrors.email = 'Email is required when source is Email.';
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    emailList.forEach((e, idx) => {
      if (e && String(e).trim()) {
        const trimmed = String(e).trim();
        if (!emailRegex.test(trimmed)) {
          const errKey = idx === 0 ? 'email' : `email_${idx}`;
          nextErrors[errKey] = `Email #${idx + 1} is not a valid email address.`;
        }
      }
    });

    const phoneList =
      Array.isArray(formData.phones) && formData.phones.length > 0
        ? formData.phones
        : formData.phone
          ? [formData.phone]
          : [];
    phoneList.forEach((p, idx) => {
      if (p && String(p).trim()) {
        const phoneEntry = findCountryByDialCodeOrName(p, formData.country);
        const phoneDialCode = phoneEntry ? countryDialCode(phoneEntry.code) : '';
        const phoneIsoCode = phoneEntry ? phoneEntry.code : '';
        const [min, max] = nationalLength(phoneIsoCode);

        if (!isValidPhone(p, phoneDialCode, phoneIsoCode)) {
          const errKey = idx === 0 ? 'phone' : `phone_${idx}`;
          nextErrors[errKey] =
            `Phone #${idx + 1} must be ${min === max ? min : `${min}-${max}`} digits${phoneDialCode ? ` after ${phoneDialCode}` : ''}.`;
        }
      }
    });

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const firstErrMsg = Object.values(nextErrors)[0];
      setSubmitError(firstErrMsg || 'Please fix the highlighted errors before saving.');
      setToast({
        show: true,
        message: firstErrMsg || 'Please fix the highlighted errors before saving.',
        type: 'error',
      });

      // Smoothly auto-scroll to the first invalid field
      setTimeout(() => {
        const firstErrorEl = document.querySelector(
          '.crm-modal-form .crm-field-error, .crm-detail-form .crm-field-error, .crm-field-error, .crm-form-error'
        );
        if (firstErrorEl) {
          firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const targetInput =
            firstErrorEl.closest('label')?.querySelector('input, select, textarea') ||
            firstErrorEl.parentElement?.querySelector('input, select, textarea');
          if (targetInput) {
            targetInput.focus();
            targetInput.classList.add('crm-input-shake');
            setTimeout(() => targetInput.classList.remove('crm-input-shake'), 600);
          }
        }
      }, 50);
      return;
    }
    const now = new Date().toISOString();
    const validPhones = phoneList.map((p) => String(p || '').trim()).filter(Boolean);
    const primaryPhone = validPhones[0] || (formData.phone ? String(formData.phone).trim() : '');
    const isImported = isProspectFinderLead(selectedCard);
    const effectiveSource = isLandingPage
      ? 'Landing Page'
      : isImported
        ? 'Prospect Finder'
        : formData.source === 'Prospect Finder' || formData.source === 'Landing Page'
          ? 'LinkedIn'
          : formData.source || 'LinkedIn';

    if (selectedCard && formData.id) {
      const origCompany = String(
        selectedCard.company || selectedCard.company_name || selectedCard.companyName || ''
      ).trim();
      const origWebsite = String(
        selectedCard.companyWebsite || selectedCard.company_website || selectedCard.website || ''
      ).trim();
      const curCompany = String(formData.company || '').trim();
      const curWebsite = String(formData.companyWebsite || '').trim();
      const isCompanyOrUrlChanged =
        (origCompany && curCompany.toLowerCase() !== origCompany.toLowerCase()) ||
        (origWebsite && curWebsite.toLowerCase() !== origWebsite.toLowerCase());

      const freshLogo = getCompanyLogoUrl(
        formData.company || selectedCard.company,
        isCompanyOrUrlChanged ? null : selectedCard.company_details,
        primaryEmail || selectedCard.email,
        formData.companyWebsite || selectedCard.companyWebsite
      );

      const updatedCrm = {
        ...(selectedCard.crm || {}),
        name: formData.name,
        jobTitle: formData.jobTitle,
        company: formData.company,
        company_name: formData.company,
        companyName: formData.company,
        companyWebsite: formData.companyWebsite,
        industry: formData.industry,
        country: formData.country,
        linkedinUrl: formData.linkedinUrl,
        email: primaryEmail,
        emails: validEmails.length > 0 ? validEmails : primaryEmail ? [primaryEmail] : [],
        phone: primaryPhone,
        phones: validPhones,
        source: effectiveSource,
        stage: formData.stage,
        owner: formData.owner,
        assignee: formData.assignee,
        dealName: formData.dealName,
        dealValue: formData.dealValue || '0',
        dealProbability: formData.dealProbability,
        currency: formData.currency,
        closeDate: formData.closeDate,
        productInterest: formData.productInterest,
        proposalReference: formData.proposalReference,
        wonValue: formData.wonValue,
        lastActivityDate: formData.lastActivityDate,
        nextFollowUpDate: formData.nextFollowUpDate,
        wonDate: formData.wonDate,
        lostDate: formData.lostDate,
        lostReason: formData.lostReason,
        lostContext: formData.lostContext,
      };

      const updated = {
        ...selectedCard,
        ...updatedCrm,
        crm: updatedCrm,
        id: formData.id,
        leadId: formData.leadId || selectedCard.leadId || `L-${formData.id}`,
        workspaceId: formData.workspaceId,
        sourceProspectId: formData.sourceProspectId,
        name: formData.name || 'Unnamed lead',
        jobTitle: formData.jobTitle,
        company: formData.company,
        company_name: formData.company,
        companyName: formData.company,
        companyLogo: freshLogo,
        logo_url: freshLogo,
        logoUrl: freshLogo,
        companyWebsite: formData.companyWebsite,
        company_details: {
          ...(selectedCard.company_details || {}),
          companyName: formData.company,
          name: formData.company,
          website: formData.companyWebsite,
          domain: formData.companyWebsite,
          logoUrl: freshLogo,
          logo_url: freshLogo,
        },
        companyDetails: {
          ...(selectedCard.companyDetails || {}),
          companyName: formData.company,
          name: formData.company,
          website: formData.companyWebsite,
          domain: formData.companyWebsite,
          logoUrl: freshLogo,
          logo_url: freshLogo,
        },
        email: primaryEmail,
        emails: validEmails.length > 0 ? validEmails : primaryEmail ? [primaryEmail] : [],
        phone: primaryPhone,
        phones: validPhones,
        phone_number: validPhones,
        phone_numbers: validPhones,
        linkedinUrl: formData.linkedinUrl,
        industry: formData.industry,
        country: formData.country,
        source: effectiveSource,
        stage: formData.stage,
        owner: formData.owner,
        assignee: formData.assignee,
        leadScore: calculateLeadScore(formData, customStages).totalScore,
        dealName: formData.dealName,
        dealValue: formData.dealValue || '0',
        dealProbability: formData.dealProbability,
        currency: formData.currency,
        closeDate: formData.closeDate,
        productInterest: formData.productInterest,
        proposalReference: formData.proposalReference,
        wonValue: formData.wonValue,
        lastActivityDate: formData.lastActivityDate,
        nextFollowUpDate: formData.nextFollowUpDate,
        wonDate: formData.wonDate,
        lostDate: formData.lostDate,
        lostReason: formData.lostReason,
        lostContext: formData.lostContext,
        createdBy: formData.createdBy || selectedCard.createdBy,
        createdDate: formData.createdDate || selectedCard.createdDate,
        updatedDate: now,
        notes: Array.isArray(formData.notes) ? formData.notes : selectedCard.notes || [],
        tasks: Array.isArray(formData.tasks) ? formData.tasks : selectedCard.tasks || [],
        activities: Array.isArray(formData.activities)
          ? formData.activities
          : selectedCard.activities || [],
        field_locks: selectedCard?.field_locks || formData?.field_locks || {},
        provenance_summary: selectedCard?.provenance_summary || formData?.provenance_summary || {},
      };
      setCards((current) =>
        current.map((c) => (String(c.id) === String(updated.id) ? updated : c))
      );
      setSelectedCard(updated);
      try {
        await axios.patch(`/lead-pipeline/${formData.id}`, {
          name: formData.name,
          jobTitle: formData.jobTitle,
          company: formData.company,
          email: primaryEmail,
          emails: validEmails.length > 0 ? validEmails : primaryEmail ? [primaryEmail] : [],
          phone: primaryPhone,
          phones: validPhones,
          linkedinUrl: formData.linkedinUrl,
          companyWebsite: formData.companyWebsite,
          industry: formData.industry,
          country: formData.country,
          source: effectiveSource,
          owner: formData.owner,
          assignee: formData.assignee,
          dealName: formData.dealName,
          dealValue: formData.dealValue,
          dealProbability: formData.dealProbability,
          currency: formData.currency,
          closeDate: formData.closeDate,
          productInterest: formData.productInterest,
          proposalReference: formData.proposalReference,
          wonValue: formData.wonValue,
          stage: formData.stage,
          leadScore: updated.leadScore,
        });
      } catch (error) {
        console.error('Failed to save CRM changes:', error);
        setToast({ show: true, message: 'Could not save changes to server.', type: 'error' });
      }
    } else {
      const payload = {
        provider: 'manual',
        provider_id: formData.sourceProspectId || null, // real provider/source id (stable per person)
        name: formData.name || 'Unnamed lead',
        job_title: formData.jobTitle,
        company: formData.company,
        location: formData.country,
        linkedin_url: formData.linkedinUrl,
        email: validEmails.length > 0 ? validEmails : primaryEmail ? [primaryEmail] : [],
        emails: validEmails.length > 0 ? validEmails : primaryEmail ? [primaryEmail] : [],
        phone_number: validPhones.length > 0 ? validPhones : primaryPhone ? [primaryPhone] : [],
        phone: primaryPhone,
        phones: validPhones,
        source: effectiveSource,
        owner: currentUsername,
        stage: formData.stage || 'New',
        deal_name: formData.dealName,
        deal_value: formData.dealValue,
        deal_probability: formData.dealProbability,
        currency: formData.currency,
        close_date: formData.closeDate,
        product_interest: formData.productInterest,
        proposal_reference: formData.proposalReference,
        won_value: formData.wonValue,
        won_date: formData.wonDate,
        lost_date: formData.lostDate,
        lost_reason: formData.lostReason,
        lost_context: formData.lostContext,
        industry: formData.industry,
        company_website: formData.companyWebsite,
        is_shared: false,
        visibility: 'private',
      };

      try {
        const conflicted = await submitAddCustomer(payload);
        if (conflicted) return;
      } catch (error) {
        return;
      }
    }

    setShowFormModal(false);
    setSelectedCard(null);
    selectedCardIdRef.current = null;
    resetFormData();
  };

  const handleDragStart = (event, cardId) => {
    isDraggingRef.current = true;
    draggedCardIdRef.current = cardId;
    setIsDragActive(true);
    event.currentTarget.classList.add('dragging');
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(cardId));
  };

  const handleDragEnd = (event) => {
    isDraggingRef.current = false;
    setIsDragActive(false);
    setTrashOver(false);
    event.currentTarget.classList.remove('dragging');
    document.querySelectorAll('.kanban-column-body.drag-over').forEach((el) => {
      el.classList.remove('drag-over');
    });
    // Delay clearing draggedCardIdRef slightly so handleDrop has reliable access even if dragend fires slightly ahead
    setTimeout(() => {
      draggedCardIdRef.current = null;
    }, 100);
  };

  const handleDragEnter = (event, stage) => {
    if (!canMarkWon && stage === 'Won') return;
    event.currentTarget.classList.add('drag-over');
  };

  const handleDragOver = (event, stage) => {
    if (!canMarkWon && stage === 'Won') return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      event.currentTarget.classList.remove('drag-over');
    }
  };

  const handleDrop = async (event, stage) => {
    event.preventDefault();
    event.currentTarget.classList.remove('drag-over');
    const id = draggedCardIdRef.current || event.dataTransfer?.getData('text/plain');
    if (!canMarkWon && stage === 'Won') {
      isDraggingRef.current = false;
      draggedCardIdRef.current = null;
      setIsDragActive(false);
      setTrashOver(false);
      return;
    }
    if (id) {
      const prev = cards.find((c) => String(c.id) === String(id));
      if (!prev) {
        isDraggingRef.current = false;
        draggedCardIdRef.current = null;
        setIsDragActive(false);
        setTrashOver(false);
        return;
      }

      // If dropped onto the same stage, do nothing
      if (prev.stage === stage) {
        isDraggingRef.current = false;
        draggedCardIdRef.current = null;
        setIsDragActive(false);
        setTrashOver(false);
        return;
      }

      // If dragged to Won, prompt for required won details (value, date)
      if (stage === 'Won') {
        openWonModal(prev);
        isDraggingRef.current = false;
        draggedCardIdRef.current = null;
        setIsDragActive(false);
        setTrashOver(false);
        return;
      }

      // If dragged to Lost, prompt for required lost details (reason, date, context)
      if (stage === 'Lost') {
        openLostModal(prev);
        isDraggingRef.current = false;
        draggedCardIdRef.current = null;
        setIsDragActive(false);
        setTrashOver(false);
        return;
      }

      const previousStage = prev.stage;
      const candidateCard = { ...prev, stage };
      const autoScore = calculateLeadScore(candidateCard, customStages).totalScore;
      const patch = { stage, leadScore: autoScore };
      if (previousStage === 'Won') {
        patch.wonDate = '';
      }
      if (previousStage === 'Lost') {
        patch.lostDate = '';
      }
      setCards((current) =>
        current.map((card) => (String(card.id) === String(id) ? { ...card, ...patch } : card))
      );
      try {
        await axios.patch(`/lead-pipeline/${id}`, patch);
      } catch (error) {
        console.error('Failed to update stage:', error?.response?.data || error.message);
        setCards((current) =>
          current.map((card) =>
            String(card.id) === String(id)
              ? {
                  ...card,
                  stage: previousStage,
                  wonDate: prev.wonDate,
                  lostDate: prev.lostDate,
                  leadScore: prev.leadScore,
                }
              : card
          )
        );
      }
    }
    // ensure dragging flag is cleared so subsequent clicks are recognized
    isDraggingRef.current = false;
    draggedCardIdRef.current = null;
    setIsDragActive(false);
    setTrashOver(false);
  };

  const handleTrashDrop = (event) => {
    event.preventDefault();
    const id = draggedCardIdRef.current ?? event.dataTransfer.getData('text/plain');
    setTrashOver(false);
    if (id) {
      const card = cards.find((c) => String(c.id) === String(id));
      setPendingDeleteCard(card || { id });
    }
    isDraggingRef.current = false;
    draggedCardIdRef.current = null;
    setIsDragActive(false);
  };

  const handleCancelDelete = () => {
    setPendingDeleteCard(null);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteCard) return;
    const id = pendingDeleteCard.id;
    const deleted = pendingDeleteCard;
    setPendingDeleteCard(null);
    setCards((current) => current.filter((c) => String(c.id) !== String(id)));
    try {
      await axios.delete(`/lead-pipeline/${id}`);
    } catch (error) {
      console.error('Failed to delete prospect:', error);
      setCards((current) =>
        current.some((c) => String(c.id) === String(id)) ? current : [...current, deleted]
      );
    }
  };

  const handleBulkDelete = async () => {
    const ids = selectedRowIds;
    if (ids.length === 0) return;
    const deletedCards = cards.filter((c) => ids.includes(String(c.id)));
    setPendingBulkDelete(false);
    setSelectedRowIds([]);
    setCards((current) => current.filter((c) => !ids.includes(String(c.id))));
    try {
      await Promise.all(ids.map((id) => axios.delete(`/lead-pipeline/${id}`)));
    } catch (error) {
      console.error('Failed to delete some leads:', error);
      setCards((current) => {
        const restored = deletedCards.filter(
          (c) => !current.some((x) => String(x.id) === String(c.id))
        );
        return restored.length ? [...current, ...restored] : current;
      });
    }
  };

  const todayStr = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate()
    ).padStart(2, '0')}`;
  };

  const handleArchiveCard = async (id, archive = true) => {
    const prev = cards.find((c) => String(c.id) === String(id)) || {};
    setCards((current) =>
      current.map((c) => (String(c.id) === String(id) ? { ...c, isArchived: archive } : c))
    );
    if (selectedCard && String(selectedCard.id) === String(id)) {
      setSelectedCard((current) => (current ? { ...current, isArchived: archive } : current));
      setFormData((current) => ({ ...current, isArchived: archive }));
    }
    try {
      await axios.patch(`/lead-pipeline/${id}`, { isArchived: archive });
    } catch (error) {
      console.error('Failed to update archive status:', error);
      setCards((current) =>
        current.map((c) =>
          String(c.id) === String(id) ? { ...c, isArchived: prev.isArchived } : c
        )
      );
      if (selectedCard && String(selectedCard.id) === String(id)) {
        setSelectedCard((current) =>
          current ? { ...current, isArchived: prev.isArchived } : current
        );
        setFormData((current) => ({ ...current, isArchived: prev.isArchived }));
      }
    }
  };

  const handleBulkArchive = async (archive = true) => {
    const ids = selectedRowIds;
    if (ids.length === 0) return;
    const previous = cards.filter((c) => ids.includes(String(c.id)));
    setSelectedRowIds([]);
    setCards((current) =>
      current.map((c) => (ids.includes(String(c.id)) ? { ...c, isArchived: archive } : c))
    );
    try {
      await Promise.all(
        ids.map((id) => axios.patch(`/lead-pipeline/${id}`, { isArchived: archive }))
      );
    } catch (error) {
      console.error('Bulk archive failed:', error);
      setCards((current) =>
        current.map((c) => {
          const prev = previous.find((p) => String(p.id) === String(c.id));
          return prev ? { ...c, ...prev } : c;
        })
      );
    }
  };

  const handleBulkAssign = async () => {
    const ids = selectedRowIds;
    if (ids.length === 0) return;
    const previous = cards.filter((c) => ids.includes(String(c.id)));
    const assignee = bulkAssignTo || '';
    setPendingBulkAssign(false);
    setBulkAssignTo('');
    setSelectedRowIds([]);
    setCards((current) =>
      current.map((c) => (ids.includes(String(c.id)) ? { ...c, assignee } : c))
    );
    try {
      await Promise.all(ids.map((id) => axios.patch(`/lead-pipeline/${id}`, { assignee })));
    } catch (error) {
      console.error('Bulk assign failed:', error);
      setCards((current) =>
        current.map((c) => {
          const prev = previous.find((p) => String(p.id) === String(c.id));
          return prev ? { ...c, ...prev } : c;
        })
      );
    }
  };

  const handleBulkFollowUp = async () => {
    const ids = selectedRowIds;
    if (ids.length === 0) return;
    const previous = cards.filter((c) => ids.includes(String(c.id)));
    const nextFollowUpDate = bulkFollowUpDate || '';
    const taskTitle = bulkFollowUpTask.trim();
    const taskPriority = bulkFollowUpPriority;
    setPendingBulkFollowUp(false);
    setBulkFollowUpDate('');
    setBulkFollowUpTask('');
    setBulkFollowUpPriority('Medium');
    setSelectedRowIds([]);
    setCards((current) =>
      current.map((c) => (ids.includes(String(c.id)) ? { ...c, nextFollowUpDate } : c))
    );
    try {
      await Promise.all(
        ids.map(async (id) => {
          await axios.patch(`/lead-pipeline/${id}`, { nextFollowUpDate });
          if (taskTitle) {
            await axios.post(`/lead-pipeline/${id}/tasks`, {
              title: taskTitle,
              dueDate: bulkFollowUpDate || null,
              priority: taskPriority,
            });
          }
        })
      );
    } catch (error) {
      console.error('Bulk follow-up update failed:', error);
      setCards((current) =>
        current.map((c) => {
          const prev = previous.find((p) => String(p.id) === String(c.id));
          return prev ? { ...c, ...prev } : c;
        })
      );
    }
  };

  const applyBulkStage = async (stage, extraPatch = {}) => {
    const ids = selectedRowIds;
    if (ids.length === 0) return;
    const previous = cards.filter((c) => ids.includes(String(c.id)));
    const patchFor = (card) => {
      const patch = { ...extraPatch, stage };
      if (stage === 'Won' && !card.wonDate) patch.wonDate = todayStr();
      if (stage === 'Lost' && !card.lostDate) patch.lostDate = todayStr();
      if (stage !== 'Won' && card.stage === 'Won') patch.wonDate = '';
      if (stage !== 'Lost' && card.stage === 'Lost') patch.lostDate = '';
      return patch;
    };
    setSelectedRowIds([]);
    setCards((current) =>
      current.map((c) =>
        ids.includes(String(c.id)) ? { ...c, ...patchFor(c), tasks: cancelTasks(c.tasks) } : c
      )
    );
    try {
      await Promise.all(
        ids.map((id) => {
          const card = cards.find((c) => String(c.id) === String(id));
          return axios.patch(`/lead-pipeline/${id}`, patchFor(card || {}));
        })
      );
    } catch (error) {
      console.error('Bulk stage update failed:', error);
      setCards((current) =>
        current.map((c) => {
          const prev = previous.find((p) => String(p.id) === String(c.id));
          return prev ? { ...c, ...prev } : c;
        })
      );
    }
  };

  const handleBulkStage = () => {
    const stage = bulkStage;
    if (!stage) return;
    setPendingBulkStage(false);
    setBulkStage('');
    if (stage === 'Won') {
      setWonForm({ value: '', wonDate: todayStr() });
      setWonError('');
      setBulkStageModal('won');
      return;
    }
    if (stage === 'Lost') {
      setLostForm({ reason: '', context: '', lostDate: todayStr() });
      setLostError('');
      setBulkStageModal('lost');
      return;
    }
    applyBulkStage(stage);
  };

  const handleBulkConfirmWon = () => {
    const value = wonForm.value.trim();
    if (value === '' || Number.isNaN(parseFloat(value)) || parseFloat(value) < 0) {
      setWonError('Please enter a valid won value.');
      return;
    }
    const wonDate = wonForm.wonDate;
    if (!wonDate) {
      setWonError('Please select a won date.');
      return;
    }
    setBulkStageModal('');
    setWonError('');
    applyBulkStage('Won', { wonValue: value, wonDate });
  };

  const handleBulkConfirmLost = () => {
    const reason = lostForm.reason.trim();
    if (!reason) {
      setLostError('Please select a reason.');
      return;
    }
    const lostDate = lostForm.lostDate;
    if (!lostDate) {
      setLostError('Please select a lost date.');
      return;
    }
    const extraPatch = { stage: 'Lost', lostDate, lostReason: reason };
    if (lostForm.context.trim()) extraPatch.lostContext = lostForm.context.trim();
    setBulkStageModal('');
    setLostError('');
    applyBulkStage('Lost', extraPatch);
  };

  const openCardDetails = async (card) => {
    console.log('openCardDetails called for', card && card.id);
    setSelectedCard(card);
    selectedCardIdRef.current = card.id;
    setNoteError('');
    setActiveActivityId(null);
    let fresh = card;
    let crmMeta = {};
    try {
      const [response, evidenceRes] = await Promise.all([
        axios.get(`/lead-pipeline/${card.id}`),
        axios.get(`/lead-pipeline/${card.id}/evidence`).catch(() => null),
      ]);
      if (response.data?.success && response.data.data) {
        const d = response.data.data;
        const evData = evidenceRes?.data?.data || {};
        crmMeta = d.crm || {};
        const rawStage = d.stage || card.stage || crmMeta.stage || 'New';
        const resolvedStage =
          STAGES.find((s) => s.toLowerCase() === String(rawStage).toLowerCase()) || rawStage;
        const resolvedLocks =
          evData.fieldLocks || d.field_locks || card.field_locks || crmMeta.field_locks || {};
        const resolvedSummary =
          evData.provenanceSummary ||
          d.provenance_summary ||
          card.provenance_summary ||
          crmMeta.provenance_summary ||
          {};
        fresh = {
          ...card,
          ...d,
          ...crmMeta,
          field_locks: resolvedLocks,
          provenance_summary: resolvedSummary,
          provider: d.provider || card.provider || crmMeta.provider || 'manual',
          stage: resolvedStage,
          notes: Array.isArray(crmMeta.notes)
            ? crmMeta.notes
            : Array.isArray(d.notes)
              ? d.notes
              : Array.isArray(card.notes)
                ? card.notes
                : [],
          tasks: Array.isArray(crmMeta.tasks)
            ? crmMeta.tasks
            : Array.isArray(d.tasks)
              ? d.tasks
              : Array.isArray(card.tasks)
                ? card.tasks
                : [],
          activities: Array.isArray(crmMeta.activities)
            ? crmMeta.activities
            : Array.isArray(d.activities)
              ? d.activities
              : Array.isArray(card.activities)
                ? card.activities
                : [],
        };
      }
    } catch (error) {
      console.warn('Could not refresh card from database, using cached data:', error);
    }
    const resolvedEmails =
      Array.isArray(crmMeta.emails) && crmMeta.emails.length > 0
        ? crmMeta.emails.map(emailToString).filter(Boolean).slice(0, 3)
        : extractEmailList(
            (Array.isArray(crmMeta.emails) && crmMeta.emails.length > 0 ? crmMeta.emails : null) ??
              (Array.isArray(fresh.email_addresses) && fresh.email_addresses.length > 0
                ? fresh.email_addresses
                : null) ??
              (Array.isArray(fresh.emails) && fresh.emails.length > 0 ? fresh.emails : null) ??
              (Array.isArray(card.email_addresses) && card.email_addresses.length > 0
                ? card.email_addresses
                : null) ??
              (Array.isArray(card.emails) && card.emails.length > 0 ? card.emails : null) ??
              (Array.isArray(fresh.email) && fresh.email.length > 0 ? fresh.email : null) ??
              (Array.isArray(card.email) && card.email.length > 0 ? card.email : null) ??
              crmMeta.emails ??
              crmMeta.email ??
              fresh.emails ??
              fresh.email_addresses ??
              fresh.email
          );

    const primaryEmail =
      resolvedEmails.find((e) => String(e || '').trim()) ||
      emailToString(crmMeta.email) ||
      emailToString(fresh.email) ||
      emailToString(fresh.email_addresses) ||
      emailToString(card.email) ||
      emailToString(card.email_addresses) ||
      '';

    const resolvedPhones =
      Array.isArray(crmMeta.phones) && crmMeta.phones.length > 0
        ? crmMeta.phones.map(phoneToString).filter(Boolean).slice(0, 3)
        : extractPhoneList(
            (Array.isArray(crmMeta.phones) && crmMeta.phones.length > 0 ? crmMeta.phones : null) ??
              (Array.isArray(fresh.phone_numbers) && fresh.phone_numbers.length > 0
                ? fresh.phone_numbers
                : null) ??
              (Array.isArray(fresh.phones) && fresh.phones.length > 0 ? fresh.phones : null) ??
              (Array.isArray(card.phone_numbers) && card.phone_numbers.length > 0
                ? card.phone_numbers
                : null) ??
              (Array.isArray(card.phones) && card.phones.length > 0 ? card.phones : null) ??
              (Array.isArray(fresh.phone_number) && fresh.phone_number.length > 0
                ? fresh.phone_number
                : null) ??
              (Array.isArray(card.phone_number) && card.phone_number.length > 0
                ? card.phone_number
                : null) ??
              crmMeta.phones ??
              crmMeta.phone ??
              fresh.phones ??
              fresh.phone_numbers ??
              fresh.phone_number ??
              fresh.phone
          );

    const primaryPhone =
      resolvedPhones.find((p) => String(p || '').trim()) ||
      phoneToString(crmMeta.phone) ||
      phoneToString(fresh.phone) ||
      phoneToString(fresh.phone_numbers) ||
      phoneToString(fresh.phone_number) ||
      phoneToString(card.phone) ||
      phoneToString(card.phone_numbers) ||
      phoneToString(card.phone_number) ||
      '';

    setFormData({
      id: fresh.id,
      leadId: fresh.leadId || fresh.lead_id || '',
      workspaceId: fresh.workspaceId || fresh.workspace_id || '',
      sourceProspectId:
        fresh.sourceProspectId || fresh.provider_id || fresh.external_id || fresh.lusha_id || '',
      name: crmMeta.name || fresh.name || fresh.full_name || '',
      jobTitle: crmMeta.jobTitle || fresh.jobTitle || fresh.job_title || '',
      company: cleanCompany(
        crmMeta.company ||
          crmMeta.companyName ||
          crmMeta.company_name ||
          fresh.company ||
          fresh.company_name ||
          fresh.companyName ||
          fresh.companyDetails?.name ||
          fresh.company_details?.name ||
          fresh.company_details?.companyName ||
          fresh.company_data?.name ||
          fresh.companyData?.name
      ),
      companyWebsite:
        crmMeta.companyWebsite ||
        crmMeta.company_website ||
        fresh.companyWebsite ||
        fresh.company_website ||
        '',
      industry:
        crmMeta.industry ||
        fresh.industry ||
        fresh.company_industry ||
        fresh.companyDetails?.mainIndustry ||
        '',
      country: crmMeta.country || fresh.country || fresh.location?.country || '',
      linkedinUrl:
        crmMeta.linkedinUrl ||
        crmMeta.linkedin_url ||
        fresh.linkedin_url ||
        fresh.linkedinUrl ||
        '',
      email: primaryEmail,
      emails: resolvedEmails.length > 0 ? resolvedEmails : primaryEmail ? [primaryEmail] : [''],
      phone: primaryPhone,
      phones: resolvedPhones.length > 0 ? resolvedPhones : primaryPhone ? [primaryPhone] : [''],
      provider: fresh.provider || card.provider || crmMeta.provider || 'manual',
      source:
        isLandingPageLead(fresh) || isLandingPageLead(card)
          ? 'Landing Page'
          : isProspectFinderLead(fresh)
            ? 'Prospect Finder'
            : normalizeSource(crmMeta.source || fresh.crm?.source || fresh.source || 'Website'),
      stage: (() => {
        const raw = fresh.stage || card.stage || 'New';
        return STAGES.find((s) => s.toLowerCase() === String(raw).toLowerCase()) || raw;
      })(),
      owner: fresh.owner || fresh.crm?.owner || fresh.creator_username || fresh.creator || '',
      assignee: fresh.assignee || '',
      leadScore: fresh.leadScore ?? fresh.lead_score ?? 0,
      dealName: fresh.dealName || fresh.deal_name || crmMeta.dealName || crmMeta.deal_name || '',
      dealValue:
        fresh.dealValue || fresh.deal_value || crmMeta.dealValue || crmMeta.deal_value || '',
      dealProbability:
        fresh.dealProbability ||
        fresh.deal_probability ||
        crmMeta.dealProbability ||
        crmMeta.deal_probability ||
        '',
      currency: fresh.currency || crmMeta.currency || 'USD',
      closeDate:
        fresh.closeDate || fresh.close_date || crmMeta.closeDate || crmMeta.close_date || '',
      productInterest:
        fresh.productInterest ||
        fresh.product_interest ||
        crmMeta.productInterest ||
        crmMeta.product_interest ||
        '',
      proposalReference:
        fresh.proposalReference ||
        fresh.proposal_reference ||
        crmMeta.proposalReference ||
        crmMeta.proposal_reference ||
        '',
      wonValue: fresh.wonValue || fresh.won_value || crmMeta.wonValue || crmMeta.won_value || '',
      lastActivityDate: fresh.lastActivityDate || fresh.last_activity_date || '',
      nextFollowUpDate: fresh.nextFollowUpDate || fresh.next_follow_up_date || '',
      wonDate: fresh.wonDate || fresh.won_date || '',
      lostDate: fresh.lostDate || fresh.lost_date || '',
      wonAt: fresh.wonAt || fresh.won_at || '',
      lostAt: fresh.lostAt || fresh.lost_at || '',
      lostReason: fresh.lostReason || fresh.lost_reason || '',
      lostContext: fresh.lostContext || fresh.lost_context || '',
      createdBy: fresh.createdBy || fresh.created_by || '',
      createdDate: fresh.createdDate || fresh.created_date || '',
      updatedDate: fresh.updatedDate || fresh.updated_date || '',
      notes: Array.isArray(fresh.notes) ? fresh.notes : [],
      tasks: Array.isArray(fresh.tasks) ? fresh.tasks : [],
      activities: Array.isArray(fresh.activities) ? fresh.activities : [],
      field_locks: fresh.field_locks || card.field_locks || {},
      provenance_summary: fresh.provenance_summary || card.provenance_summary || {},
    });
    setSelectedCard(fresh);
    setNoteInput('');
    setTaskInput('');
    setTaskDueDate('');
    setUnaddedNoteWarning(false);
    setUnaddedTaskWarning(false);
    setShowFormModal(true);
    const leadName = crmMeta.name || fresh.name || fresh.full_name || card.name || '';
    const allLeadIds = [
      fresh.id,
      fresh.shared_item_id,
      fresh.lead_id,
      card.id,
      card.shared_item_id,
      card.lead_id,
    ].filter(Boolean);
    fetchLeadMeetings(fresh.id, primaryEmail, leadName, resolvedEmails, allLeadIds);
  };

  const handleAddNote = async () => {
    if (!selectedCard || !noteInput.trim()) return;
    const cardId = selectedCard.id;
    const note = {
      id: `N-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      text: noteInput.trim(),
      createdAt: new Date().toISOString(),
      createdBy: currentUserId,
      createdByName: currentUsername,
    };
    pendingNotesRef.current.set(note.id, { note, cardId });
    setNoteError('');
    setNoteInput('');
    setUnaddedNoteWarning(false);
    setFormData((current) => ({
      ...current,
      notes: [...(Array.isArray(current.notes) ? current.notes : []), note],
    }));
    try {
      const response = await axios.post(`/lead-pipeline/${cardId}/notes`, {
        text: note.text,
        createdAt: note.createdAt,
      });
      pendingNotesRef.current.delete(note.id);
      // The user may have opened a different lead while this note was saving —
      // don't mutate the notes list of the currently shown lead.
      if (cardId !== selectedCardIdRef.current) return;
      // Merge (never blindly replace) so a server snapshot taken while other
      // notes were still saving cannot drop them from the list.
      setFormData((current) => ({
        ...current,
        notes: reconcileNotes(
          response.data?.data?.notes,
          current.notes,
          pendingNotesRef.current,
          cardId
        ),
      }));
    } catch (error) {
      console.error('Failed to save note:', error);
      pendingNotesRef.current.delete(note.id);
      setFormData((current) => ({
        ...current,
        notes: Array.isArray(current.notes) ? current.notes.filter((n) => n.id !== note.id) : [],
      }));
      // Keep the user's text so the note is not silently lost, and explain why.
      setNoteInput((current) => current || note.text);
      setNoteError('Could not save the note. Your text was kept — please try again.');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!selectedCard) return;
    const cardId = selectedCard.id;
    const previousNote = Array.isArray(formData.notes)
      ? formData.notes.find((n) => n.id === noteId)
      : undefined;
    setFormData((current) => ({
      ...current,
      notes: Array.isArray(current.notes) ? current.notes.filter((n) => n.id !== noteId) : [],
    }));
    try {
      const response = await axios.delete(`/lead-pipeline/${cardId}/notes/${noteId}`);
      if (cardId !== selectedCardIdRef.current) return;
      setFormData((current) => ({
        ...current,
        notes: reconcileNotes(
          response.data?.data?.notes,
          current.notes,
          pendingNotesRef.current,
          cardId
        ),
      }));
    } catch (error) {
      console.error('Failed to delete note:', error);
      if (cardId !== selectedCardIdRef.current) return;
      // Best effort restore if the server could not be reached.
      if (previousNote) {
        setFormData((current) => {
          const notes = Array.isArray(current.notes) ? current.notes : [];
          if (!notes.some((n) => n.id === noteId)) {
            return { ...current, notes: [...notes, previousNote] };
          }
          return current;
        });
      }
    }
  };

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditNoteText(note.text);
  };

  const openNoteHistory = async () => {
    if (!selectedCard) return;
    setShowNoteHistory(true);
    setNoteHistoryLoading(true);
    try {
      const response = await axios.get(`/lead-pipeline/${selectedCard.id}/notes/deleted`);
      setDeletedNotes(response.data?.data?.notes || []);
    } catch (error) {
      console.error('Failed to load deleted notes:', error);
      setDeletedNotes([]);
    } finally {
      setNoteHistoryLoading(false);
    }
  };

  const closeNoteHistory = () => {
    setShowNoteHistory(false);
    setDeletedNotes([]);
  };

  const handleUpdateNote = async () => {
    if (!selectedCard || !editNoteText.trim() || !editingNoteId) return;
    const cardId = selectedCard.id;
    const noteId = editingNoteId;
    const updatedText = editNoteText.trim();
    const previous = Array.isArray(formData.notes) ? formData.notes : [];
    const updated = previous.map((n) =>
      n.id === noteId ? { ...n, text: updatedText, createdAt: new Date().toISOString() } : n
    );
    setFormData((current) => ({ ...current, notes: updated }));
    setEditingNoteId(null);
    setEditNoteText('');
    try {
      const response = await axios.patch(`/lead-pipeline/${cardId}/notes/${noteId}`, {
        text: updatedText,
      });
      if (cardId !== selectedCardIdRef.current) return;
      setFormData((current) => ({
        ...current,
        notes: reconcileNotes(
          response.data?.data?.notes,
          current.notes,
          pendingNotesRef.current,
          cardId
        ),
      }));
    } catch (error) {
      console.error('Failed to update note:', error);
      if (cardId !== selectedCardIdRef.current) return;
      setFormData((current) => ({
        ...current,
        notes: reconcileNotes(null, previous, pendingNotesRef.current, cardId),
      }));
      setEditingNoteId(noteId);
      setEditNoteText(updatedText);
    }
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
    setEditNoteText('');
  };

  const taskAssigneeName = (task) => {
    if (!task) return '';
    if (task.assigneeName) return task.assigneeName;
    const member = task.assignee ? workspaceMembers.find((m) => m.user_id === task.assignee) : null;
    return member?.display_name || member?.username || member?.email || '';
  };

  const cleanUsername = (str) => {
    if (!str || typeof str !== 'string') return '';
    const trimmed = str.trim();
    if (trimmed.includes('@')) {
      return trimmed.split('@')[0];
    }
    return trimmed;
  };

  const getCardPersonName = (card) => {
    if (!card) return 'Unassigned';
    if (card.assignee) {
      const member = workspaceMembers.find(
        (m) =>
          m.user_id === card.assignee ||
          (m.username && m.username.toLowerCase() === String(card.assignee).toLowerCase()) ||
          (m.email && m.email.toLowerCase() === String(card.assignee).toLowerCase())
      );
      if (member) {
        return cleanUsername(
          member.username || member.email || member.display_name || card.assignee
        );
      }
      return cleanUsername(card.assigneeName || card.assignee);
    }
    return cleanUsername(card.owner) || 'Unassigned';
  };

  const TASK_STATUS_LABELS = {
    pending: 'Pending',
    in_progress: 'In progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
    overdue: 'Overdue',
  };

  const taskStatus = (task) => {
    if (!task) return 'pending';
    const status = task.status || (task.completed ? 'completed' : 'pending');
    if (status === 'completed' || status === 'cancelled') return status;
    if (task.dueDate) {
      const due = new Date(
        String(task.dueDate).includes('T') ? task.dueDate : task.dueDate + 'T00:00:00'
      );
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (!Number.isNaN(due.getTime()) && due < today) return 'overdue';
    }
    return status;
  };

  const PRIORITY_WEIGHT = {
    high: 3,
    medium: 2,
    low: 1,
  };

  const sortCrmTasks = (tasks) => {
    if (!Array.isArray(tasks)) return [];
    return [...tasks].sort((a, b) => {
      // 1. Priority first (High > Medium > Low)
      const pA = PRIORITY_WEIGHT[String(a?.priority || '').toLowerCase()] || 0;
      const pB = PRIORITY_WEIGHT[String(b?.priority || '').toLowerCase()] || 0;
      if (pB !== pA) {
        return pB - pA;
      }

      // 2. Due Date second (Earliest due date first, tasks without due dates placed at the end)
      const dateA = a?.dueDate || a?.due_date || a?.due;
      const dateB = b?.dueDate || b?.due_date || b?.due;
      const timeA = dateA
        ? new Date(String(dateA).includes('T') ? dateA : dateA + 'T00:00:00').getTime()
        : Infinity;
      const timeB = dateB
        ? new Date(String(dateB).includes('T') ? dateB : dateB + 'T00:00:00').getTime()
        : Infinity;

      if (timeA !== timeB) {
        return timeA - timeB;
      }

      return 0;
    });
  };

  const handleAddTask = async () => {
    if (!selectedCard || !taskInput.trim()) return;
    const lines = taskInput
      .trim()
      .split('\n')
      .filter((l) => l.trim());
    const title = lines[0] || taskInput.trim();
    const description = lines.slice(1).join('\n').trim();
    const member = workspaceMembers.find((m) => m.user_id === taskAssignee);
    const task = {
      id: `T-${Date.now()}`,
      title,
      text: title,
      description,
      assignee: taskAssignee || null,
      assigneeName: member?.display_name || member?.username || member?.email || '',
      priority: taskPriority,
      dueDate: taskDueDate || null,
      status: 'pending',
      completed: false,
      createdAt: new Date().toISOString(),
      createdBy: currentUserId,
      createdByName: currentUsername,
      relatedLead: selectedCard.id,
    };
    const optimistic = [...formData.tasks, task];
    setFormData((current) => ({ ...current, tasks: optimistic }));
    setTaskInput('');
    setTaskDueDate('');
    setTaskAssignee('');
    setTaskPriority('Medium');
    setUnaddedTaskWarning(false);
    try {
      const response = await axios.post(`/lead-pipeline/${selectedCard.id}/tasks`, {
        title: task.title,
        description: task.description,
        assignee: task.assignee,
        priority: task.priority,
        dueDate: task.dueDate,
      });
      const savedTasks = response.data?.data?.tasks || optimistic;
      const updatedCard = { ...(selectedCard || {}), ...(formData || {}), tasks: savedTasks };
      const autoScore = calculateLeadScore(updatedCard, customStages).totalScore;
      setFormData((current) => ({ ...current, tasks: savedTasks, leadScore: autoScore }));
      setCards((current) =>
        current.map((c) =>
          String(c.id) === String(selectedCard.id)
            ? { ...c, tasks: savedTasks, leadScore: autoScore }
            : c
        )
      );
    } catch (error) {
      console.error('Failed to save task:', error);
      setFormData((current) => {
        const remaining = current.tasks.filter((t) => t.id !== task.id);
        const autoScore = calculateLeadScore(
          {
            ...(selectedCard || {}),
            ...(formData || {}),
            tasks: remaining,
          },
          customStages
        ).totalScore;
        return {
          ...current,
          tasks: remaining,
          leadScore: autoScore,
        };
      });
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    if (!selectedCard) return;
    const status = ['pending', 'in_progress', 'completed', 'cancelled'].includes(newStatus)
      ? newStatus
      : 'pending';
    const previous = formData.tasks;
    setFormData((current) => {
      const nextTasks = current.tasks.map((t) =>
        t.id === taskId ? { ...t, status, completed: status === 'completed' } : t
      );
      const autoScore = calculateLeadScore(
        {
          ...(selectedCard || {}),
          ...(formData || {}),
          tasks: nextTasks,
        },
        customStages
      ).totalScore;
      return {
        ...current,
        tasks: nextTasks,
        leadScore: autoScore,
      };
    });
    try {
      const response = await axios.patch(`/lead-pipeline/${selectedCard.id}/tasks/${taskId}`, {
        status,
      });
      const savedTasks = response.data?.data?.tasks;
      const savedActivities = response.data?.data?.activities;
      if (Array.isArray(savedTasks)) {
        const updatedCard = { ...(selectedCard || {}), ...(formData || {}), tasks: savedTasks };
        const autoScore = calculateLeadScore(updatedCard, customStages).totalScore;
        setFormData((current) => ({
          ...current,
          tasks: savedTasks,
          leadScore: autoScore,
          ...(Array.isArray(savedActivities) ? { activities: savedActivities } : {}),
        }));
        setCards((current) =>
          current.map((c) =>
            String(c.id) === String(selectedCard.id)
              ? { ...c, tasks: savedTasks, leadScore: autoScore }
              : c
          )
        );
      }
    } catch (error) {
      console.error('Failed to update task status:', error);
      const autoScore = calculateLeadScore(
        {
          ...(selectedCard || {}),
          ...(formData || {}),
          tasks: previous,
        },
        customStages
      ).totalScore;
      setFormData((current) => ({ ...current, tasks: previous, leadScore: autoScore }));
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!selectedCard) return;
    const removed = formData.tasks.filter((t) => t.id !== taskId);
    const autoScore = calculateLeadScore(
      {
        ...(selectedCard || {}),
        ...(formData || {}),
        tasks: removed,
      },
      customStages
    ).totalScore;
    setFormData((current) => ({ ...current, tasks: removed, leadScore: autoScore }));
    try {
      const response = await axios.delete(`/lead-pipeline/${selectedCard.id}/tasks/${taskId}`);
      const savedTasks = response.data?.data?.tasks;
      if (Array.isArray(savedTasks)) {
        const updatedCard = { ...(selectedCard || {}), ...(formData || {}), tasks: savedTasks };
        const finalScore = calculateLeadScore(updatedCard, customStages).totalScore;
        setFormData((current) => ({ ...current, tasks: savedTasks, leadScore: finalScore }));
        setCards((current) =>
          current.map((c) =>
            String(c.id) === String(selectedCard.id)
              ? { ...c, tasks: savedTasks, leadScore: finalScore }
              : c
          )
        );
      }
    } catch (error) {
      console.error('Failed to delete task:', error);
      const rollbackScore = calculateLeadScore(
        {
          ...(selectedCard || {}),
          ...(formData || {}),
          tasks: formData.tasks,
        },
        customStages
      ).totalScore;
      setFormData((current) => ({ ...current, tasks: formData.tasks, leadScore: rollbackScore }));
    }
  };

  const handleCardClick = (card) => {
    console.log('handleCardClick', card && card.id, 'isDragging=', isDraggingRef.current);
    openCardDetails(card);
  };

  const openWonModal = (targetCard = null) => {
    const card = targetCard && targetCard.id ? targetCard : selectedCard;
    if (!card) return;
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate()
    ).padStart(2, '0')}`;
    setSelectedCard(card);
    selectedCardIdRef.current = card.id || null;
    setWonForm({
      value:
        (targetCard && targetCard.id ? card.dealValue : formData.dealValue) ||
        card.dealValue ||
        card.deal_value ||
        card.crm?.dealValue ||
        '',
      wonDate:
        (targetCard && targetCard.id ? card.wonDate : formData.wonDate) || card.wonDate || today,
    });
    setWonError('');
    setShowFormModal(false);
    setShowWonModal(true);
  };

  const closeWonModal = () => {
    setShowWonModal(false);
    setWonError('');
    setSelectedCard(null);
    selectedCardIdRef.current = null;
  };

  const cancelTasks = (tasks) =>
    Array.isArray(tasks)
      ? tasks.map((task) => {
          const status = task.status || (task.completed ? 'completed' : 'pending');
          return ['completed', 'cancelled'].includes(status)
            ? task
            : { ...task, status: 'cancelled' };
        })
      : tasks;

  const handleConfirmWon = async () => {
    if (!selectedCard) return;
    const value = wonForm.value.trim();
    if (value === '' || Number.isNaN(parseFloat(value)) || parseFloat(value) < 0) {
      setWonError('Please enter a valid won value.');
      return;
    }
    const wonDate = wonForm.wonDate || undefined;
    if (!wonDate) {
      setWonError('Please select a won date.');
      return;
    }
    const id = selectedCard.id;
    const prev = cards.find((c) => String(c.id) === String(id)) || {};
    const previousStage = prev.stage;
    const previousWonDate = prev.wonDate;
    const patch = { stage: 'Won', wonValue: value, wonDate, leadScore: 100 };
    setShowWonModal(false);
    setWonError('');
    setSelectedCard(null);
    selectedCardIdRef.current = null;
    setCards((current) =>
      current.map((card) =>
        String(card.id) === String(id)
          ? { ...card, ...patch, tasks: cancelTasks(card.tasks) }
          : card
      )
    );
    try {
      await axios.patch(`/lead-pipeline/${id}`, patch);
    } catch (error) {
      console.error('Failed to mark as won:', error);
      const rollback = {
        stage: previousStage,
        wonDate: previousWonDate,
        wonValue: prev.wonValue,
        leadScore: prev.leadScore,
      };
      setCards((current) =>
        current.map((card) => (String(card.id) === String(id) ? { ...card, ...rollback } : card))
      );
    }
  };

  const openLostModal = (targetCard = null) => {
    const card = targetCard && targetCard.id ? targetCard : selectedCard;
    if (!card) return;
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate()
    ).padStart(2, '0')}`;
    setSelectedCard(card);
    selectedCardIdRef.current = card.id || null;
    setLostForm({
      reason: card.lostReason || card.crm?.lostReason || '',
      context: card.lostContext || card.crm?.lostContext || '',
      lostDate:
        (targetCard && targetCard.id ? card.lostDate : formData.lostDate) || card.lostDate || today,
    });
    setLostError('');
    setShowFormModal(false);
    setShowLostModal(true);
  };

  const closeLostModal = () => {
    setShowLostModal(false);
    setLostError('');
    setSelectedCard(null);
    selectedCardIdRef.current = null;
  };

  const handleConfirmLost = async () => {
    if (!selectedCard) return;
    const reason = lostForm.reason.trim();
    if (!reason) {
      setLostError('Please select a reason.');
      return;
    }
    const id = selectedCard.id;
    const prev = cards.find((c) => String(c.id) === String(id)) || {};
    const lostDate = lostForm.lostDate || undefined;
    if (!lostDate) {
      setLostError('Please select a lost date.');
      return;
    }
    const patch = { stage: 'Lost', lostReason: reason, lostDate, leadScore: 0 };
    if (lostForm.context.trim()) patch.lostContext = lostForm.context.trim();
    setShowLostModal(false);
    setLostError('');
    setSelectedCard(null);
    selectedCardIdRef.current = null;
    setCards((current) =>
      current.map((card) =>
        String(card.id) === String(id)
          ? { ...card, ...patch, tasks: cancelTasks(card.tasks) }
          : card
      )
    );
    try {
      await axios.patch(`/lead-pipeline/${id}`, patch);
    } catch (error) {
      console.error('Failed to mark as lost:', error);
      const rollback = {
        stage: prev.stage,
        lostReason: prev.lostReason,
        lostDate: prev.lostDate,
        lostContext: prev.lostContext,
      };
      setCards((current) =>
        current.map((card) => (String(card.id) === String(id) ? { ...card, ...rollback } : card))
      );
    }
  };

  const handleAddClick = () => {
    resetFormData();
    setErrors({});
    setSelectedCard(null);
    setActiveActivityId(null);
    setShowFormModal(true);
  };

  const closeModal = () => {
    setErrors({});
    setSubmitError('');
    setUnaddedNoteWarning(false);
    setUnaddedTaskWarning(false);
    setShowFormModal(false);
    setSelectedCard(null);
    setActiveActivityId(null);
    setLeadMeetings([]);
    leadMeetingsRef.current = [];
    setIsLoadingLeadMeetings(false);
  };

  // Dismiss a modal via its backdrop. Uses onMouseDown instead of onClick and
  // only fires when the press BEGINS on the backdrop itself (event.target ===
  // event.currentTarget). This avoids the classic drag-to-select bug: if the
  // user presses down inside an input, drags out of the modal onto the backdrop
  // and releases, the browser dispatches a click on the nearest common ancestor
  // (the overlay), which would otherwise close the modal unintentionally.
  const backdropDismiss = (handler) => (event) => {
    if (event.target === event.currentTarget) handler();
  };

  const csvEscape = (value) => {
    const s = value == null ? '' : String(value);
    return `"${s.replace(/"/g, '""')}"`;
  };

  const exportCards = async (cardsToExport) => {
    if (cardsToExport.length === 0) return;
    const headers = [
      'Name',
      'Job title',
      'Company',
      'Email',
      'Phone',
      'Country',
      'Stage',
      'Owner',
      'Source',
      'Industry',
      'Lead score',
      'Deal name',
      'Deal value',
      'Currency',
      'Close date',
      'LinkedIn URL',
      'Company website',
      'Created date',
      'Updated date',
      'Next follow-up date',
    ];
    const rows = cardsToExport.map((card) => [
      card.name || 'Unnamed lead',
      card.jobTitle || '',
      card.company || '',
      card.email || '',
      card.phone || '',
      card.country || '',
      card.stage || 'New',
      card.owner || '',
      card.source || '',
      card.industry || '',
      card.leadScore ?? '',
      card.dealName || '',
      card.dealValue || '',
      card.currency || 'USD',
      card.closeDate || '',
      card.linkedinUrl || '',
      card.companyWebsite || '',
      card.createdDate || '',
      card.updatedDate || '',
      card.nextFollowUpDate || card.next_follow_up_date || '',
    ]);
    const csv = [headers, ...rows].map((row) => row.map(csvEscape).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `crm-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    const exportedIds = Array.from(
      new Set(
        cardsToExport
          .map((card) =>
            String(card.shared_item_id || card.id || card.leadId || card.lead_id || '')
          )
          .filter(Boolean)
      )
    );
    const targetCardIdSet = new Set(
      cardsToExport
        .flatMap((card) => [
          card.id !== undefined && card.id !== null && String(card.id),
          card.shared_item_id && String(card.shared_item_id),
          card.leadId && String(card.leadId),
          card.lead_id && String(card.lead_id),
        ])
        .filter(Boolean)
    );
    const now = new Date().toISOString();

    // 1. Immediately record in local state so the Contact's Activity tab has the export activity
    setCards((prevCards) =>
      prevCards.map((c) => {
        const matches =
          targetCardIdSet.has(String(c.id)) ||
          (c.shared_item_id && targetCardIdSet.has(String(c.shared_item_id))) ||
          (c.leadId && targetCardIdSet.has(String(c.leadId))) ||
          (c.lead_id && targetCardIdSet.has(String(c.lead_id)));
        if (matches) {
          const newActivity = {
            id: `A-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            type: 'LEAD_EXPORTED',
            message: 'Lead exported',
            description: 'Lead exported',
            createdAt: now,
            created_at: now,
            createdByName: currentUsername || 'User',
            createdBy: currentUserId,
          };
          const existingActs = Array.isArray(c.activities)
            ? c.activities
            : Array.isArray(c.crm?.activities)
              ? c.crm.activities
              : [];
          return {
            ...c,
            lastActivityDate: now,
            last_activity_date: now,
            updatedDate: now,
            activities: [...existingActs, newActivity],
          };
        }
        return c;
      })
    );

    // If detail modal is open for an exported card, update formData activities as well
    if (
      selectedCard &&
      (targetCardIdSet.has(String(selectedCard.id)) ||
        (selectedCard.shared_item_id && targetCardIdSet.has(String(selectedCard.shared_item_id))) ||
        (selectedCard.leadId && targetCardIdSet.has(String(selectedCard.leadId))) ||
        (selectedCard.lead_id && targetCardIdSet.has(String(selectedCard.lead_id))))
    ) {
      const newActivity = {
        id: `A-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        type: 'LEAD_EXPORTED',
        message: 'Lead exported',
        description: 'Lead exported',
        createdAt: now,
        created_at: now,
        createdByName: currentUsername || 'User',
        createdBy: currentUserId,
      };
      setFormData((prev) => ({
        ...prev,
        lastActivityDate: now,
        activities: [...(Array.isArray(prev.activities) ? prev.activities : []), newActivity],
      }));
    }

    try {
      await axios.post('/lead-pipeline/export', { ids: exportedIds });
    } catch (error) {
      console.warn('Could not record export activity:', error);
    }
  };

  const handleExport = async () => {
    if (sortedCards.length === 0) return;
    await exportCards(sortedCards);
  };

  const handleExportSelected = async () => {
    const selectedCards = sortedCards.filter((card) => selectedRowIds.includes(String(card.id)));
    if (selectedCards.length === 0) return;
    await exportCards(selectedCards);
  };

  const openImportModal = async () => {
    setImportMessage('');
    setSelectedImportIds([]);
    setImportSearchTerm('');
    setShowImportModal(true);
    try {
      const [savedResponse, prospectsResponse] = await Promise.all([
        axios.get('/saved-prospects').catch(() => ({ data: { data: [] } })),
        axios.get('/prospects').catch(() => ({ data: { data: [] } })),
      ]);

      const saved = Array.isArray(savedResponse.data?.data) ? savedResponse.data.data : [];
      const prospects = Array.isArray(prospectsResponse.data?.data)
        ? prospectsResponse.data.data
        : [];

      const combined = [
        ...saved.map((item) => ({ ...item, sourceType: 'saved', importId: `saved-${item.id}` })),
        ...prospects.map((item) => ({
          ...item,
          sourceType: 'prospect',
          importId: `prospect-${item.id}`,
        })),
      ];

      setSavedProspects(combined);
    } catch (error) {
      console.warn('Could not load prospects for import:', error);
      setSavedProspects([]);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportMessage('');
    setImportSearchTerm('');
    setImportConflicts([]);
    setShowConflictModal(false);
    setPendingAddCustomer(null);
    setSelectedImportIds([]);
  };

  const closeConflictModal = () => {
    setShowConflictModal(false);
    setImportConflicts([]);
    setPendingAddCustomer(null);
  };

  const toggleImportSelection = (id) => {
    setSelectedImportIds((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    );
  };

  const buildImportPayload = () => {
    const savedProspectIds = selectedImportIds
      .filter((id) => String(id).startsWith('saved-'))
      .map((id) => {
        const raw = String(id).replace('saved-', '');
        return /^\d+$/.test(raw) ? Number(raw) : raw;
      })
      .filter((id) => id !== '' && id !== null && id !== undefined);
    const prospectIds = selectedImportIds
      .filter((id) => String(id).startsWith('prospect-'))
      .map((id) => String(id).replace('prospect-', ''))
      .filter((id) => id !== '' && id !== null && id !== undefined);
    return { savedProspectIds, prospectIds };
  };

  const handleImportSelected = async () => {
    if (selectedImportIds.length === 0) return;
    setImportLoading(true);
    setImportMessage('');
    setImportConflicts([]);
    setShowConflictModal(false);
    try {
      const { savedProspectIds, prospectIds } = buildImportPayload();

      const response = await axios.post('/lead-pipeline/import', {
        savedProspectIds,
        prospectIds,
      });
      const imported = response.data?.imported || 0;
      const skipped = response.data?.skipped || 0;
      const conflictItems = response.data?.data?.conflicts || [];

      if (conflictItems.length > 0) {
        setImportConflicts(conflictItems);
        setShowConflictModal(true);
        setImportMessage(
          imported > 0 ? `Imported ${imported} lead${imported > 1 ? 's' : ''}.` : ''
        );
        return;
      }
      setImportMessage(
        imported > 0
          ? `Imported ${imported} lead${imported > 1 ? 's' : ''}${skipped > 0 ? `, ${skipped} already in pipeline` : ''}.`
          : skipped > 0
            ? 'Selected leads are already in the pipeline.'
            : 'Nothing to import.'
      );
      setSelectedImportIds([]);
      fetchCards();
    } catch (error) {
      console.error('Failed to import prospects:', error);
      setImportMessage('Import failed. Please try again.');
    } finally {
      setImportLoading(false);
    }
  };

  const submitAddCustomer = async (payload, confirmed = false) => {
    try {
      const now = new Date().toISOString();
      const body = confirmed ? { ...payload, confirm: true } : payload;
      const response = await axios.post('/lead-pipeline', body);
      const savedId = response.data?.data?.id || Date.now();
      const savedIds = response.data?.data || {};
      const savedStage = savedIds.stage || payload.stage || formData.stage || 'New';
      const savedLeadScore =
        savedIds.lead_score !== undefined
          ? savedIds.lead_score
          : calculateLeadScore({ ...formData, stage: savedStage }, customStages).totalScore;
      const newCard = {
        id: savedId,
        leadId: savedIds.lead_id || `L-${savedId}`,
        workspaceId: savedIds.workspace_id || formData.workspaceId,
        sourceProspectId: formData.sourceProspectId || savedIds.external_id || '',
        name: formData.name || 'Unnamed lead',
        jobTitle: formData.jobTitle,
        company: formData.company,
        companyLogo: getCompanyLogoUrl(
          formData.company,
          null,
          formData.email,
          formData.companyWebsite
        ),
        email: formData.email,
        phone: formData.phone,
        linkedinUrl: formData.linkedinUrl,
        companyWebsite: formData.companyWebsite,
        industry: formData.industry,
        country: formData.country,
        source: formData.source,
        stage: savedStage,
        owner: currentUsername,
        leadScore: savedLeadScore,
        dealName: formData.dealName,
        dealValue: formData.dealValue || '0',
        dealProbability: formData.dealProbability,
        currency: formData.currency,
        closeDate: formData.closeDate,
        productInterest: formData.productInterest,
        proposalReference: formData.proposalReference,
        wonValue: formData.wonValue,
        lastActivityDate: formData.lastActivityDate,
        nextFollowUpDate: formData.nextFollowUpDate,
        wonDate: formData.wonDate,
        lostDate: formData.lostDate,
        lostReason: formData.lostReason,
        lostContext: formData.lostContext,
        createdBy: formData.createdBy,
        createdDate: now,
        updatedDate: now,
      };
      setCards((current) => [...current, newCard]);
      setShowFormModal(false);
      setSelectedCard(null);
      resetFormData();
      return false;
    } catch (error) {
      // Members are not allowed to create a possible duplicate.
      if (error?.response?.data?.code === 'DUPLICATE_PERMISSION_DENIED') {
        setPendingAddCustomer(payload);
        setImportConflicts([error.response.data.conflict || { name: payload?.name || '' }]);
        setShowConflictModal(true);
        return true;
      }
      if (!confirmed && error?.response?.status === 409 && error?.response?.data?.conflict) {
        setPendingAddCustomer(payload);
        setImportConflicts([error.response.data.conflict]);
        setShowConflictModal(true);
        return true;
      }
      console.error('Failed to save lead to PostgreSQL:', error);
      setSubmitError(error?.response?.data?.error || 'Failed to save lead. Please try again.');
      throw error;
    }
  };

  const handleConfirmImport = async () => {
    setConfirmingImport(true);
    try {
      if (pendingAddCustomer) {
        await submitAddCustomer(pendingAddCustomer, true);
        setPendingAddCustomer(null);
        setImportConflicts([]);
        setShowConflictModal(false);
        return;
      }
      const { savedProspectIds, prospectIds } = buildImportPayload();
      const response = await axios.post('/lead-pipeline/import', {
        savedProspectIds,
        prospectIds,
        confirm: true,
      });
      const imported = response.data?.imported || 0;
      const skipped = response.data?.skipped || 0;
      setImportMessage(
        `Imported ${imported} lead${imported > 1 ? 's' : ''}${skipped > 0 ? `, ${skipped} already in pipeline` : ''}.`
      );
      setImportConflicts([]);
      setShowConflictModal(false);
      setSelectedImportIds([]);
      fetchCards();
    } catch (error) {
      console.error('Failed to confirm import:', error);
      setImportMessage('Confirm import failed. Please try again.');
    } finally {
      setConfirmingImport(false);
    }
  };

  const ownerOptions = Array.from(new Set(cards.map((card) => card.owner).filter(Boolean)));

  const toTitleCase = (str = '') => {
    if (!str) return '';
    const trimmed = String(str).trim();
    const ACRONYMS = new Set([
      'it',
      'crm',
      'ai',
      'saas',
      'b2b',
      'b2c',
      'uk',
      'usa',
      'myr',
      'usd',
      'api',
    ]);
    return trimmed
      .split(/\s+/)
      .map((word) => {
        const lower = word.toLowerCase();
        if (ACRONYMS.has(lower)) return lower.toUpperCase();
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  const dedupeTitleCaseOptions = (list = []) => {
    const map = new Map();
    list.forEach((item) => {
      if (!item) return;
      const str = String(item).trim();
      if (!str) return;
      const key = str.toLowerCase();
      if (!map.has(key)) {
        map.set(key, toTitleCase(str));
      }
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
  };

  const sourceOptions = dedupeTitleCaseOptions([
    'Referral',
    'Website',
    'LinkedIn',
    'Email',
    'Prospect Finder',
    'Landing Page',
    ...cards.map((card) => card.source).filter(Boolean),
  ]);

  const companyOptions = Array.from(
    new Set(
      cards
        .map((card) => card.company)
        .filter(Boolean)
        .map((c) => String(c).trim())
    )
  ).sort((a, b) => a.localeCompare(b));

  const industryOptions = dedupeTitleCaseOptions(
    cards.map((card) => card.industry).filter(Boolean)
  );

  const countryOptions = dedupeTitleCaseOptions(cards.map((card) => card.country).filter(Boolean));

  const createdDateOptions = Array.from(
    new Set(
      cards
        .map((card) => {
          if (!card.createdDate) return null;
          const match = String(card.createdDate).match(/^\d{4}-\d{2}-\d{2}/);
          return match ? match[0] : null;
        })
        .filter(Boolean)
    )
  ).sort((a, b) => (a < b ? 1 : -1));

  const startOfWeek = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
    return d;
  };

  const endOfWeek = (start) => {
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return end;
  };

  const matchesFollowUp = (card, filter) => {
    if (!filter || filter === 'Any follow-up') return true;
    if (card.stage === 'Won' || card.stage === 'Lost') return false;
    const followUpDate = getCardFollowUpDate(card);
    if (!followUpDate) return false;

    const parsedDate = parseFlexibleDate(followUpDate);
    if (!parsedDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDay = new Date(parsedDate);
    targetDay.setHours(0, 0, 0, 0);

    const isOverdue = isFollowUpOverdue(followUpDate);
    const isDueSoon = isFollowUpDueSoon(followUpDate);
    const thisWeekStart = startOfWeek(today);
    const thisWeekEnd = endOfWeek(thisWeekStart);
    const nextWeekStart = new Date(thisWeekStart);
    nextWeekStart.setDate(thisWeekStart.getDate() + 7);
    const nextWeekEnd = endOfWeek(nextWeekStart);

    switch (filter) {
      case 'Overdue':
        return isOverdue;
      case 'Today':
        return isDueSoon || targetDay.getTime() === today.getTime();
      case 'This week':
        return targetDay >= thisWeekStart && targetDay <= thisWeekEnd;
      case 'Next week':
        return targetDay >= nextWeekStart && targetDay <= nextWeekEnd;
      default:
        return true;
    }
  };

  const parseCardDate = (raw) => {
    if (!raw) return null;
    const date = new Date(String(raw).includes('T') ? raw : raw + 'T00:00:00');
    if (Number.isNaN(date.getTime())) return null;
    return date;
  };

  const matchesLeadScoreRange = (score, filter) => {
    if (!filter || filter === 'Any lead score') return true;
    const num = Number(score);
    if (Number.isNaN(num)) return false;
    const [min, max] = filter.split('-').map(Number);
    return num >= min && num <= max;
  };

  const matchesDateRange = (raw, filter, anyLabel) => {
    if (!filter || filter === anyLabel) return true;
    const date = parseCardDate(raw);
    if (!date) return false;
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((today - dayStart) / 86400000);
    switch (filter) {
      case 'Today':
        return diffDays === 0;
      case 'Last 7 days':
        return diffDays >= 0 && diffDays <= 7;
      case 'Last 30 days':
        return diffDays >= 0 && diffDays <= 30;
      case 'Last 90 days':
        return diffDays >= 0 && diffDays <= 90;
      case 'This year':
        return date.getFullYear() === today.getFullYear();
      default:
        if (/^\d{4}-\d{2}-\d{2}$/.test(filter)) {
          const target = new Date(`${filter}T00:00:00`);
          const dayStart = new Date(date);
          dayStart.setHours(0, 0, 0, 0);
          return dayStart.getTime() === target.getTime();
        }
        return true;
    }
  };

  const matchesCloseDateRange = (raw, filter) => {
    if (!filter || filter === 'Any close date') return true;
    if (filter === 'No date set') return !raw;
    const date = parseCardDate(raw);
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const nextMonthStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const thisWeekStart = startOfWeek(today);
    const nextWeekStart = new Date(thisWeekStart);
    nextWeekStart.setDate(thisWeekStart.getDate() + 7);
    const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
    const nextQuarterStart = new Date(
      today.getFullYear(),
      Math.floor(today.getMonth() / 3) * 3 + 3,
      1
    );
    switch (filter) {
      case 'Overdue':
        return date < today;
      case 'This week':
        return date >= thisWeekStart && date < nextWeekStart;
      case 'This month':
        return date >= monthStart && date < nextMonthStart;
      case 'This quarter':
        return date >= quarterStart && date < nextQuarterStart;
      case 'This year':
        return date.getFullYear() === today.getFullYear();
      default:
        return true;
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = String(name).trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return String(name).trim().slice(0, 2).toUpperCase();
  };

  const CRM_FILTER_FIELDS = [
    { key: 'stages', label: 'Stages', icon: 'fas fa-tasks' },
    { key: 'companies', label: 'Companies', icon: 'fas fa-building' },
    { key: 'industries', label: 'Industries', icon: 'fas fa-industry' },
    { key: 'owners', label: 'Owners', icon: 'fas fa-user' },
    { key: 'sources', label: 'Sources', icon: 'fas fa-share-alt' },
    { key: 'countries', label: 'Countries', icon: 'fas fa-globe' },
    { key: 'leadScore', label: 'Lead score', icon: 'fas fa-star' },
    { key: 'createdDate', label: 'Created date', icon: 'fas fa-calendar-alt' },
    { key: 'closeDate', label: 'Close date', icon: 'fas fa-calendar-check' },
    { key: 'outcome', label: 'Outcome', icon: 'fas fa-flag' },
    { key: 'followUp', label: 'Follow-up', icon: 'fas fa-clock' },
  ];

  const getFieldOptions = (fieldKey) => {
    switch (fieldKey) {
      case 'stages': {
        return STAGES.map((stage) => ({
          id: stage,
          label: stage,
          avatarType: 'stage',
        }));
      }
      case 'companies': {
        return companyOptions.map((c) => ({
          id: c,
          label: c,
          avatarType: 'icon',
          icon: 'fas fa-building',
        }));
      }
      case 'industries': {
        return industryOptions.map((i) => ({
          id: i,
          label: i,
          avatarType: 'icon',
          icon: 'fas fa-industry',
        }));
      }
      case 'owners': {
        const currentMember = workspaceMembers.find((m) => m.user_id === currentUserId);
        const currentUserName =
          currentMember?.display_name ||
          currentMember?.username ||
          keycloak?.tokenParsed?.name ||
          currentUsername ||
          '';

        const options = [{ id: 'unassigned', label: 'Unassigned', avatarType: 'unassigned' }];
        if (currentUserName) {
          options.push({
            id: currentUserName,
            label: currentUserName,
            avatarType: 'initials',
            initials: getInitials(currentUserName),
          });
        }
        salesMemberOptions.forEach((m) => {
          const name = m.display_name || m.username || m.email;
          if (
            m.user_id !== currentUserId &&
            name &&
            name !== currentUserName &&
            !options.some((o) => o.label.toLowerCase() === name.toLowerCase() || o.id === name)
          ) {
            options.push({
              id: name,
              label: name,
              avatarType: 'initials',
              initials: getInitials(name),
            });
          }
        });
        const extraNames = Array.from(
          new Set(cards.map((c) => c.assigneeName || c.owner || c.assignee).filter(Boolean))
        );
        extraNames.forEach((name) => {
          if (
            name !== 'unassigned' &&
            name !== currentUsername &&
            name !== currentUserName &&
            name !== currentUserId &&
            !options.some((o) => o.label.toLowerCase() === name.toLowerCase() || o.id === name)
          ) {
            options.push({
              id: name,
              label: name,
              avatarType: 'initials',
              initials: getInitials(name),
            });
          }
        });
        return options;
      }
      case 'sources': {
        return sourceOptions.map((src) => ({
          id: src,
          label: src,
          avatarType: 'icon',
          icon: 'fas fa-share-alt',
        }));
      }
      case 'countries': {
        return countryOptions.map((c) => ({
          id: c,
          label: c,
          avatarType: 'icon',
          icon: 'fas fa-globe',
        }));
      }
      case 'leadScore': {
        return ['0-20', '21-40', '41-60', '61-80', '81-100'].map((s) => ({
          id: s,
          label: s,
          avatarType: 'icon',
          icon: 'fas fa-star',
        }));
      }
      case 'createdDate': {
        return [
          'Today',
          'Last 7 days',
          'Last 30 days',
          'Last 90 days',
          'This year',
          ...createdDateOptions,
        ].map((d) => ({
          id: d,
          label: d,
          avatarType: 'icon',
          icon: 'fas fa-calendar-alt',
        }));
      }
      case 'closeDate': {
        return [
          'Overdue',
          'This week',
          'This month',
          'This quarter',
          'This year',
          'No date set',
        ].map((d) => ({
          id: d,
          label: d,
          avatarType: 'icon',
          icon: 'fas fa-calendar-check',
        }));
      }
      case 'outcome': {
        return ['Won', 'Lost', 'Open'].map((o) => ({
          id: o,
          label: o,
          avatarType: 'stage',
        }));
      }
      case 'followUp': {
        return ['Overdue', 'Today', 'This week', 'Next week'].map((f) => ({
          id: f,
          label: f,
          avatarType: 'icon',
          icon: 'fas fa-clock',
        }));
      }
      default:
        return [];
    }
  };

  const isProspectAlreadyInPipeline = (sp) => {
    if (!sp || !Array.isArray(cards) || cards.length === 0) return false;
    const spEmail = emailToString(sp.email || sp.email_addresses)
      .trim()
      .toLowerCase();
    const spName = String(sp.name || sp.full_name || '')
      .trim()
      .toLowerCase();
    const spCompany = String(sp.company_name || sp.companyName || sp.company || '')
      .trim()
      .toLowerCase();
    const spId = String(sp.id || sp.prospect_id || sp.saved_prospect_id || '').trim();
    const spContactId = String(sp.contact_id || sp.external_id || '').trim();

    return cards.some((card) => {
      // 1. Matched by ID / External ID / Source Prospect ID
      const cardSourceId = String(
        card.sourceProspectId ||
          card.source_prospect_id ||
          card.source_saved_prospect_id ||
          card.external_id ||
          card.id ||
          ''
      ).trim();

      if (spId && cardSourceId === spId) return true;
      if (spContactId && cardSourceId === spContactId) return true;

      // 2. Matched by Email (if non-empty)
      const cardEmails = [card.email, ...(Array.isArray(card.emails) ? card.emails : [])]
        .map((e) =>
          String(e || '')
            .trim()
            .toLowerCase()
        )
        .filter(Boolean);
      const spEmails = [
        spEmail,
        ...(Array.isArray(sp.email) ? sp.email : []),
        ...(Array.isArray(sp.email_addresses) ? sp.email_addresses : []),
      ]
        .map((e) =>
          String(e || '')
            .trim()
            .toLowerCase()
        )
        .filter(Boolean);

      if (spEmails.some((se) => cardEmails.includes(se))) return true;

      // 3. Matched by exact Name + Company
      const cardName = String(card.name || card.full_name || '')
        .trim()
        .toLowerCase();
      const cardCompany = String(card.company || card.company_name || '')
        .trim()
        .toLowerCase();
      if (
        spName &&
        cardName &&
        spName === cardName &&
        spCompany &&
        cardCompany &&
        spCompany === cardCompany
      ) {
        return true;
      }

      return false;
    });
  };

  const filteredImportProspects = savedProspects
    .filter((sp) => {
      if (!importSearchTerm.trim()) return true;
      const term = importSearchTerm.trim().toLowerCase();
      const name = String(sp.name || sp.full_name || '').toLowerCase();
      const title = String(sp.job_title || sp.jobTitle || sp.title || '').toLowerCase();
      const company = String(sp.company_name || sp.companyName || sp.company || '').toLowerCase();
      const email = emailToString(sp.email || sp.email_addresses).toLowerCase();
      const phone = phoneToString(sp.phone_number || sp.phones || sp.phone).toLowerCase();
      return (
        name.includes(term) ||
        title.includes(term) ||
        company.includes(term) ||
        email.includes(term) ||
        phone.includes(term)
      );
    })
    .sort((a, b) => {
      const aInPipeline = isProspectAlreadyInPipeline(a);
      const bInPipeline = isProspectAlreadyInPipeline(b);
      if (aInPipeline !== bInPipeline) {
        return aInPipeline ? 1 : -1; // Items already in pipeline always placed at the bottom
      }
      const nameA = String(a.name || a.full_name || '');
      const nameB = String(b.name || b.full_name || '');
      return nameA.localeCompare(nameB);
    });

  const visibleCards = cards.filter((card) => {
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !term ||
      [
        card.name,
        card.email,
        ...(Array.isArray(card.emails) ? card.emails : []),
        card.phone,
        ...(Array.isArray(card.phones) ? card.phones : []),
        card.company,
        card.jobTitle,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term);

    // 1. All stages
    const selectedStages = filters.stages || filters.status;
    const matchesStage = (() => {
      if (!selectedStages || selectedStages === 'All stages') return true;
      const list = Array.isArray(selectedStages) ? selectedStages : [selectedStages];
      if (list.length === 0 || list.includes('All stages')) return true;
      return list.includes(card.stage);
    })();

    // 2. All companies
    const selectedCompanies = filters.companies || filters.company || filters.parent;
    const matchesCompany = (() => {
      if (!selectedCompanies || selectedCompanies === 'All companies') return true;
      const list = Array.isArray(selectedCompanies) ? selectedCompanies : [selectedCompanies];
      if (list.length === 0 || list.includes('All companies')) return true;
      return list.some((c) => {
        if (c === 'unassigned' || c === 'Unassigned') return !card.company;
        return card.company === c;
      });
    })();

    // 3. All industries
    const selectedIndustries = filters.industries || filters.industry;
    const matchesIndustry = (() => {
      if (!selectedIndustries || selectedIndustries === 'All industries') return true;
      const list = Array.isArray(selectedIndustries) ? selectedIndustries : [selectedIndustries];
      if (list.length === 0 || list.includes('All industries')) return true;
      return list.some((i) => (card.industry || '').toLowerCase() === String(i).toLowerCase());
    })();

    // 4. All owners
    const selectedOwners = filters.owners || filters.assignee;
    const matchesOwner = (() => {
      if (!selectedOwners || selectedOwners === 'All owners') return true;
      const list = Array.isArray(selectedOwners) ? selectedOwners : [selectedOwners];
      if (list.length === 0 || list.includes('All owners')) return true;
      return list.some((val) => {
        if (val === 'unassigned' || val === 'Unassigned') {
          return !card.assignee || String(card.assignee).trim() === '';
        }
        const normalizedVal = String(val).toLowerCase();
        return (
          (card.assignee && String(card.assignee).toLowerCase() === normalizedVal) ||
          (card.owner && String(card.owner).toLowerCase() === normalizedVal) ||
          (card.assigneeName && String(card.assigneeName).toLowerCase() === normalizedVal) ||
          (val === currentUserId &&
            (card.assignee === currentUserId || card.owner === currentUsername)) ||
          (currentUsername &&
            normalizedVal === currentUsername.toLowerCase() &&
            (card.owner === currentUsername || card.assignee === currentUserId))
        );
      });
    })();

    // 5. All sources
    const selectedSources = filters.sources || filters.workType;
    const matchesSource = (() => {
      if (!selectedSources || selectedSources === 'All sources') return true;
      const list = Array.isArray(selectedSources) ? selectedSources : [selectedSources];
      if (list.length === 0 || list.includes('All sources')) return true;
      return list.some((s) => (card.source || '').toLowerCase() === String(s).toLowerCase());
    })();

    // 6. All countries
    const selectedCountries = filters.countries || filters.country;
    const matchesCountry = (() => {
      if (!selectedCountries || selectedCountries === 'All countries') return true;
      const cardCountry = (card.country || '').toLowerCase();
      const list = Array.isArray(selectedCountries) ? selectedCountries : [selectedCountries];
      if (list.length === 0 || list.includes('All countries')) return true;
      return list.some((item) => {
        const filter = COUNTRY_ALIASES[item.toLowerCase()] || item.toLowerCase();
        return cardCountry.includes(filter) || filter.includes(cardCountry);
      });
    })();

    // 7. Any lead score
    const selectedLeadScore = filters.leadScore;
    const matchesLeadScore = (() => {
      if (!selectedLeadScore || selectedLeadScore === 'Any lead score') return true;
      const list = Array.isArray(selectedLeadScore) ? selectedLeadScore : [selectedLeadScore];
      if (list.length === 0 || list.includes('Any lead score')) return true;
      return list.some((r) => matchesLeadScoreRange(card.leadScore, r));
    })();

    // 8. Any created date
    const selectedCreatedDate = filters.createdDate;
    const matchesCreatedDate = (() => {
      if (!selectedCreatedDate || selectedCreatedDate === 'Any created date') return true;
      const list = Array.isArray(selectedCreatedDate) ? selectedCreatedDate : [selectedCreatedDate];
      if (list.length === 0 || list.includes('Any created date')) return true;
      return list.some((d) => matchesDateRange(card.createdDate, d, 'Any created date'));
    })();

    // 9. Any close date
    const selectedCloseDate = filters.closeDate;
    const matchesCloseDate = (() => {
      if (!selectedCloseDate || selectedCloseDate === 'Any close date') return true;
      const list = Array.isArray(selectedCloseDate) ? selectedCloseDate : [selectedCloseDate];
      if (list.length === 0 || list.includes('Any close date')) return true;
      return list.some((d) => matchesCloseDateRange(card.closeDate, d));
    })();

    // 10. Any outcome
    const selectedOutcome = filters.outcome || filters.labels;
    const matchesOutcome = (() => {
      if (!selectedOutcome || selectedOutcome === 'Any outcome') return true;
      const list = Array.isArray(selectedOutcome) ? selectedOutcome : [selectedOutcome];
      if (list.length === 0 || list.includes('Any outcome')) return true;
      return list.some((lbl) => {
        if (lbl === 'Won') return card.stage === 'Won';
        if (lbl === 'Lost') return card.stage === 'Lost';
        if (lbl === 'Open') return card.stage !== 'Won' && card.stage !== 'Lost';
        return card.stage === lbl;
      });
    })();

    // 11. Any follow-up
    const selectedFollowUp = filters.followUp;
    const matchesFollowUpFilter = (() => {
      if (!selectedFollowUp || selectedFollowUp === 'Any follow-up') return true;
      const list = Array.isArray(selectedFollowUp) ? selectedFollowUp : [selectedFollowUp];
      if (list.length === 0 || list.includes('Any follow-up')) return true;
      return list.some((f) => matchesFollowUp(card, f));
    })();

    return (
      matchesSearch &&
      matchesStage &&
      matchesCompany &&
      matchesIndustry &&
      matchesOwner &&
      matchesSource &&
      matchesCountry &&
      matchesLeadScore &&
      matchesCreatedDate &&
      matchesCloseDate &&
      matchesOutcome &&
      (showArchived ? card.isArchived === true : !card.isArchived) &&
      matchesFollowUpFilter
    );
  });

  const lastActivityRaw = (card) => {
    if (card.lastActivityDate || card.last_activity_date) {
      return card.lastActivityDate || card.last_activity_date;
    }
    const candidates = [];
    const collect = (items) => {
      if (!Array.isArray(items)) return;
      for (const item of items) {
        if (item?.createdAt) candidates.push(item.createdAt);
        if (item?.created_at) candidates.push(item.created_at);
        if (item?.completedAt) candidates.push(item.completedAt);
        if (item?.completed_at) candidates.push(item.completed_at);
      }
    };
    collect(card.notes);
    collect(card.tasks);
    collect(card.activities);
    const times = candidates
      .map((raw) => {
        const d = parseCardDate(raw);
        return d ? d.getTime() : null;
      })
      .filter((t) => t !== null);
    return times.length === 0 ? null : new Date(Math.max(...times)).toISOString();
  };

  const nextFollowUpRaw = (card) => {
    if (card.nextFollowUpDate || card.next_follow_up_date) {
      return card.nextFollowUpDate || card.next_follow_up_date;
    }
    const tasks = Array.isArray(card.tasks) ? card.tasks : [];
    const pending = tasks.filter((t) => {
      const status = t.status || (t.completed ? 'completed' : 'pending');
      return !['completed', 'cancelled'].includes(status);
    });
    const dueDates = [];
    for (const task of pending) {
      const raw = task.dueDate || task.due_date;
      if (!raw) continue;
      const d = parseCardDate(raw);
      if (d) dueDates.push(d.getTime());
    }
    return dueDates.length === 0 ? null : new Date(Math.min(...dueDates)).toISOString();
  };

  const sortValue = (card, key) => {
    switch (key) {
      case 'createdDate':
      case 'updatedDate':
      case 'expectedCloseDate':
      case 'lastActivityDate':
      case 'nextFollowUpDate': {
        const date = parseCardDate(
          key === 'lastActivityDate'
            ? lastActivityRaw(card)
            : key === 'nextFollowUpDate'
              ? nextFollowUpRaw(card)
              : card[key === 'expectedCloseDate' ? 'closeDate' : key]
        );
        return date ? date.getTime() : null;
      }
      case 'leadScore':
        return Number(card.leadScore);
      case 'dealValue': {
        const value = parseFloat(card.dealValue);
        return Number.isNaN(value) ? null : value;
      }
      default:
        return null;
    }
  };

  const sortedCards = [...visibleCards].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const va = sortValue(a, sortConfig.key);
    const vb = sortValue(b, sortConfig.key);
    const asc = sortConfig.dir === 'asc';
    if (va == null && vb == null) return 0;
    if (va == null) return asc ? -1 : 1;
    if (vb == null) return asc ? 1 : -1;
    if (va < vb) return asc ? -1 : 1;
    if (va > vb) return asc ? 1 : -1;
    return 0;
  });

  // ── List view: pagination ────────────────────────────────────────────────
  const pageCount = Math.max(1, Math.ceil(sortedCards.length / pageSize));
  const effectivePage = Math.min(Math.max(1, tablePage), pageCount);
  const pageStart = (effectivePage - 1) * pageSize;
  const paginatedCards = sortedCards.slice(pageStart, pageStart + pageSize);

  // ── List view: row selection ─────────────────────────────────────────────
  const pageCardIds = paginatedCards.map((c) => String(c.id));
  const allPageSelected =
    pageCardIds.length > 0 && pageCardIds.every((id) => selectedRowIds.includes(id));
  const toggleRowSelection = (id) => {
    const sid = String(id);
    setSelectedRowIds((current) =>
      current.includes(sid) ? current.filter((x) => x !== sid) : [...current, sid]
    );
  };
  const toggleSelectAllPage = () => {
    setSelectedRowIds((current) =>
      allPageSelected
        ? current.filter((id) => !pageCardIds.includes(id))
        : Array.from(new Set([...current, ...pageCardIds]))
    );
  };
  const clearRowSelection = () => setSelectedRowIds([]);

  const renderPageNumbers = () => {
    const pages = [];
    const start = Math.max(1, effectivePage - 2);
    const end = Math.min(pageCount, effectivePage + 2);
    if (start > 1) pages.push(1);
    if (start > 2) pages.push('…');
    for (let p = start; p <= end; p += 1) pages.push(p);
    if (end < pageCount - 1) pages.push('…');
    if (end < pageCount) pages.push(pageCount);
    return pages;
  };

  const filterDefinitions = FILTERS.map((filter) => ({
    ...filter,
    options:
      filter.key === 'stages'
        ? ['All stages', ...STAGES]
        : filter.key === 'owners'
          ? ['All owners', ...ownerOptions]
          : filter.key === 'sources'
            ? ['All sources', ...sourceOptions]
            : filter.key === 'companies'
              ? ['All companies', ...companyOptions]
              : filter.key === 'industries'
                ? ['All industries', ...industryOptions]
                : filter.key === 'countries'
                  ? ['All countries', ...countryOptions]
                  : filter.key === 'createdDate'
                    ? [
                        'Any created date',
                        'Today',
                        'Last 7 days',
                        'Last 30 days',
                        'Last 90 days',
                        'This year',
                        ...createdDateOptions,
                      ]
                    : filter.options,
  }));

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (!value) return false;
    if (Array.isArray(value)) return value.length > 0;
    const def = FILTERS.find((f) => f.key === key);
    return !def || value !== def.label;
  });

  const currentFieldDef =
    CRM_FILTER_FIELDS.find((f) => f.key === activeFilterField) || CRM_FILTER_FIELDS[0];
  const allCurrentFieldOptions = getFieldOptions(activeFilterField);
  const filteredFieldOptions = allCurrentFieldOptions.filter(
    (opt) =>
      !filterSearchQuery.trim() ||
      opt.label.toLowerCase().includes(filterSearchQuery.trim().toLowerCase())
  );
  const totalActiveFilterCount = Object.values(filters).reduce((acc, v) => {
    if (!v) return acc;
    if (Array.isArray(v)) return acc + v.length;
    return acc + 1;
  }, 0);

  // Pipeline summary stats scoped to current mode (normal active leads vs archived leads)
  const currentModeCards = cards.filter((card) =>
    showArchived ? card.isArchived === true : !card.isArchived
  );

  // Group raw open pipeline values by native currency (for display/breakdown)
  const openPipelineByCurrency = currentModeCards
    .filter((card) => card.stage !== 'Won' && card.stage !== 'Lost')
    .reduce((acc, card) => {
      const curr = String(card.currency || 'USD').toUpperCase();
      const val = parseFloat(card.dealValue) || 0;
      acc[curr] = (acc[curr] || 0) + val;
      return acc;
    }, {});

  // Group raw won pipeline values by native currency
  const wonPipelineByCurrency = currentModeCards
    .filter((card) => card.stage === 'Won')
    .reduce((acc, card) => {
      const curr = String(card.currency || 'USD').toUpperCase();
      const val = parseFloat(card.dealValue) || 0;
      acc[curr] = (acc[curr] || 0) + val;
      return acc;
    }, {});

  // Calculate TOTAL Open Pipeline Value CONVERTED to the user's preferred viewing currency
  const totalConvertedOpenValue = currentModeCards
    .filter((card) => card.stage !== 'Won' && card.stage !== 'Lost')
    .reduce((sum, card) => {
      const converted = convertCurrency(
        card.dealValue,
        card.currency || 'USD',
        pipelineCurrency,
        fxRates
      );
      return sum + converted;
    }, 0);

  // Calculate TOTAL Won Pipeline Value CONVERTED to the user's preferred viewing currency
  const totalConvertedWonValue = currentModeCards
    .filter((card) => card.stage === 'Won')
    .reduce((sum, card) => {
      const converted = convertCurrency(
        card.dealValue,
        card.currency || 'USD',
        pipelineCurrency,
        fxRates
      );
      return sum + converted;
    }, 0);

  const selectedCurrencySymbol = CURRENCY_SYMBOLS[pipelineCurrency] || `${pipelineCurrency} `;

  // Pipeline summary stats shown in the KPI boxes and CRM summary view
  const totalActiveLeads = currentModeCards.filter(
    (card) => card.stage !== 'Won' && card.stage !== 'Lost'
  ).length;
  const unassignedLeads = currentModeCards.filter(
    (card) =>
      card.stage !== 'Won' &&
      card.stage !== 'Lost' &&
      (!card.assignee || String(card.assignee).trim() === '')
  ).length;
  const wonLeads = currentModeCards.filter((card) => card.stage === 'Won').length;
  const lostLeads = currentModeCards.filter((card) => card.stage === 'Lost').length;

  const totalClosedLeads = wonLeads + lostLeads;
  const winRate = totalClosedLeads > 0 ? Math.round((wonLeads / totalClosedLeads) * 100) : 0;
  const avgDealSize =
    totalActiveLeads > 0 ? Math.round(totalConvertedOpenValue / totalActiveLeads) : 0;

  // Stage distribution data for pipeline funnel (converted to user's preferred currency)
  const stageStats = STAGES.map((stage) => {
    const stageCards = currentModeCards.filter((c) => c.stage === stage);
    const count = stageCards.length;
    const value = stageCards.reduce((sum, c) => {
      const converted = convertCurrency(
        c.dealValue,
        c.currency || 'USD',
        pipelineCurrency,
        fxRates
      );
      return sum + converted;
    }, 0);
    const pct =
      currentModeCards.length > 0 ? Math.round((count / currentModeCards.length) * 100) : 0;
    return { stage, count, value, pct };
  });

  // Lead score quality tiers
  const highQualityLeads = currentModeCards.filter((c) => Number(c.leadScore) >= 70).length;
  const mediumQualityLeads = currentModeCards.filter(
    (c) => Number(c.leadScore) >= 40 && Number(c.leadScore) < 70
  ).length;
  const lowQualityLeads = currentModeCards.filter(
    (c) => !c.leadScore || Number(c.leadScore) < 40
  ).length;
  const highQualityPct =
    currentModeCards.length > 0
      ? Math.round((highQualityLeads / currentModeCards.length) * 100)
      : 0;
  const mediumQualityPct =
    currentModeCards.length > 0
      ? Math.round((mediumQualityLeads / currentModeCards.length) * 100)
      : 0;
  const lowQualityPct =
    currentModeCards.length > 0 ? Math.round((lowQualityLeads / currentModeCards.length) * 100) : 0;

  // Top lead sources
  const sourceStats = (() => {
    const counts = {};
    currentModeCards.forEach((c) => {
      const src = c.source || 'Direct / Other';
      counts[src] = (counts[src] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([source, count]) => ({
        source,
        count,
        pct: currentModeCards.length > 0 ? Math.round((count / currentModeCards.length) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  })();

  // Follow-up stats (calculated across active leads in current mode)
  const followUpStats = (() => {
    let due = 0;
    let overdue = 0;
    for (const card of currentModeCards) {
      if (card.stage === 'Won' || card.stage === 'Lost') continue;
      const followUpDate = getCardFollowUpDate(card);
      if (!followUpDate) continue;

      if (isFollowUpOverdue(followUpDate)) {
        overdue += 1;
      } else if (isFollowUpDueSoon(followUpDate)) {
        due += 1;
      }
    }
    return { due, overdue };
  })();
  const followUpsDue = followUpStats.due;
  const overdueFollowUps = followUpStats.overdue;

  return (
    <div className="crm-page">
      {isDragActive && <div className="crm-drag-overlay" />}
      {isLoading ? (
        <div className="crm-loading">Loading leads...</div>
      ) : (
        <>
          <div className="crm-banner-wrap">
            <Banner
              title="Customer Relationship Management"
              subtitle="Manage customer relationships and track interactions."
              type="page-header"
            />
            {isDragActive && canDeleteLead && (
              <div
                className={`crm-delete-zone${trashOver ? ' crm-delete-zone-active' : ''}`}
                onDragEnter={(event) => {
                  event.preventDefault();
                  setTrashOver(true);
                }}
                onDragLeave={() => setTrashOver(false)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleTrashDrop}
              >
                <i className="fas fa-trash" />
              </div>
            )}
          </div>
          {roleAccessBanner && (
            <div className="crm-role-access-banner">
              <i className={roleAccessBanner.icon} />
              <span>
                <strong>{roleAccessBanner.title}</strong> {roleAccessBanner.desc}
              </span>
            </div>
          )}
          <div className="crm-toolbar">
            <input
              type="text"
              className="crm-search-input"
              placeholder="Search by name, email, phone or company"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />

            <div className="crm-filter-anchor" ref={filterMenuRef}>
              <button
                type="button"
                className={`crm-filter-toggle${filtersOpen ? ' active' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
              >
                <svg
                  className="crm-filter-btn-icon"
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path d="M1.5 3.25a.75.75 0 0 1 .75-.75h11.5a.75.75 0 0 1 0 1.5H2.25a.75.75 0 0 1-.75-.75Zm2 4.75a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5H4.25a.75.75 0 0 1-.75-.75Zm2.75 4a.75.75 0 0 1 .75-.75h2a.75.75 0 0 1 0 1.5h-2a.75.75 0 0 1-.75-.75Z" />
                </svg>
                Filter
                {totalActiveFilterCount > 0 && (
                  <span className="crm-filter-count-badge">{totalActiveFilterCount}</span>
                )}
              </button>

              {filtersOpen && (
                <div className="crm-filter-popover" role="dialog" aria-label="Filter menu">
                  <div className="crm-filter-popover-main">
                    {/* Left Column: Filter Fields (Exact 11 CRM categories) */}
                    <div className="crm-filter-popover-left">
                      <div className="crm-filter-fields-list">
                        {CRM_FILTER_FIELDS.map((field) => {
                          const isActive = activeFilterField === field.key;
                          const selectedVal = filters[field.key];
                          const count = Array.isArray(selectedVal)
                            ? selectedVal.length
                            : selectedVal
                              ? 1
                              : 0;

                          return (
                            <div
                              key={field.key}
                              className={`crm-filter-field-item${isActive ? ' active' : ''}`}
                              onClick={() => {
                                setActiveFilterField(field.key);
                                setFilterSearchQuery('');
                              }}
                            >
                              <i className={`${field.icon} crm-filter-field-icon`} />
                              <span className="crm-filter-field-label">{field.label}</span>
                              {count > 0 && <span className="crm-filter-field-badge">{count}</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Right Column: Search + Options */}
                    <div className="crm-filter-popover-right">
                      {/* Search box */}
                      <div className="crm-filter-search-box">
                        <i className="fas fa-search crm-filter-search-icon" />
                        <input
                          type="text"
                          className="crm-filter-search-input"
                          placeholder={`Search ${currentFieldDef?.label.toLowerCase() || 'options'}`}
                          value={filterSearchQuery}
                          onChange={(e) => setFilterSearchQuery(e.target.value)}
                          autoFocus
                        />
                        {filterSearchQuery && (
                          <button
                            type="button"
                            className="crm-filter-search-clear"
                            onClick={() => setFilterSearchQuery('')}
                          >
                            <i className="fas fa-times" />
                          </button>
                        )}
                      </div>

                      {/* Options with checkboxes (no redundant icons) */}
                      <div className="crm-filter-options-list">
                        {filteredFieldOptions.map((opt) => {
                          const currentSelected = filters[activeFilterField];
                          const isChecked = Array.isArray(currentSelected)
                            ? currentSelected.includes(opt.id)
                            : currentSelected === opt.id;

                          return (
                            <label key={opt.id} className="crm-filter-option-item">
                              <input
                                type="checkbox"
                                className="crm-filter-checkbox"
                                checked={!!isChecked}
                                onChange={() => toggleFilterOption(activeFilterField, opt.id)}
                              />
                              <span className="crm-filter-option-label">{opt.label}</span>
                            </label>
                          );
                        })}
                        {filteredFieldOptions.length === 0 && (
                          <div className="crm-filter-no-results">No options found</div>
                        )}
                      </div>

                      {/* Right Pane Footer */}
                      <div className="crm-filter-right-footer">
                        <button
                          type="button"
                          className="crm-filter-clear-btn"
                          onClick={() => clearFieldFilter(activeFilterField)}
                          disabled={!filters[activeFilterField]}
                        >
                          Clear
                        </button>
                        <span className="crm-filter-count-label">
                          {filteredFieldOptions.length} of {allCurrentFieldOptions.length}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Full-Width Footer Bar */}
                  <div className="crm-filter-popover-bottom-bar">
                    <div className="crm-filter-shortcut-hint">
                      Press <kbd>Shift</kbd> + <kbd>F</kbd> to open and close
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="crm-sort-anchor" ref={sortMenuRef}>
              <button
                className={`crm-sort-toggle${sortOpen ? ' active' : ''}${sortConfig.key ? ' filtered' : ''}`}
                onClick={() => setSortOpen((open) => !open)}
                aria-expanded={sortOpen}
              >
                Sort <i className="fas fa-chevron-down" />
              </button>
              {sortOpen && (
                <div className="crm-sort-panel">
                  <div className="crm-sort-options-list">
                    {SORT_OPTIONS.map((opt) => (
                      <span
                        key={opt.key}
                        className={`crm-sort-option${sortConfig.key === opt.key ? ' active' : ''}`}
                        onClick={() => toggleSort(opt.key)}
                      >
                        <i className={`${opt.icon} crm-sort-option-icon`} />
                        <span className="crm-sort-option-label">{opt.label}</span>
                        {sortConfig.key === opt.key && (
                          <i
                            className={`fas fa-arrow-${sortConfig.dir === 'asc' ? 'up' : 'down'} crm-sort-dir-icon`}
                          />
                        )}
                      </span>
                    ))}
                  </div>
                  <div className="crm-sort-footer">
                    <button
                      type="button"
                      className="crm-sort-clear-btn"
                      onClick={() => {
                        setSortConfig({ key: '', dir: 'desc' });
                        setSortOpen(false);
                      }}
                      disabled={!sortConfig.key}
                    >
                      <i className="fas fa-times-circle" /> Clear sort
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="crm-toolbar-menu" ref={customerMenuRef}>
              <button
                className={`crm-add-button crm-toolbar-toggle${customerMenuOpen ? ' active' : ''}`}
                onClick={() => setCustomerMenuOpen((open) => !open)}
                aria-expanded={customerMenuOpen}
              >
                <span>+ Customer actions</span>
                <i className="fas fa-chevron-down" />
              </button>
              {customerMenuOpen && (
                <div className="crm-toolbar-menu-panel">
                  {canCreateLead && (
                    <button
                      className="crm-toolbar-menu-item"
                      onClick={() => {
                        setCustomerMenuOpen(false);
                        handleAddClick();
                      }}
                    >
                      <i className="fas fa-user-plus crm-menu-item-icon" />
                      Add Customer
                    </button>
                  )}
                  {canCreateLead && (
                    <button
                      className="crm-toolbar-menu-item"
                      onClick={() => {
                        setCustomerMenuOpen(false);
                        openImportModal();
                      }}
                    >
                      <i className="fas fa-cloud-arrow-down crm-menu-item-icon" />
                      Import from Prospect Finder
                    </button>
                  )}
                  <button
                    className="crm-toolbar-menu-item"
                    onClick={() => {
                      setCustomerMenuOpen(false);
                      handleExport();
                    }}
                    disabled={sortedCards.length === 0}
                  >
                    <i className="fas fa-file-download crm-menu-item-icon" />
                    Export leads
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              className={`crm-archive-toggle${showArchived ? ' active' : ''}`}
              onClick={() => {
                setShowArchived((value) => !value);
                setSelectedRowIds([]);
              }}
              title={showArchived ? 'View active leads' : 'View archived leads'}
            >
              <i className="fas fa-archive" /> {showArchived ? 'Active leads' : 'Archived'}
            </button>
            {(isEffectiveOwner || isEffectiveAdmin) && (
              <button
                type="button"
                className="crm-stage-manage-toolbar-btn"
                onClick={() => setShowStageModal(true)}
                title="Customize and manage pipeline stages"
              >
                <i className="fas fa-sliders-h" /> Manage Stages
              </button>
            )}
          </div>
          <div className="crm-sort-wrap">
            <div className="crm-view-toggle">
              <button
                className={viewMode === 'summary' ? 'active' : ''}
                onClick={() => setViewMode('summary')}
              >
                Summary
              </button>
              <button
                className={viewMode === 'kanban' ? 'active' : ''}
                onClick={() => setViewMode('kanban')}
              >
                Kanban
              </button>
              <button
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
              >
                List
              </button>
            </div>
            <NotificationBell />
          </div>
          {showArchived && (
            <div className="crm-archived-banner">
              <i className="fas fa-archive" />
              <span>
                Viewing archived leads. Select leads and use{' '}
                <strong>{viewMode === 'list' ? 'Restore' : 'the lead detail panel'}</strong> to move
                them back to your pipeline.
              </span>
            </div>
          )}
          {/* Exact Open / Archived Pipeline Stats Row */}
          <div className="crm-open-pipeline">
            <div className="crm-stat-boxes">
              <div className="crm-stat-box">
                <span className="crm-stat-label">
                  {showArchived ? 'Total archived leads' : 'Total active leads'}
                </span>
                <span className="crm-stat-value">{totalActiveLeads}</span>
              </div>
              <div className="crm-stat-box">
                <span className="crm-stat-label">
                  {showArchived ? 'Unassigned archived' : 'Unassigned leads'}
                </span>
                <span className="crm-stat-value">{unassignedLeads}</span>
              </div>
              <div className="crm-stat-box">
                <span className="crm-stat-label">Won leads</span>
                <span className="crm-stat-value">{wonLeads}</span>
              </div>
              <div className="crm-stat-box">
                <span className="crm-stat-label">Lost leads</span>
                <span className="crm-stat-value">{lostLeads}</span>
              </div>
              <div className="crm-stat-box">
                <span className="crm-stat-label">Follow-ups due</span>
                <span className="crm-stat-value">{followUpsDue}</span>
              </div>
              <div className="crm-stat-box">
                <span className="crm-stat-label">Overdue follow-ups</span>
                <span className="crm-stat-value">{overdueFollowUps}</span>
              </div>
            </div>
            <div className="crm-open-pipeline-box">
              <span className="crm-open-pipeline-label">
                {showArchived ? 'Archived pipeline value' : 'Open pipeline value'}
              </span>
              <div className="crm-open-pipeline-bottom">
                <span className="crm-open-pipeline-value">
                  {selectedCurrencySymbol}
                  {totalConvertedOpenValue.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })}
                </span>
                <div className="crm-currency-custom-wrap" ref={currencyDropdownRef}>
                  <button
                    type="button"
                    className={`crm-currency-arrow-btn${showCurrencyDropdown ? ' active' : ''}`}
                    onClick={() => setShowCurrencyDropdown((prev) => !prev)}
                    title={`Current: ${pipelineCurrency} (${selectedCurrencySymbol.trim()}) • Click to change`}
                    aria-label="Select currency"
                    aria-expanded={showCurrencyDropdown}
                  >
                    <i className={`fas fa-chevron-${showCurrencyDropdown ? 'up' : 'down'}`} />
                  </button>

                  {showCurrencyDropdown && (
                    <div className="crm-currency-popover">
                      {/* Top Header with Live FX Badge & Date */}
                      <div className="crm-currency-popover-header">
                        <div className="crm-currency-header-left">
                          <i className="fas fa-coins crm-currency-header-icon" />
                          <span className="crm-currency-popover-title">Currency</span>
                        </div>
                        <span
                          className="crm-currency-latest-badge"
                          title={`Active real-time exchange rates (effective ${fxRateMeta?.date ? formatDate(fxRateMeta.date) : 'today'})`}
                        >
                          <span className="crm-currency-latest-dot" />
                          <span className="crm-currency-latest-text">Live FX</span>
                          <span className="crm-currency-latest-shine" />
                        </span>
                      </div>

                      {/* Quick Search Filter */}
                      <div className="crm-currency-search-wrap">
                        <i className="fas fa-search crm-currency-search-icon" />
                        <input
                          type="text"
                          className="crm-currency-search-input"
                          placeholder="Search currency or country..."
                          value={currencySearch}
                          onChange={(e) => setCurrencySearch(e.target.value)}
                        />
                        {currencySearch && (
                          <button
                            type="button"
                            className="crm-currency-search-clear"
                            onClick={() => setCurrencySearch('')}
                          >
                            <i className="fas fa-times" />
                          </button>
                        )}
                      </div>

                      {/* Filtered Currency List */}
                      <div className="crm-currency-popover-list">
                        {CURRENCIES.filter((curr) => {
                          if (!currencySearch.trim()) return true;
                          const q = currencySearch.toLowerCase().trim();
                          const name = (CURRENCY_NAMES[curr] || '').toLowerCase();
                          const sym = (CURRENCY_SYMBOLS[curr] || '').toLowerCase();
                          return (
                            curr.toLowerCase().includes(q) || name.includes(q) || sym.includes(q)
                          );
                        }).map((curr) => {
                          const isSelected = curr === pipelineCurrency;
                          const symbol = CURRENCY_SYMBOLS[curr]?.trim() || curr;
                          const name = CURRENCY_NAMES[curr] || curr;
                          const countryCode = CURRENCY_COUNTRY_CODES[curr] || 'un';
                          const rateVal = fxRates[curr]
                            ? Number(fxRates[curr]).toFixed(curr === 'JPY' ? 1 : 2)
                            : null;

                          return (
                            <button
                              key={curr}
                              type="button"
                              className={`crm-currency-popover-item${isSelected ? ' selected' : ''}`}
                              onClick={() => {
                                setPipelineCurrency(curr);
                                setShowCurrencyDropdown(false);
                                setCurrencySearch('');
                              }}
                            >
                              <div className="crm-currency-flag-container">
                                <img
                                  src={`https://flagcdn.com/w40/${countryCode}.png`}
                                  srcSet={`https://flagcdn.com/w80/${countryCode}.png 2x`}
                                  alt={name}
                                  className="crm-currency-flag-img"
                                  loading="lazy"
                                />
                              </div>
                              <div className="crm-currency-info">
                                <div className="crm-currency-code-row">
                                  <span className="crm-currency-item-code">{curr}</span>
                                  <span className="crm-currency-item-symbol">{symbol}</span>
                                </div>
                                <span className="crm-currency-item-name">{name}</span>
                              </div>
                              <div className="crm-currency-right-meta">
                                {rateVal && curr !== 'USD' && (
                                  <span
                                    className="crm-currency-rate-badge"
                                    title={`1 USD = ${rateVal} ${curr}`}
                                  >
                                    {rateVal}
                                  </span>
                                )}
                                {isSelected && (
                                  <span className="crm-currency-check-wrap">
                                    <i className="fas fa-check crm-currency-item-check" />
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      {/* Effective Date & Sync Footer */}
                      <div className="crm-currency-popover-footer">
                        <div className="crm-currency-footer-row">
                          <span className="crm-currency-footer-source">
                            <i className="fas fa-exchange-alt" /> Converted from USD base
                          </span>
                          <span className="crm-currency-footer-date">
                            Effective:{' '}
                            <strong>
                              {fxRateMeta?.date ? formatDate(fxRateMeta.date) : 'Latest'}
                            </strong>
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="container">
            {viewMode === 'kanban' &&
              STAGES.map((stage) => {
                const stageCards = sortedCards.filter((card) => card.stage === stage);
                return (
                  <div key={stage} className="kanban-column">
                    <div className="kanban-column-header">
                      <span>{stage}</span>
                      <span className="kanban-column-count">{stageCards.length}</span>
                    </div>
                    <div
                      className={`kanban-column-body${stageCards.length === 0 ? ' kanban-column-body-empty' : ''}`}
                      onDragEnter={(event) => handleDragEnter(event, stage)}
                      onDragOver={(event) => handleDragOver(event, stage)}
                      onDragLeave={(event) => handleDragLeave(event)}
                      onDrop={(event) => handleDrop(event, stage)}
                    >
                      {stageCards.map((card) => {
                        const tasks = Array.isArray(card.tasks)
                          ? card.tasks
                          : Array.isArray(card.crm?.tasks)
                            ? card.crm.tasks
                            : [];
                        const userEmail = keycloak?.tokenParsed?.email;
                        const isOwnerOrManager =
                          isEffectiveOwner || isEffectiveAdmin || isEffectiveSalesManager;
                        const isCardUnassigned =
                          !card.assignee || String(card.assignee).trim() === '';
                        const isOwnedByMe =
                          (card.owner &&
                            currentUsername &&
                            String(card.owner).toLowerCase() ===
                              String(currentUsername).toLowerCase()) ||
                          (card.owner &&
                            userEmail &&
                            String(card.owner).toLowerCase() === String(userEmail).toLowerCase()) ||
                          (card.user_id && String(card.user_id) === String(currentUserId));

                        const isAssignedToMe =
                          card.assignee === currentUserId ||
                          (currentUsername &&
                            String(card.assignee || '').toLowerCase() ===
                              String(currentUsername).toLowerCase()) ||
                          (userEmail &&
                            String(card.assignee || '').toLowerCase() ===
                              String(userEmail).toLowerCase());

                        const hasAssignee = Boolean(
                          card.assignee && String(card.assignee).trim() !== ''
                        );

                        const pendingTasks = tasks.filter((t) => {
                          const status = String(
                            t?.status || (t?.completed ? 'completed' : 'pending')
                          ).toLowerCase();
                          return !['completed', 'cancelled', 'done'].includes(status);
                        });

                        // Check if any pending task is assigned to the current user or scheduled by/under the user
                        const hasMyPendingTask = pendingTasks.some((t) => {
                          if (t.assignee && String(t.assignee).trim() !== '') {
                            return (
                              String(t.assignee) === String(currentUserId) ||
                              (currentUsername &&
                                String(t.assignee).toLowerCase() ===
                                  String(currentUsername).toLowerCase()) ||
                              (userEmail &&
                                String(t.assignee).toLowerCase() ===
                                  String(userEmail).toLowerCase())
                            );
                          }

                          // Unassigned task:
                          const isTaskCreatedByMe =
                            (t.createdBy && String(t.createdBy) === String(currentUserId)) ||
                            (t.createdByName &&
                              currentUsername &&
                              String(t.createdByName).toLowerCase() ===
                                String(currentUsername).toLowerCase());

                          // Owner: sees own unassigned tasks AND manager-scheduled tasks
                          if (isEffectiveOwner) {
                            return hasAssignee
                              ? isAssignedToMe
                              : isOwnedByMe || isTaskCreatedByMe || true;
                          }

                          // Manager: sees own scheduled tasks and assigned tasks, but NOT owner's unassigned tasks
                          if (isEffectiveSalesManager || isEffectiveAdmin) {
                            if (isTaskCreatedByMe) return true;
                            return hasAssignee ? isAssignedToMe : isOwnedByMe;
                          }

                          // Task has no specific assignee: inherits card assignee if assigned, or owner if unassigned
                          return hasAssignee ? isAssignedToMe : isOwnedByMe;
                        });

                        // If assigned to another person, ONLY that assigned person gets the colored border (owner does not)
                        const isMyTaskOrLead =
                          hasMyPendingTask || (hasAssignee ? isAssignedToMe : isOwnedByMe);

                        // Label/highlight card with blue line ONLY for Owner/Manager when lead is unassigned and owned by this account
                        const shouldHighlight = isOwnerOrManager && isCardUnassigned && isOwnedByMe;
                        const followUpDate = getCardFollowUpDate(card);
                        const isOverdue = isFollowUpOverdue(followUpDate);
                        const isDueSoon = isFollowUpDueSoon(followUpDate);
                        const remainingHours = getFollowUpRemainingHours(followUpDate);
                        const followUpText = formatDateShort(followUpDate);
                        const hasPendingTasks = pendingTasks.length > 0;
                        const calculatedScore = calculateLeadScore(card, customStages).totalScore;
                        const score = Number(calculatedScore ?? card.leadScore ?? 0);
                        const { name: coName, logo: coLogo } = getCompany(card);

                        const closeDateInfo = getCardCloseDateInfo(card);
                        const isCloseOverdue = closeDateInfo?.status === 'overdue';
                        const isCloseDueSoon =
                          closeDateInfo?.status === 'due-soon' ||
                          closeDateInfo?.status === 'due-today';
                        const showOverdueBorder = (isCloseOverdue || isOverdue) && isMyTaskOrLead;
                        const showDueSoonBorder =
                          !showOverdueBorder && (isCloseDueSoon || isDueSoon) && isMyTaskOrLead;

                        return (
                          <div
                            key={card.id}
                            className={`kanban-card${shouldHighlight ? ' kanban-card-assigned' : ''}${showOverdueBorder ? ' card-overdue' : showDueSoonBorder ? ' card-due-soon' : ''}`}
                            draggable={canEditCard(card)}
                            onClick={() => {
                              if (!isDraggingRef.current) {
                                handleCardClick(card);
                              }
                            }}
                            onDragStart={(event) => handleDragStart(event, card.id)}
                            onDragEnd={handleDragEnd}
                          >
                            {/* Card Top Row: Dedicated to Expected Close Date (Left) & Lead Score (Right) */}
                            <div className="kanban-card-top">
                              {closeDateInfo ? (
                                <div
                                  className={`kanban-card-followup close-date ${closeDateInfo.status}`}
                                  title={closeDateInfo.tooltip}
                                >
                                  <i className={closeDateInfo.icon} />
                                  <span>{closeDateInfo.text}</span>
                                </div>
                              ) : (
                                <div
                                  className="kanban-card-followup close-date no-date"
                                  title="No expected close date scheduled"
                                >
                                  <i className="fas fa-calendar-plus" />
                                  <span>No close date</span>
                                </div>
                              )}
                              <span
                                className={`kanban-card-score-badge ${
                                  score >= 70
                                    ? 'score-high'
                                    : score >= 40
                                      ? 'score-med'
                                      : 'score-low'
                                }`}
                                title={`Lead Score: ${score}/100`}
                              >
                                <i className="fas fa-star" />
                                <span>{score}</span>
                              </span>
                              {Object.keys(card.field_locks || {}).length > 0 && (
                                <span
                                  className="kanban-card-lock-badge"
                                  title={`${Object.keys(card.field_locks).length} field(s) manually verified & locked`}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 3,
                                    fontSize: 10,
                                    padding: '2px 6px',
                                    borderRadius: 6,
                                    background: '#fef3c7',
                                    color: '#92400e',
                                    border: '1px solid #f59e0b',
                                    fontWeight: 600,
                                    marginLeft: 4,
                                  }}
                                >
                                  <i className="fas fa-lock" style={{ fontSize: 9 }} />
                                  <span>{Object.keys(card.field_locks).length}</span>
                                </span>
                              )}
                            </div>

                            {/* Card Main: Contact Avatar + Name, Position, and Company */}
                            <div className="kanban-card-main">
                              <div className="kanban-card-main-identity">
                                <ContactAvatar name={card.name} size={32} />
                                <div className="kanban-card-main-info">
                                  <p className="kanban-card-title">{card.name || 'Unnamed lead'}</p>
                                  {card.jobTitle && (
                                    <p className="kanban-card-position" title={card.jobTitle}>
                                      {card.jobTitle}
                                    </p>
                                  )}
                                  {coName && coName !== 'Unknown' && (
                                    <div className="kanban-card-company-row" title={coName}>
                                      <CompanyMark name={coName} logoUrl={coLogo} size={18} />
                                      <span className="kanban-card-company-name">{coName}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Card Footer: Assigned Person (Left), Follow-up Task Status (Middle), and Deal Value (Right) */}
                            <div className="kanban-card-meta">
                              <span
                                className="kanban-card-owner"
                                title={
                                  card.assignee
                                    ? `Assigned to: ${getCardPersonName(card)}`
                                    : `Owner: ${card.owner || 'Unassigned'}`
                                }
                              >
                                <i
                                  className={`fas ${
                                    card.assignee ? 'fa-user-check' : 'fa-user'
                                  } kanban-card-user-icon`}
                                />
                                <span>{getCardPersonName(card)}</span>
                              </span>

                              {/* Dedicated Follow-up Task Status Tag */}
                              {followUpText ? (
                                <span
                                  className={`kanban-card-meta-task ${isOverdue ? 'overdue' : isDueSoon ? 'due-soon' : 'normal'}`}
                                  title={
                                    isOverdue
                                      ? `⚠️ Overdue Follow-up (${formatDate(followUpDate)})`
                                      : isDueSoon
                                        ? `⏰ Reminder: Due within 24 hours (${formatDate(followUpDate)})${remainingHours !== null && remainingHours <= 24 ? ` • ~${remainingHours}h left` : ''}`
                                        : `📅 Next Follow-up: ${formatDate(followUpDate)}`
                                  }
                                >
                                  <i
                                    className={
                                      isOverdue
                                        ? 'fas fa-exclamation-triangle'
                                        : isDueSoon
                                          ? 'fas fa-bell'
                                          : 'fas fa-calendar-alt'
                                    }
                                  />
                                  <span>
                                    {isOverdue
                                      ? `Overdue ${followUpText}`
                                      : isDueSoon
                                        ? remainingHours !== null &&
                                          remainingHours > 0 &&
                                          remainingHours <= 24
                                          ? `Due ${remainingHours}h`
                                          : `Due today`
                                        : `Due ${followUpText}`}
                                  </span>
                                </span>
                              ) : hasPendingTasks ? (
                                <span
                                  className="kanban-card-meta-task has-task-no-date"
                                  title={`📋 ${pendingTasks.length} task${pendingTasks.length > 1 ? 's' : ''} pending without due date.`}
                                >
                                  <i className="fas fa-clipboard-list" />
                                  <span>Task: No date</span>
                                </span>
                              ) : (
                                <span
                                  className="kanban-card-meta-task no-date"
                                  title="No follow-up date scheduled"
                                >
                                  <i className="fas fa-calendar-check" />
                                  <span>No follow-up</span>
                                </span>
                              )}

                              <span
                                className={`kanban-card-deal-value${Number(card.dealValue) > 0 ? ' has-value' : ''}`}
                                title={`Deal Value: ${formatCurrency(card.dealValue, card.currency)}`}
                              >
                                {formatCurrency(card.dealValue, card.currency) || '-'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

            {viewMode === 'list' && (
              <div className="crm-table-wrap">
                {selectedRowIds.length > 0 && (
                  <div className="crm-table-selection-bar">
                    <span className="crm-table-selection-count">
                      {selectedRowIds.length} selected
                    </span>
                    <button type="button" className="crm-modal-cancel" onClick={clearRowSelection}>
                      Clear
                    </button>
                    {canAssignLead && (
                      <button
                        type="button"
                        className="crm-table-bulk-btn"
                        onClick={() => {
                          setBulkAssignTo('');
                          setPendingBulkAssign(true);
                        }}
                      >
                        <i className="fas fa-user-plus" /> Assign to
                      </button>
                    )}
                    {canEdit && (
                      <button
                        type="button"
                        className="crm-table-bulk-btn"
                        onClick={() => {
                          setBulkStage('');
                          setPendingBulkStage(true);
                        }}
                      >
                        <i className="fas fa-tag" /> Change stage
                      </button>
                    )}
                    {canEdit && (
                      <button
                        type="button"
                        className="crm-table-bulk-btn"
                        onClick={() => {
                          setBulkFollowUpDate('');
                          setPendingBulkFollowUp(true);
                        }}
                      >
                        <i className="fas fa-calendar-plus" /> Set follow-up
                      </button>
                    )}
                    <button
                      type="button"
                      className="crm-table-bulk-btn"
                      onClick={handleExportSelected}
                    >
                      <i className="fas fa-file-csv" /> Export selected
                    </button>
                    <span className="crm-table-selection-bar-spacer" />
                    {canEdit && (
                      <button
                        type="button"
                        className="crm-table-bulk-btn"
                        onClick={() => handleBulkArchive(!showArchived)}
                      >
                        <i className="fas fa-archive" /> {showArchived ? 'Restore' : 'Archive'}
                      </button>
                    )}
                    {canDeleteLead && (
                      <button
                        type="button"
                        className="crm-table-bulk-btn crm-table-bulk-btn-danger"
                        onClick={() => setPendingBulkDelete(true)}
                        title={`Delete ${selectedRowIds.length} selected lead${selectedRowIds.length > 1 ? 's' : ''}`}
                      >
                        <i className="fas fa-trash-alt" /> Delete selected
                      </button>
                    )}
                  </div>
                )}
                <div className="crm-table-scroll">
                  <table className="crm-table">
                    <thead>
                      <tr>
                        <th className="crm-table-checkbox-cell">
                          <input
                            type="checkbox"
                            checked={allPageSelected}
                            onChange={toggleSelectAllPage}
                            aria-label="Select all rows on this page"
                          />
                        </th>
                        <th>Contact</th>
                        <th>Company</th>
                        <th>Stage</th>
                        <th>Lead score</th>
                        <th>Owner</th>
                        <th>Deal value</th>
                        <th>Last activity</th>
                        <th>Next follow-up</th>
                        <th>Source</th>
                        <th>Updated date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCards.length === 0 ? (
                        <tr>
                          <td colSpan="12" className="crm-table-empty">
                            No customers match the current filters.
                          </td>
                        </tr>
                      ) : (
                        paginatedCards.map((card) => {
                          const isSelected = selectedRowIds.includes(String(card.id));
                          return (
                            <tr
                              key={card.id}
                              className={isSelected ? 'crm-table-row-selected' : ''}
                              onClick={() => handleCardClick(card)}
                            >
                              <td className="crm-table-checkbox-cell">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(event) => {
                                    event.stopPropagation();
                                    toggleRowSelection(card.id);
                                  }}
                                  onClick={(event) => event.stopPropagation()}
                                  aria-label={`Select ${card.name || 'lead'}`}
                                />
                              </td>
                              <td>
                                <div className="crm-table-contact">
                                  <span className="crm-table-name">
                                    {card.name || 'Unnamed lead'}
                                  </span>
                                  {card.email && (
                                    <span className="crm-table-email">
                                      {card.email}
                                      {Array.isArray(card.emails) &&
                                        card.emails.filter(Boolean).length > 1 && (
                                          <span
                                            className="crm-contact-plus-badge"
                                            title={card.emails.filter(Boolean).join(', ')}
                                          >
                                            +{card.emails.filter(Boolean).length - 1}
                                          </span>
                                        )}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>{card.company || '-'}</td>
                              <td>
                                <span className="crm-status-badge">{card.stage || 'New'}</span>
                              </td>
                              <td>
                                {calculateLeadScore(card, customStages).totalScore ??
                                  card.leadScore ??
                                  '-'}
                              </td>
                              <td>{getCardPersonName(card)}</td>
                              <td>{formatCurrency(card.dealValue, card.currency) || '-'}</td>
                              <td>{formatDate(card.lastActivityDate)}</td>
                              <td>{formatDate(getCardFollowUpDate(card))}</td>
                              <td>{card.source || '-'}</td>
                              <td>{formatDate(card.updatedDate)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="crm-table-action"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleCardClick(card);
                                  }}
                                >
                                  View
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                {paginatedCards.length > 0 && (
                  <div className="crm-table-pagination">
                    <span className="crm-table-page-info">
                      Showing {pageStart + 1}–{pageStart + paginatedCards.length} of{' '}
                      {sortedCards.length}
                    </span>
                    <div className="crm-table-page-size">
                      <label htmlFor="crmPageSize">Rows per page</label>
                      <select
                        id="crmPageSize"
                        value={pageSize}
                        onChange={(event) => {
                          setPageSize(Number(event.target.value));
                          setTablePage(1);
                        }}
                      >
                        {[10, 20, 50].map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="crm-table-page-controls">
                      <button
                        type="button"
                        className="crm-table-page-btn"
                        disabled={effectivePage <= 1}
                        onClick={() => setTablePage((p) => Math.max(1, p - 1))}
                      >
                        ‹ Prev
                      </button>
                      {renderPageNumbers().map((page, idx) =>
                        page === '…' ? (
                          <span key={`ellipsis-${idx}`} className="crm-table-page-ellipsis">
                            …
                          </span>
                        ) : (
                          <button
                            key={page}
                            type="button"
                            className={`crm-table-page-btn${page === effectivePage ? ' active' : ''}`}
                            onClick={() => setTablePage(page)}
                          >
                            {page}
                          </button>
                        )
                      )}
                      <button
                        type="button"
                        className="crm-table-page-btn"
                        disabled={effectivePage >= pageCount}
                        onClick={() => setTablePage((p) => Math.min(pageCount, p + 1))}
                      >
                        Next ›
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {viewMode === 'summary' && (
              <div className="crm-summary-dashboard">
                {/* Charts & Insights Grid */}
                <div className="crm-summary-charts-grid">
                  {/* Left Column: Pipeline Funnel & Stage Breakdown */}
                  <div className="crm-summary-card crm-stage-funnel-card">
                    <div className="crm-summary-card-header">
                      <div>
                        <h4 className="crm-summary-card-title">
                          {showArchived
                            ? 'Archived Stage Distribution'
                            : 'Pipeline Stage Distribution'}
                        </h4>
                        <p className="crm-summary-card-desc">
                          {showArchived
                            ? 'Lead count and deal value across each stage for archived leads'
                            : 'Lead count and deal value across each stage'}
                        </p>
                      </div>
                      <div className="crm-funnel-total-pill">
                        {currentModeCards.length} {showArchived ? 'Archived Leads' : 'Total Leads'}
                      </div>
                    </div>

                    <div className="crm-stage-funnel-list">
                      {stageStats.map(({ stage, count, value, pct }) => (
                        <div
                          key={stage}
                          className={`crm-funnel-row${count === 0 ? ' crm-stage-empty' : ''}`}
                        >
                          <div className="crm-funnel-stage-info">
                            <span className="crm-funnel-stage-name">
                              <span className={`crm-stage-dot stage-${stage.toLowerCase()}`} />
                              {stage}
                            </span>
                            <div className="crm-funnel-stage-numbers">
                              <span className="crm-funnel-count">
                                {count} {count === 1 ? 'lead' : 'leads'} ({pct}%)
                              </span>
                              <span className="crm-funnel-value">
                                {selectedCurrencySymbol}
                                {value.toLocaleString()}
                              </span>
                            </div>
                          </div>
                          <div className="crm-funnel-bar-track">
                            <div
                              className={`crm-funnel-bar-fill stage-bg-${stage.toLowerCase()}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right Column: Lead Quality & Source Attribution */}
                  <div className="crm-summary-side-charts">
                    {/* Lead Score Health */}
                    <div className="crm-summary-card">
                      <div className="crm-summary-card-header">
                        <div>
                          <h4 className="crm-summary-card-title">Lead Quality Distribution</h4>
                          <p className="crm-summary-card-desc">
                            AI propensity and engagement score tiers
                          </p>
                        </div>
                      </div>

                      <div className="crm-lead-quality-meter">
                        <div className="crm-quality-segments">
                          <div
                            className="crm-quality-segment crm-seg-high"
                            style={{ width: `${highQualityPct}%` }}
                            title={`High: ${highQualityLeads} leads (${highQualityPct}%)`}
                          />
                          <div
                            className="crm-quality-segment crm-seg-med"
                            style={{ width: `${mediumQualityPct}%` }}
                            title={`Medium: ${mediumQualityLeads} leads (${mediumQualityPct}%)`}
                          />
                          <div
                            className="crm-quality-segment crm-seg-low"
                            style={{ width: `${lowQualityPct}%` }}
                            title={`Low: ${lowQualityLeads} leads (${lowQualityPct}%)`}
                          />
                        </div>
                        <div className="crm-quality-legend">
                          <div className="crm-legend-item">
                            <span className="crm-legend-dot crm-dot-high" />
                            <span>
                              High (70–100): <strong>{highQualityLeads}</strong> ({highQualityPct}%)
                            </span>
                          </div>
                          <div className="crm-legend-item">
                            <span className="crm-legend-dot crm-dot-med" />
                            <span>
                              Medium (40–69): <strong>{mediumQualityLeads}</strong> (
                              {mediumQualityPct}%)
                            </span>
                          </div>
                          <div className="crm-legend-item">
                            <span className="crm-legend-dot crm-dot-low" />
                            <span>
                              Low (&lt;40): <strong>{lowQualityLeads}</strong> ({lowQualityPct}%)
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Top Lead Sources */}
                    <div className="crm-summary-card">
                      <div className="crm-summary-card-header">
                        <div>
                          <h4 className="crm-summary-card-title">Top Acquisition Sources</h4>
                          <p className="crm-summary-card-desc">
                            Where active pipeline leads originate
                          </p>
                        </div>
                      </div>

                      <div className="crm-sources-list">
                        {sourceStats.map(({ source, count, pct }) => (
                          <div key={source} className="crm-source-item">
                            <div className="crm-source-header">
                              <span className="crm-source-name">{source}</span>
                              <span className="crm-source-count">
                                {count} leads ({pct}%)
                              </span>
                            </div>
                            <div className="crm-source-track">
                              <div
                                className="crm-source-fill"
                                style={{ width: `${Math.max(pct, 4)}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {showFormModal && (
        <div className="crm-modal-overlay" onMouseDown={backdropDismiss(closeModal)}>
          <div
            className={`crm-modal ${selectedCard ? 'crm-detail-modal' : 'crm-add-lead-modal'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {selectedCard ? (
              <div className="crm-detail-content">
                <div className="crm-modal-header crm-detail-header">
                  <div className="crm-detail-header-left">
                    <div className="crm-avatar">
                      {(formData.name || selectedCard.name || 'U')
                        .split(' ')
                        .map((n) => n[0])
                        .slice(0, 2)
                        .join('')
                        .toUpperCase()}
                    </div>
                    <div className="crm-detail-header-info">
                      <p className="crm-detail-breadcrumb">Contact details</p>
                      <h3>{formData.name || selectedCard.name}</h3>
                      <p className="crm-detail-subtitle">
                        {formData.jobTitle || selectedCard.jobTitle} ·{' '}
                        {formData.company || selectedCard.company}
                      </p>
                    </div>
                  </div>
                  <div className="crm-detail-header-right">
                    {(() => {
                      const currentScoreData = calculateLeadScore(formData, customStages);
                      const { breakdown, totalScore } = currentScoreData;
                      return (
                        <div className="crm-score-badge-wrap">
                          <span className="crm-score-badge" title="Automated lead score (0 - 100)">
                            <i className="fas fa-star" />
                            <span className="crm-score-value-text">{totalScore}</span>
                          </span>
                          {/* Breakdown info popover / tooltip */}
                          <div className="crm-score-breakdown-popover">
                            <div className="crm-score-popover-header">
                              <span>Lead Score Breakdown</span>
                              <strong>{totalScore} / 100</strong>
                            </div>
                            <div className="crm-score-popover-body">
                              <div className="crm-score-row">
                                <span>🏷️ Stage ({formData.stage || 'New'})</span>
                                <strong>+{breakdown.stageScore || 0} pts</strong>
                              </div>
                              <div className="crm-score-row">
                                <span>👔 Seniority ({breakdown.seniorityTitle || 'None'})</span>
                                <strong>+{breakdown.seniorityScore || 0} pts</strong>
                              </div>
                              <div className="crm-score-row">
                                <span>
                                  📋 Tasks ({breakdown.completedCount || 0} completed
                                  {breakdown.hasInProgressTask
                                    ? ', in progress'
                                    : breakdown.activeFollowUpPoints > 0
                                      ? ', follow-up set'
                                      : ''}
                                  {breakdown.timelyFollowUpPoints > 0 ? ', timely' : ''})
                                </span>
                                <strong>+{breakdown.taskScore || 0} pts</strong>
                              </div>
                              <div className="crm-score-row">
                                <span>
                                  📇 Contact Data (
                                  {[
                                    breakdown.hasEmail ? 'Email' : null,
                                    breakdown.hasPhone ? 'Phone' : null,
                                    breakdown.hasLinkedin ? 'LinkedIn' : null,
                                  ]
                                    .filter(Boolean)
                                    .join(', ') || 'None'}
                                  )
                                </span>
                                <strong>+{breakdown.contactScore || 0} pts</strong>
                              </div>
                              <div className="crm-score-row">
                                <span>
                                  💼 Commercial Intent (
                                  {[
                                    breakdown.hasDealValue ? 'Deal Value' : null,
                                    breakdown.hasProductInterest ? 'Product Interest' : null,
                                  ]
                                    .filter(Boolean)
                                    .join(', ') || 'None'}
                                  )
                                </span>
                                <strong>+{breakdown.intentScore || 0} pts</strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    <span className="crm-status-badge">
                      {formData.stage || selectedCard.stage || 'New'}
                    </span>
                    <button
                      type="button"
                      className="crm-modal-close"
                      onClick={closeModal}
                      aria-label="Close"
                    >
                      <i className="fas fa-times" />
                    </button>
                  </div>
                </div>
                <div className="crm-detail-actions">
                  {!['Won', 'Lost'].includes(formData.stage || selectedCard.stage || 'New') && (
                    <>
                      <button
                        className="crm-detail-action crm-detail-action-success"
                        type="button"
                        disabled={!canMarkWon || !canEditCard(selectedCard)}
                        onClick={() => openWonModal(selectedCard)}
                      >
                        Mark as won
                      </button>
                      <button
                        className="crm-detail-action crm-detail-action-danger"
                        type="button"
                        disabled={!canEditCard(selectedCard)}
                        onClick={() => openLostModal(selectedCard)}
                      >
                        Mark as lost
                      </button>
                    </>
                  )}
                  {formData.isArchived || selectedCard.isArchived ? (
                    <button
                      className="crm-detail-action crm-detail-action-restore"
                      type="button"
                      disabled={!canEditCard(selectedCard)}
                      onClick={() => handleArchiveCard(selectedCard.id, false)}
                    >
                      <i className="fas fa-undo" /> Restore lead
                    </button>
                  ) : (
                    <button
                      className="crm-detail-action crm-detail-action-archive"
                      type="button"
                      disabled={!canEditCard(selectedCard)}
                      onClick={() => handleArchiveCard(selectedCard.id, true)}
                    >
                      <i className="fas fa-archive" /> Archive lead
                    </button>
                  )}
                </div>
                <div className="crm-detail-tabs">
                  <button
                    className={`crm-detail-tab${activeDetailTab === 'details' ? ' active' : ''}`}
                    type="button"
                    onClick={() => setActiveDetailTab('details')}
                  >
                    Details
                  </button>
                  <button
                    className={`crm-detail-tab${activeDetailTab === 'activity' ? ' active' : ''}`}
                    type="button"
                    onClick={() => setActiveDetailTab('activity')}
                  >
                    Activity
                  </button>
                  <button
                    className={`crm-detail-tab${activeDetailTab === 'meetings' ? ' active' : ''}`}
                    type="button"
                    onClick={handleOpenMeetingsTab}
                  >
                    Meetings
                    {categorizedLeadMeetings.upcoming.length +
                      categorizedLeadMeetings.in_progress.length >
                      0 && (
                      <span className="crm-detail-tab-badge">
                        {categorizedLeadMeetings.upcoming.length +
                          categorizedLeadMeetings.in_progress.length}
                      </span>
                    )}
                  </button>
                  <button
                    className={`crm-detail-tab${activeDetailTab === 'evidence' ? ' active' : ''}`}
                    type="button"
                    onClick={() => handleOpenEvidenceTab('fields')}
                    title="View Field Evidence, Locks & Conflicts"
                  >
                    Evidence & Locks
                    {Object.keys(selectedCard?.field_locks || {}).length > 0 && (
                      <span className="crm-detail-tab-badge">
                        {Object.keys(selectedCard.field_locks).length}
                      </span>
                    )}
                  </button>
                </div>
                {activeDetailTab === 'details' ? (
                  <form
                    className="crm-detail-form"
                    onSubmit={handleFormSubmit}
                    onKeyDown={(e) => {
                      if (
                        e.key === 'Enter' &&
                        e.target.tagName !== 'TEXTAREA' &&
                        e.target.type !== 'submit'
                      ) {
                        e.preventDefault();
                      }
                    }}
                  >
                    <fieldset disabled={!canEditCard(selectedCard)} className="crm-detail-fields">
                      <div className="crm-detail-section">
                        {(formData.stage || selectedCard.stage || 'New') === 'Won' && (
                          <div className="crm-detail-won">
                            <span className="crm-won-badge">Won</span>
                            {(formData.wonValue || selectedCard.wonValue) && (
                              <p className="crm-won-reason">
                                {formatCurrency(
                                  formData.wonValue || selectedCard.wonValue,
                                  formData.currency || selectedCard.currency
                                )}
                              </p>
                            )}
                            {(formData.wonDate || selectedCard.wonDate) && (
                              <p className="crm-won-context">
                                Won on {formData.wonDate || selectedCard.wonDate}
                              </p>
                            )}
                            {(formData.wonAt || selectedCard.wonAt) && (
                              <p className="crm-won-context">
                                Won timestamp:{' '}
                                {new Date(formData.wonAt || selectedCard.wonAt).toLocaleString()}
                              </p>
                            )}
                          </div>
                        )}
                        {(formData.stage || selectedCard.stage || 'New') === 'Lost' && (
                          <div className="crm-detail-lost">
                            <span className="crm-lost-badge">Lost</span>
                            {formData.lostReason || selectedCard.lostReason ? (
                              <p className="crm-lost-reason">
                                {formData.lostReason || selectedCard.lostReason}
                              </p>
                            ) : (
                              <p className="crm-lost-reason">No reason provided</p>
                            )}
                            {(formData.lostContext || selectedCard.lostContext) && (
                              <p className="crm-lost-context">
                                {formData.lostContext || selectedCard.lostContext}
                              </p>
                            )}
                            {(formData.lostDate || selectedCard.lostDate) && (
                              <p className="crm-lost-context">
                                Lost on {formData.lostDate || selectedCard.lostDate}
                              </p>
                            )}
                            {(formData.lostAt || selectedCard.lostAt) && (
                              <p className="crm-lost-context">
                                Lost at{' '}
                                {new Date(formData.lostAt || selectedCard.lostAt).toLocaleString()}
                              </p>
                            )}
                          </div>
                        )}
                        <h4>Contact & company</h4>
                        <div className="crm-detail-grid">
                          <label>
                            <span>
                              Full name <span className="crm-required-star">*</span>
                              {isFieldLocked('name') && (
                                <i
                                  className="fas fa-lock"
                                  style={{
                                    marginLeft: 6,
                                    color: '#94a3b8',
                                    fontSize: 12,
                                    cursor: 'help',
                                  }}
                                  title="Field is locked in Evidence & Locks. Unlock it there to modify."
                                />
                              )}
                            </span>
                            {errors.name && <em className="crm-field-error">{errors.name}</em>}
                            <input
                              value={formData.name}
                              disabled={!canEditCard(selectedCard) || isFieldLocked('name')}
                              onChange={(e) => updateFormField('name', e.target.value)}
                              title={
                                isFieldLocked('name')
                                  ? 'Field is locked in Evidence & Locks. Unlock it there to modify.'
                                  : ''
                              }
                            />
                          </label>
                          <label>
                            <span>
                              Title
                              {isFieldLocked('jobTitle') && (
                                <i
                                  className="fas fa-lock"
                                  style={{
                                    marginLeft: 6,
                                    color: '#94a3b8',
                                    fontSize: 12,
                                    cursor: 'help',
                                  }}
                                  title="Field is locked in Evidence & Locks. Unlock it there to modify."
                                />
                              )}
                            </span>
                            {errors.jobTitle && (
                              <em className="crm-field-error">{errors.jobTitle}</em>
                            )}
                            <input
                              value={formData.jobTitle}
                              disabled={!canEditCard(selectedCard) || isFieldLocked('jobTitle')}
                              onChange={(e) => updateFormField('jobTitle', e.target.value)}
                              title={
                                isFieldLocked('jobTitle')
                                  ? 'Field is locked in Evidence & Locks. Unlock it there to modify.'
                                  : ''
                              }
                            />
                          </label>
                          {(() => {
                            const origCompany = String(
                              selectedCard?.company ||
                                selectedCard?.company_name ||
                                selectedCard?.companyName ||
                                ''
                            ).trim();
                            const origWebsite = String(
                              selectedCard?.companyWebsite ||
                                selectedCard?.company_website ||
                                selectedCard?.website ||
                                ''
                            ).trim();
                            const curCompany = String(formData.company || '').trim();
                            const curWebsite = String(formData.companyWebsite || '').trim();
                            const isCompanyChanged = Boolean(
                              selectedCard &&
                              origCompany &&
                              origWebsite &&
                              curCompany.toLowerCase() !== origCompany.toLowerCase()
                            );

                            return (
                              <>
                                <label>
                                  <span>
                                    Company <span className="crm-required-star">*</span>
                                    {isFieldLocked('company') && (
                                      <i
                                        className="fas fa-lock"
                                        style={{
                                          marginLeft: 6,
                                          color: '#94a3b8',
                                          fontSize: 12,
                                          cursor: 'help',
                                        }}
                                        title="Field is locked in Evidence & Locks. Unlock it there to modify."
                                      />
                                    )}
                                  </span>
                                  {errors.company && (
                                    <em className="crm-field-error">{errors.company}</em>
                                  )}
                                  <input
                                    value={formData.company}
                                    disabled={
                                      !canEditCard(selectedCard) || isFieldLocked('company')
                                    }
                                    onChange={(e) => updateFormField('company', e.target.value)}
                                    title={
                                      isFieldLocked('company')
                                        ? 'Field is locked in Evidence & Locks. Unlock it there to modify.'
                                        : ''
                                    }
                                  />
                                </label>
                                <label>
                                  <span>Industry</span>
                                  <input
                                    type="text"
                                    placeholder="e.g. SaaS"
                                    value={formData.industry}
                                    disabled={!canEditCard(selectedCard)}
                                    onChange={(e) => updateFormField('industry', e.target.value)}
                                  />
                                </label>
                                <label>
                                  <span>
                                    LinkedIn URL{' '}
                                    {formData.source === 'LinkedIn' && (
                                      <span className="crm-required-star">*</span>
                                    )}
                                  </span>
                                  {errors.linkedinUrl && (
                                    <em className="crm-field-error">{errors.linkedinUrl}</em>
                                  )}
                                  <input
                                    type="text"
                                    placeholder="https://linkedin.com/in/name"
                                    value={formData.linkedinUrl}
                                    disabled={!canEditCard(selectedCard)}
                                    onChange={(e) => updateFormField('linkedinUrl', e.target.value)}
                                  />
                                </label>
                                <label>
                                  <span>
                                    Company website{' '}
                                    {(formData.source === 'Website' || isCompanyChanged) && (
                                      <span className="crm-required-star">*</span>
                                    )}
                                    {isCompanyChanged && (
                                      <small
                                        style={{
                                          color: '#0284c7',
                                          marginLeft: '6px',
                                          fontWeight: 500,
                                        }}
                                      >
                                        (required for new company)
                                      </small>
                                    )}
                                  </span>
                                  {errors.companyWebsite && (
                                    <em className="crm-field-error">{errors.companyWebsite}</em>
                                  )}
                                  <input
                                    type="text"
                                    placeholder="https://company.com"
                                    value={formData.companyWebsite}
                                    disabled={!canEditCard(selectedCard)}
                                    onChange={(e) =>
                                      updateFormField('companyWebsite', e.target.value)
                                    }
                                    onBlur={(event) => {
                                      const v = event.target.value.trim();
                                      if (!v) return;

                                      const withProtocol = /^https?:\/\//i.test(v)
                                        ? v
                                        : `https://${v}`;
                                      const domain = v
                                        .replace(/^https?:\/\//i, '')
                                        .replace(/^www\./i, '')
                                        .split('/')[0]
                                        .toLowerCase();

                                      updateFormField('companyWebsite', withProtocol);
                                      updateFormField('companyDomain', domain);
                                    }}
                                  />
                                </label>
                              </>
                            );
                          })()}
                          <label className="crm-detail-fullwidth">
                            <span>
                              Email{' '}
                              {formData.source === 'Email' && (
                                <span className="crm-required-star">*</span>
                              )}
                              {isFieldLocked('email') && (
                                <i
                                  className="fas fa-lock"
                                  style={{
                                    marginLeft: 6,
                                    color: '#94a3b8',
                                    fontSize: 12,
                                    cursor: 'help',
                                  }}
                                  title="Field is locked in Evidence & Locks. Unlock it there to modify."
                                />
                              )}
                            </span>
                            <MultiEmailField
                              emails={formData.emails || [formData.email || '']}
                              onChange={(next) => updateFormField('emails', next)}
                              errors={errors}
                              disabled={!canEditCard(selectedCard) || isFieldLocked('email')}
                            />
                          </label>
                          <label className="crm-detail-fullwidth">
                            <span>
                              Phone & country
                              {isFieldLocked('phone') && (
                                <i
                                  className="fas fa-lock"
                                  style={{
                                    marginLeft: 6,
                                    color: '#94a3b8',
                                    fontSize: 12,
                                    cursor: 'help',
                                  }}
                                  title="Field is locked in Evidence & Locks. Unlock it there to modify."
                                />
                              )}
                            </span>
                            <MultiCountryPhoneField
                              phones={formData.phones || [formData.phone || '']}
                              country={formData.country}
                              onChange={(next) => updateFormField('phones', next)}
                              onCountryChange={(v) => updateFormField('country', v)}
                              errors={errors}
                              disabled={!canEditCard(selectedCard) || isFieldLocked('phone')}
                            />
                          </label>
                        </div>
                      </div>
                      <div className="crm-detail-section">
                        <h4>Identifiers</h4>
                        <div className="crm-detail-grid crm-detail-grid-two">
                          <label>
                            <span>
                              Source
                              {(isProspectFinderLead(selectedCard) ||
                                isLandingPageLead(selectedCard)) && (
                                <i
                                  className="fas fa-lock"
                                  style={{
                                    marginLeft: 6,
                                    color: '#94a3b8',
                                    fontSize: 12,
                                    cursor: 'help',
                                  }}
                                  title={
                                    isLandingPageLead(selectedCard)
                                      ? 'Source is locked because this lead was captured from a landing page.'
                                      : 'Source is locked because this lead was imported from Prospect Finder.'
                                  }
                                />
                              )}
                            </span>
                            <select
                              value={
                                isLandingPageLead(selectedCard)
                                  ? 'Landing Page'
                                  : isProspectFinderLead(selectedCard)
                                    ? 'Prospect Finder'
                                    : normalizeSource(formData.source)
                              }
                              disabled={
                                isLandingPageLead(selectedCard) ||
                                isProspectFinderLead(selectedCard) ||
                                !canEditCard(selectedCard)
                              }
                              onChange={(e) => updateFormField('source', e.target.value)}
                              title={
                                isLandingPageLead(selectedCard)
                                  ? 'Source is locked for leads captured from a landing page'
                                  : isProspectFinderLead(selectedCard)
                                    ? 'Source is locked for leads imported from Prospect Finder'
                                    : undefined
                              }
                            >
                              {isLandingPageLead(selectedCard) ? (
                                <option value="Landing Page">Landing Page</option>
                              ) : isProspectFinderLead(selectedCard) ? (
                                <option value="Prospect Finder">Prospect Finder</option>
                              ) : (
                                <>
                                  <option value="Website">Website</option>
                                  <option value="LinkedIn">LinkedIn</option>
                                  <option value="Email">Email</option>
                                </>
                              )}
                            </select>
                          </label>
                        </div>
                      </div>
                      <div className="crm-detail-section">
                        <h4>Ownership & stage</h4>
                        <div className="crm-detail-grid crm-detail-grid-two">
                          <label>
                            <span>Owner</span>
                            <input
                              value={
                                formData.owner ||
                                selectedCard?.owner ||
                                selectedCard?.crm?.owner ||
                                ''
                              }
                              disabled
                            />
                          </label>
                          <label>
                            <span>Stage</span>
                            <select
                              value={
                                STAGES.find(
                                  (s) =>
                                    s.toLowerCase() ===
                                    String(
                                      formData.stage || selectedCard?.stage || ''
                                    ).toLowerCase()
                                ) ||
                                formData.stage ||
                                selectedCard?.stage ||
                                'New'
                              }
                              disabled={!canEditCard(selectedCard)}
                              onChange={(e) => {
                                const next = e.target.value;
                                if (next === 'Won') {
                                  openWonModal();
                                } else if (next === 'Lost') {
                                  openLostModal();
                                } else {
                                  updateFormField('stage', next);
                                }
                              }}
                            >
                              {STAGES.filter((s) => canMarkWon || s !== 'Won').map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                              {formData.stage &&
                                !STAGES.some(
                                  (s) => s.toLowerCase() === String(formData.stage).toLowerCase()
                                ) && <option value={formData.stage}>{formData.stage}</option>}
                            </select>
                          </label>
                          {!isPersonalAccount && (
                            <label>
                              <span>Assign to member</span>
                              <select
                                value={formData.assignee}
                                disabled={!canAssignLead || !canEditCard(selectedCard)}
                                onChange={(e) => updateFormField('assignee', e.target.value)}
                              >
                                <option value="">Unassigned</option>
                                {salesMemberOptions.map((m) => {
                                  const isViewer =
                                    m.workspace_role === 'org_viewer' ||
                                    m.workspace_role === 'viewer';
                                  const roleBadge = isViewer ? ' (Viewer)' : ' (Sales Member)';
                                  const uname = cleanUsername(
                                    m.username || m.email || m.display_name
                                  );
                                  return (
                                    <option key={m.user_id} value={m.user_id}>
                                      {uname + roleBadge}
                                    </option>
                                  );
                                })}
                              </select>
                            </label>
                          )}
                        </div>
                      </div>
                      <div className="crm-detail-section crm-detail-section-bottom">
                        <h4>Deal</h4>
                        <div className="crm-detail-grid crm-detail-grid-two">
                          <label>
                            <span>Deal name</span>
                            <input
                              value={formData.dealName}
                              disabled={!canEditCard(selectedCard)}
                              onChange={(e) => updateFormField('dealName', e.target.value)}
                            />
                          </label>
                          <label>
                            <span>Deal value</span>
                            <input
                              type="number"
                              min="0"
                              value={formData.dealValue}
                              disabled={!canEditCard(selectedCard)}
                              onWheel={(e) => e.currentTarget.blur()}
                              onChange={(e) => updateFormField('dealValue', e.target.value)}
                            />
                          </label>
                          <label>
                            <span>Deal probability (%)</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={formData.dealProbability}
                              disabled={!canEditCard(selectedCard)}
                              onWheel={(e) => e.currentTarget.blur()}
                              onChange={(e) => updateFormField('dealProbability', e.target.value)}
                            />
                          </label>
                          <label>
                            <span>Currency</span>
                            <select
                              value={formData.currency || 'USD'}
                              disabled={!canEditCard(selectedCard)}
                              onChange={(e) => updateFormField('currency', e.target.value)}
                            >
                              {CURRENCIES.map((c) => (
                                <option key={c} value={c}>
                                  {c} ({CURRENCY_SYMBOLS[c].trim()})
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            <span>Expected close date</span>
                            <input
                              type="date"
                              min={todayMin}
                              value={formData.closeDate}
                              disabled={!canEditCard(selectedCard)}
                              onChange={(e) => updateFormField('closeDate', e.target.value)}
                            />
                          </label>
                          {formData.stage === 'Won' && (
                            <label>
                              <span>Won value</span>
                              <input
                                type="number"
                                min="0"
                                value={formData.wonValue}
                                disabled={!canEditCard(selectedCard)}
                                onWheel={(e) => e.currentTarget.blur()}
                                onChange={(e) => updateFormField('wonValue', e.target.value)}
                              />
                            </label>
                          )}
                          {formData.stage === 'Won' && (
                            <label>
                              <span>Won date</span>
                              <input
                                type="date"
                                value={formData.wonDate}
                                disabled={!canEditCard(selectedCard)}
                                onChange={(e) => updateFormField('wonDate', e.target.value)}
                              />
                            </label>
                          )}
                          <label>
                            <span>Proposal reference</span>
                            <input
                              value={formData.proposalReference}
                              disabled={!canEditCard(selectedCard)}
                              onChange={(e) => updateFormField('proposalReference', e.target.value)}
                            />
                          </label>
                          <label className="crm-detail-fullwidth">
                            <span>Product/service interest</span>
                            <input
                              value={formData.productInterest}
                              disabled={!canEditCard(selectedCard)}
                              onChange={(e) => updateFormField('productInterest', e.target.value)}
                            />
                          </label>
                          {formData.stage === 'Lost' && (
                            <label className="crm-detail-fullwidth">
                              <span>Lost reason</span>
                              <input
                                value={formData.lostReason}
                                disabled={!canEditCard(selectedCard)}
                                onChange={(e) => updateFormField('lostReason', e.target.value)}
                              />
                            </label>
                          )}
                        </div>
                      </div>
                      <div ref={noteSectionRef} className="crm-detail-section">
                        <div className="crm-section-header">
                          <h4>Notes</h4>
                          <button
                            type="button"
                            className="crm-note-history-btn"
                            onClick={openNoteHistory}
                          >
                            History
                          </button>
                        </div>
                        <div className="crm-detail-notes">
                          <textarea
                            ref={noteInputRef}
                            className={`crm-notes-input${unaddedNoteWarning ? ' crm-input-unadded' : ''}`}
                            placeholder="Write a note about this lead…"
                            rows="3"
                            value={noteInput}
                            disabled={!canEditCard(selectedCard)}
                            onChange={(e) => {
                              setNoteInput(e.target.value);
                              if (unaddedNoteWarning) setUnaddedNoteWarning(false);
                              if (noteError) setNoteError('');
                            }}
                          />
                          <button
                            type="button"
                            className={`crm-notes-add${unaddedNoteWarning ? ' crm-btn-attention' : ''}`}
                            onClick={handleAddNote}
                            disabled={!canEditCard(selectedCard) || !noteInput.trim()}
                          >
                            + Add note
                          </button>
                          {noteError && <em className="crm-field-error">{noteError}</em>}
                          <div className="crm-notes-list">
                            {formData.notes.length === 0 ? (
                              <p className="crm-notes-empty">No notes yet.</p>
                            ) : (
                              [...(Array.isArray(formData.notes) ? formData.notes : [])]
                                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                                .map((n) => (
                                  <div
                                    className={
                                      'crm-note' +
                                      (activeNoteId === n.id ? ' crm-note-active' : '') +
                                      (highlightNoteId === n.id ? ' crm-note-highlight' : '')
                                    }
                                    key={n.id}
                                    onClick={() => {
                                      setActiveNoteId(activeNoteId === n.id ? null : n.id);
                                      if (highlightNoteId === n.id) setHighlightNoteId(null);
                                    }}
                                  >
                                    {highlightNoteId === n.id && (
                                      <div className="crm-note-red-dot" />
                                    )}
                                    {editingNoteId === n.id ? (
                                      <div className="crm-note-edit-container">
                                        <textarea
                                          className="crm-note-edit-input"
                                          rows="3"
                                          value={editNoteText}
                                          disabled={!canEditCard(selectedCard)}
                                          onChange={(e) => setEditNoteText(e.target.value)}
                                          onKeyDown={(e) => {
                                            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                                              e.preventDefault();
                                              if (
                                                canEditCard(selectedCard) &&
                                                editNoteText.trim()
                                              ) {
                                                handleUpdateNote();
                                              }
                                            } else if (e.key === 'Escape') {
                                              e.preventDefault();
                                              cancelEditNote();
                                            }
                                          }}
                                          autoFocus
                                        />
                                        <div className="crm-note-edit-actions">
                                          <button
                                            type="button"
                                            className="crm-note-edit-save"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleUpdateNote();
                                            }}
                                            disabled={
                                              !canEditCard(selectedCard) || !editNoteText.trim()
                                            }
                                          >
                                            <i
                                              className="fas fa-check"
                                              style={{ marginRight: '5px' }}
                                            />
                                            Save
                                          </button>
                                          <button
                                            type="button"
                                            className="crm-note-edit-cancel"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              cancelEditNote();
                                            }}
                                          >
                                            <i
                                              className="fas fa-times"
                                              style={{ marginRight: '5px' }}
                                            />
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="crm-note-content">
                                        <div className="crm-note-header">
                                          <div className="crm-note-meta">
                                            <span className="crm-note-author">
                                              <i
                                                className="fas fa-user-circle"
                                                style={{ marginRight: '5px', color: '#6366f1' }}
                                              />
                                              {n.createdByName ||
                                                (n.createdBy === currentUserId
                                                  ? currentUsername
                                                  : 'User')}
                                            </span>
                                            <span className="crm-note-dot">·</span>
                                            <span className="crm-note-date">
                                              {new Date(n.createdAt).toLocaleString('en-GB')}
                                            </span>
                                          </div>
                                          {canEditCard(selectedCard) &&
                                            (canEditLeadAll || n.createdBy === currentUserId) && (
                                              <div className="crm-note-actions">
                                                <button
                                                  type="button"
                                                  className="crm-note-action-btn crm-note-edit-btn"
                                                  title="Edit note"
                                                  aria-label="Edit note"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    startEditNote(n);
                                                  }}
                                                >
                                                  <i className="fas fa-pen" />
                                                  <span>Edit</span>
                                                </button>
                                                <button
                                                  type="button"
                                                  className="crm-note-action-btn crm-note-delete-btn"
                                                  title="Delete note"
                                                  aria-label="Delete note"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteNote(n.id);
                                                  }}
                                                >
                                                  <i className="fas fa-trash-alt" />
                                                  <span>Delete</span>
                                                </button>
                                              </div>
                                            )}
                                        </div>
                                        <p className="crm-note-text">{n.text}</p>
                                      </div>
                                    )}
                                  </div>
                                ))
                            )}
                          </div>
                        </div>
                      </div>
                      <div ref={taskSectionRef} className="crm-detail-section">
                        <h4>Follow up task</h4>
                        <div className="crm-tasks">
                          <div className="crm-task-input-row">
                            <textarea
                              ref={taskInputRef}
                              className={`crm-task-text${unaddedTaskWarning ? ' crm-input-unadded' : ''}`}
                              placeholder="First line = task title&#10;Following lines = description"
                              rows={3}
                              value={taskInput}
                              disabled={!canEditCard(selectedCard)}
                              onChange={(e) => {
                                setTaskInput(e.target.value);
                                if (unaddedTaskWarning) setUnaddedTaskWarning(false);
                              }}
                            />
                            <input
                              type="date"
                              className="crm-task-date"
                              min={todayMin}
                              value={taskDueDate}
                              disabled={!canEditCard(selectedCard)}
                              onChange={(e) => setTaskDueDate(e.target.value)}
                            />
                          </div>
                          <div className="crm-task-input-row">
                            <select
                              className="crm-task-select"
                              value={taskPriority}
                              disabled={!canEditCard(selectedCard)}
                              onChange={(e) => setTaskPriority(e.target.value)}
                            >
                              <option>Low</option>
                              <option>Medium</option>
                              <option>High</option>
                            </select>
                            <button
                              type="button"
                              className={`crm-task-add${unaddedTaskWarning ? ' crm-btn-attention' : ''}`}
                              onClick={handleAddTask}
                              disabled={!canEditCard(selectedCard) || !taskInput.trim()}
                            >
                              + Add
                            </button>
                          </div>
                          {formData.tasks.length > 0 && (
                            <div className="crm-task-list">
                              {sortCrmTasks(formData.tasks).map((t) => {
                                const status = taskStatus(t);
                                return (
                                  <div
                                    className={`crm-task${status === 'completed' ? ' crm-task-done' : ''}`}
                                    key={t.id}
                                  >
                                    <button
                                      type="button"
                                      className="crm-task-circle"
                                      disabled={!canEditCard(selectedCard)}
                                      title={
                                        status === 'completed'
                                          ? 'Click to revert to In progress'
                                          : 'Click to mark as Completed'
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!canEditCard(selectedCard)) return;
                                        handleUpdateTaskStatus(
                                          t.id,
                                          status === 'completed' ? 'in_progress' : 'completed'
                                        );
                                      }}
                                      aria-label={
                                        status === 'completed'
                                          ? 'Revert task to in progress'
                                          : 'Mark task as completed'
                                      }
                                    />
                                    <span className="crm-task-label">
                                      <span className="crm-task-text-label">
                                        {t.title || t.text}
                                      </span>
                                      {t.description && (
                                        <span className="crm-task-desc">{t.description}</span>
                                      )}
                                      <span className="crm-task-meta">
                                        {status && (
                                          <span
                                            className={`crm-task-status crm-task-status-${status}`}
                                          >
                                            {TASK_STATUS_LABELS[status] || status}
                                          </span>
                                        )}
                                        {t.priority && (
                                          <span
                                            className={`crm-task-priority crm-task-priority-${String(t.priority).toLowerCase()}`}
                                          >
                                            {t.priority}
                                          </span>
                                        )}
                                        {taskAssigneeName(t) && <span>{taskAssigneeName(t)}</span>}
                                        {t.dueDate && (
                                          <span>
                                            Due{' '}
                                            {new Date(t.dueDate + 'T00:00:00').toLocaleDateString(
                                              'en-GB'
                                            )}
                                          </span>
                                        )}
                                      </span>
                                    </span>
                                    <div className="crm-task-actions">
                                      <select
                                        className="crm-task-status-select"
                                        value={
                                          status === 'overdue' ? t.status || 'pending' : status
                                        }
                                        disabled={!canEditCard(selectedCard)}
                                        onChange={(e) =>
                                          handleUpdateTaskStatus(t.id, e.target.value)
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <option value="pending">Pending</option>
                                        <option value="in_progress">In progress</option>
                                        <option value="completed">Completed</option>
                                        <option value="cancelled">Cancelled</option>
                                      </select>
                                      <button
                                        type="button"
                                        className="crm-task-delete"
                                        disabled={!canEditCard(selectedCard)}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteTask(t.id);
                                        }}
                                      >
                                        <i className="fas fa-trash" />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </fieldset>
                    {submitError && (
                      <div className="crm-form-error">
                        <i className="fas fa-exclamation-circle" />
                        <span>{submitError}</span>
                      </div>
                    )}
                    <div className="crm-modal-actions crm-detail-actions-footer">
                      <button type="button" className="crm-modal-cancel" onClick={closeModal}>
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="crm-modal-submit"
                        disabled={!canEditCard(selectedCard)}
                      >
                        Save changes
                      </button>
                    </div>
                  </form>
                ) : activeDetailTab === 'activity' ? (
                  <div className="crm-detail-activity">
                    {formData.activities.length === 0 ? (
                      <p className="crm-activity-empty">No activity recorded for this lead yet.</p>
                    ) : (
                      <ol className="crm-activity-list">
                        {[...formData.activities].reverse().map((a) => {
                          const activityKey = a.id || a.createdAt;
                          const isOpen = activeActivityId === activityKey;
                          const hasDiff =
                            a.type !== 'LEAD_EXPORTED' &&
                            (a.previousValue !== undefined || a.newValue !== undefined);
                          const isTask = isTaskActivity(a.type);
                          const task = isTask ? taskFromActivity(a) : null;
                          return (
                            <li
                              key={activityKey}
                              className={`crm-activity-item${isOpen ? ' crm-activity-item-open' : ''}`}
                              role="button"
                              tabIndex={0}
                              aria-expanded={isOpen}
                              onClick={() => setActiveActivityId(isOpen ? null : activityKey)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setActiveActivityId(isOpen ? null : activityKey);
                                }
                              }}
                            >
                              <div className="crm-activity-rail">
                                <span
                                  className={`crm-activity-dot crm-activity-dot-${String(a.type || 'updated').toLowerCase()}`}
                                />
                                <span className="crm-activity-line" />
                              </div>
                              <div className="crm-activity-content">
                                <p className="crm-activity-message">
                                  {a.message || a.description || 'Lead exported'}
                                </p>
                                <span className="crm-activity-meta">
                                  {a.createdByName || a.createdBy || 'Unknown'} ·{' '}
                                  {new Date(
                                    a.createdAt || a.created_at || Date.now()
                                  ).toLocaleString('en-GB')}
                                  {hasDiff && (
                                    <span
                                      className={`crm-activity-caret${isOpen ? ' crm-activity-caret-open' : ''}`}
                                    >
                                      <i className="fas fa-chevron-down" />
                                    </span>
                                  )}
                                </span>
                                {isOpen &&
                                  hasDiff &&
                                  (isTask ? (
                                    <div className="crm-activity-diff crm-activity-diff-task">
                                      <div className="crm-activity-diff-row">
                                        <span className="crm-activity-diff-row-label">Prev</span>
                                        <span className="crm-activity-diff-row-value">
                                          {formatActivityValue(a.previousValue)}
                                        </span>
                                      </div>
                                      <div className="crm-activity-diff-row">
                                        <span className="crm-activity-diff-row-label">New</span>
                                        <span className="crm-activity-diff-row-value">
                                          {a.type === 'TASK_CREATED' && task
                                            ? task.title ||
                                              task.text ||
                                              formatActivityValue(a.newValue)
                                            : formatActivityValue(a.newValue)}
                                        </span>
                                      </div>
                                      <div className="crm-activity-diff-row">
                                        <span className="crm-activity-diff-row-label">
                                          Priority
                                        </span>
                                        <span className="crm-activity-diff-row-value">
                                          {task?.priority || '—'}
                                        </span>
                                      </div>
                                      <div className="crm-activity-diff-row">
                                        <span className="crm-activity-diff-row-label">Date</span>
                                        <span className="crm-activity-diff-row-value">
                                          {formatActivityDate(task?.dueDate)}
                                        </span>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="crm-activity-diff">
                                      <span className="crm-activity-diff-label">Changed</span>
                                      <span className="crm-activity-diff-from">
                                        {formatActivityValue(a.previousValue)}
                                      </span>
                                      <span className="crm-activity-diff-arrow">→</span>
                                      <span className="crm-activity-diff-to">
                                        {formatActivityValue(a.newValue)}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                    )}
                  </div>
                ) : activeDetailTab === 'meetings' ? (
                  <div className="crm-detail-meetings">
                    <div className="crm-meetings-action-bar">
                      <div className="crm-meetings-filter-pills">
                        <button
                          type="button"
                          className={`crm-meeting-filter-pill ${leadMeetingFilter === 'all' ? 'active' : ''}`}
                          onClick={() => setLeadMeetingFilter('all')}
                        >
                          All ({categorizedLeadMeetings.all.length})
                        </button>
                        <button
                          type="button"
                          className={`crm-meeting-filter-pill ${leadMeetingFilter === 'upcoming' ? 'active' : ''}`}
                          onClick={() => setLeadMeetingFilter('upcoming')}
                        >
                          Upcoming ({categorizedLeadMeetings.upcoming.length})
                        </button>
                        <button
                          type="button"
                          className={`crm-meeting-filter-pill ${leadMeetingFilter === 'in_progress' ? 'active' : ''}`}
                          onClick={() => setLeadMeetingFilter('in_progress')}
                        >
                          In Progress ({categorizedLeadMeetings.in_progress.length})
                        </button>
                        <button
                          type="button"
                          className={`crm-meeting-filter-pill ${leadMeetingFilter === 'completed' ? 'active' : ''}`}
                          onClick={() => setLeadMeetingFilter('completed')}
                        >
                          Past ({categorizedLeadMeetings.completed.length})
                        </button>
                        <button
                          type="button"
                          className={`crm-meeting-filter-pill ${leadMeetingFilter === 'cancelled' ? 'active' : ''}`}
                          onClick={() => setLeadMeetingFilter('cancelled')}
                        >
                          Cancelled ({categorizedLeadMeetings.cancelled.length})
                        </button>
                      </div>

                      <button
                        type="button"
                        className="crm-meetings-schedule-btn"
                        onClick={handleScheduleMeetingForLead}
                        title="Schedule a meeting with this lead"
                      >
                        <i className="fas fa-plus" />
                        <span>Schedule Meeting</span>
                      </button>
                    </div>

                    {isLoadingLeadMeetings ? (
                      <div className="crm-meetings-loading">
                        <i className="fas fa-circle-notch fa-spin" />
                        <span>Loading meetings...</span>
                      </div>
                    ) : leadMeetings.length === 0 ? (
                      <div className="crm-meetings-empty">
                        <div className="crm-meetings-empty-icon">
                          <i className="far fa-calendar-alt" />
                        </div>
                        <h5>No meetings found</h5>
                        <p>
                          There are no upcoming, in-progress, completed, or cancelled meetings
                          recorded for this lead.
                        </p>
                      </div>
                    ) : (categorizedLeadMeetings[leadMeetingFilter] || []).length === 0 ? (
                      <div className="crm-meetings-empty crm-meetings-empty-filtered">
                        <div className="crm-meetings-empty-icon">
                          <i className="far fa-calendar-times" />
                        </div>
                        <h5>No {leadMeetingFilter.replace('_', ' ')} meetings</h5>
                        <p>There are no meetings matching this status for this lead.</p>
                        <button
                          type="button"
                          className="crm-meetings-clear-filter-btn"
                          onClick={() => setLeadMeetingFilter('all')}
                        >
                          View all meetings ({leadMeetings.length})
                        </button>
                      </div>
                    ) : (
                      <div className="crm-meetings-list">
                        {(categorizedLeadMeetings[leadMeetingFilter] || []).map((m) => {
                          const effStatus = getEffectiveMeetingStatus(m);
                          const isInProg = effStatus === 'in_progress' || isMeetingInProgress(m);
                          const canJoin = canJoinMeeting(m, currentTime);
                          const msUntilOpen = getMsUntilMeetingAccessOpen(m, currentTime);
                          const countdownText = formatMeetingCountdown(msUntilOpen);
                          const meetUrl =
                            m.conferencingUrl ||
                            m.conferencing_url ||
                            m.meetingUrl ||
                            m.googleMeetUrl ||
                            m.hangoutLink ||
                            '';
                          const isCopied = copiedMeetingId === m.id;

                          let statusLabel = 'Upcoming';
                          let statusClass = 'status-confirmed';
                          if (effStatus === 'in_progress') {
                            statusLabel = 'In Progress';
                            statusClass = 'status-in-progress';
                          } else if (effStatus === 'completed') {
                            statusLabel = 'Completed';
                            statusClass = 'status-completed';
                          } else if (effStatus === 'cancelled') {
                            statusLabel = 'Cancelled';
                            statusClass = 'status-cancelled';
                          }

                          return (
                            <div key={m.id || m.uid} className={`crm-meeting-card ${statusClass}`}>
                              <div className="crm-meeting-card-header">
                                <div className="crm-meeting-card-title-group">
                                  <h5 className="crm-meeting-card-title">
                                    {m.title || m.meetingType?.name || 'Video Call'}
                                  </h5>
                                  <div className="crm-meeting-card-tags">
                                    {m.meetingType?.name && m.title !== m.meetingType.name && (
                                      <span className="crm-meeting-tag crm-meeting-tag-type">
                                        {m.meetingType.name}
                                      </span>
                                    )}
                                    {(m.campaignName ||
                                      m.campaign_name ||
                                      m.preMeetingBrief?.campaignName) && (
                                      <span
                                        className="crm-meeting-campaign-tag"
                                        title="Source Campaign"
                                      >
                                        <i className="fas fa-bullhorn" />{' '}
                                        {m.campaignName ||
                                          m.campaign_name ||
                                          m.preMeetingBrief?.campaignName}
                                      </span>
                                    )}
                                    {(m.isGroupMeeting ||
                                      m.is_group_meeting ||
                                      m.preMeetingBrief?.isGroupMeeting) && (
                                      <span
                                        className="crm-meeting-tag"
                                        style={{
                                          background: '#f5f3ff',
                                          color: '#7c3aed',
                                          border: '1px solid #ddd6fe',
                                        }}
                                      >
                                        <i className="fas fa-users" /> Group Session
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <span className={`crm-meeting-status-badge ${statusClass}`}>
                                  <span
                                    className={`crm-meeting-status-dot ${effStatus === 'in_progress' ? 'crm-dot-pulse' : ''}`}
                                  />
                                  {statusLabel}
                                </span>
                              </div>

                              <div className="crm-meeting-card-meta">
                                <div className="crm-meeting-meta-item">
                                  <i className="far fa-calendar-alt crm-meta-date-icon" />
                                  <span className="crm-meeting-meta-text">
                                    {formatMeetingDate(m.startTime)}
                                  </span>
                                </div>
                                <div className="crm-meeting-meta-item">
                                  <i className="far fa-clock crm-meta-time-icon" />
                                  <span className="crm-meeting-meta-text">
                                    {formatMeetingTimeRange(
                                      m.startTime,
                                      m.endTime,
                                      m.durationMinutes || m.meetingType?.durationMinutes || 30
                                    )}
                                  </span>
                                </div>
                                {(m.hostName || m.hostEmail) && (
                                  <div className="crm-meeting-meta-item">
                                    <i className="far fa-user crm-meta-host-icon" />
                                    <span className="crm-meeting-meta-text">
                                      Host: <strong>{m.hostName || m.hostEmail}</strong>
                                    </span>
                                  </div>
                                )}
                              </div>

                              {meetUrl &&
                                (effStatus === 'upcoming' ||
                                  effStatus === 'in_progress' ||
                                  effStatus === 'confirmed') && (
                                  <div className="crm-meeting-link-bar">
                                    {isInProg ? (
                                      <a
                                        href={meetUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="crm-meeting-join-link is-live"
                                        title="Meeting is in progress - Join now!"
                                      >
                                        <i className="fas fa-video" />
                                        <span>Join Now</span>
                                        <i className="fas fa-external-link-alt crm-external-icon" />
                                      </a>
                                    ) : canJoin ? (
                                      <a
                                        href={meetUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="crm-meeting-join-link"
                                        title="Join Google Meet"
                                      >
                                        <i className="fas fa-video" />
                                        <span>Join Google Meet</span>
                                        <i className="fas fa-external-link-alt crm-external-icon" />
                                      </a>
                                    ) : (
                                      <div
                                        className="crm-meeting-join-locked"
                                        title="Meeting room access opens 15 minutes before the scheduled start time"
                                      >
                                        <i className="fas fa-lock" />
                                        <span>Join opens in {countdownText}</span>
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      className={`crm-meeting-copy-link-btn ${isCopied ? 'copied' : ''}`}
                                      onClick={() => handleCopyMeetingLink(m.id, meetUrl)}
                                      title="Copy meeting link"
                                    >
                                      <i className={isCopied ? 'fas fa-check' : 'far fa-copy'} />
                                      <span>{isCopied ? 'Copied!' : 'Copy Link'}</span>
                                    </button>
                                  </div>
                                )}

                              {(effStatus === 'cancelled' || m.status === 'cancelled') &&
                                (m.cancellationReason || m.cancellation_reason) && (
                                  <div className="crm-meeting-cancelled-box">
                                    <i className="fas fa-info-circle" />
                                    <div className="crm-meeting-cancelled-text">
                                      <strong>Cancellation Reason:</strong>
                                      <span>{m.cancellationReason || m.cancellation_reason}</span>
                                    </div>
                                  </div>
                                )}

                              {(m.guestNotes || m.agenda || m.aiBriefing || m.aiSummary) && (
                                <div className="crm-meeting-notes-box">
                                  {m.aiBriefing && (
                                    <div className="crm-meeting-ai-briefing">
                                      <div className="crm-meeting-ai-briefing-header">
                                        <i className="fas fa-magic" />
                                        <span>AI Meeting Briefing</span>
                                      </div>
                                      <p>{m.aiBriefing}</p>
                                    </div>
                                  )}
                                  {m.aiSummary && (
                                    <div className="crm-meeting-ai-summary">
                                      <div className="crm-meeting-ai-summary-header">
                                        <i className="fas fa-sparkles" />
                                        <span>Post-Meeting AI Summary</span>
                                      </div>
                                      <p>{m.aiSummary}</p>
                                    </div>
                                  )}
                                  {(m.guestNotes || m.agenda) && (
                                    <div className="crm-meeting-guest-notes">
                                      <strong>Notes / Agenda:</strong>
                                      <p>{m.guestNotes || m.agenda}</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="crm-detail-evidence">
                    <EvidencePanel
                      lead={{
                        ...selectedCard,
                        ...formData,
                        field_locks: {
                          ...(selectedCard?.field_locks || {}),
                          ...(formData?.field_locks || {}),
                        },
                        provenance_summary: {
                          ...(selectedCard?.provenance_summary || {}),
                          ...(formData?.provenance_summary || {}),
                        },
                      }}
                      apiEndpointPrefix="/lead-pipeline"
                      initialTab={evidenceDrawerTab}
                      highlightField={evidenceHighlightField}
                      onOpenLockModal={handleOpenLockModal}
                      canEdit={canEditCard(selectedCard)}
                      refreshTrigger={evidenceRefreshKey}
                      onLeadUpdated={(unlockedFieldName) => {
                        setEvidenceRefreshKey((k) => k + 1);
                        if (unlockedFieldName) {
                          const norm =
                            String(unlockedFieldName).toLowerCase() === 'emails'
                              ? 'email'
                              : String(unlockedFieldName).toLowerCase() === 'phones'
                                ? 'phone'
                                : ['full_name', 'fullname', 'contact_name', 'contactname'].includes(
                                      String(unlockedFieldName).toLowerCase()
                                    )
                                  ? 'name'
                                  : ['job_title', 'jobtitle', 'title', 'role'].includes(
                                        String(unlockedFieldName).toLowerCase()
                                      )
                                    ? 'jobTitle'
                                    : ['company_name', 'companyname'].includes(
                                          String(unlockedFieldName).toLowerCase()
                                        )
                                      ? 'company'
                                      : unlockedFieldName;
                          setSelectedCard((prev) => {
                            if (!prev) return prev;
                            const nextLocks = { ...(prev.field_locks || {}) };
                            delete nextLocks[unlockedFieldName];
                            delete nextLocks[norm];
                            return { ...prev, field_locks: nextLocks };
                          });
                          setFormData((prev) => {
                            if (!prev) return prev;
                            const nextLocks = { ...(prev.field_locks || {}) };
                            delete nextLocks[unlockedFieldName];
                            delete nextLocks[norm];
                            return { ...prev, field_locks: nextLocks };
                          });
                          setCards((prevCards) =>
                            prevCards.map((c) => {
                              if (c.id === selectedCard?.id) {
                                const nextLocks = { ...(c.field_locks || {}) };
                                delete nextLocks[unlockedFieldName];
                                delete nextLocks[norm];
                                return { ...c, field_locks: nextLocks };
                              }
                              return c;
                            })
                          );
                        } else if (selectedCard?.id) {
                          axios
                            .get(`/lead-pipeline/${selectedCard.id}`)
                            .then((res) => {
                              if (res.data?.data) {
                                const d = res.data.data;
                                const freshLocks = d.field_locks || {};
                                setSelectedCard((prev) => ({
                                  ...prev,
                                  ...d,
                                  field_locks: { ...(prev?.field_locks || {}), ...freshLocks },
                                  provenance_summary: {
                                    ...(prev?.provenance_summary || {}),
                                    ...(d.provenance_summary || {}),
                                  },
                                }));
                                setFormData((prev) => ({
                                  ...prev,
                                  field_locks: { ...(prev?.field_locks || {}), ...freshLocks },
                                  provenance_summary: {
                                    ...(prev?.provenance_summary || {}),
                                    ...(d.provenance_summary || {}),
                                  },
                                }));
                              }
                            })
                            .catch(() => null);
                        }
                        fetchCards();
                      }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="crm-modal-header">
                  <div>
                    <h3>Add new lead</h3>
                    <p className="crm-modal-subtitle">
                      Create a prospect and drop them straight into the pipeline.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="crm-modal-close"
                    onClick={closeModal}
                    aria-label="Close"
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
                <form
                  className="crm-modal-form"
                  onSubmit={handleFormSubmit}
                  onKeyDown={(e) => {
                    if (
                      e.key === 'Enter' &&
                      e.target.tagName !== 'TEXTAREA' &&
                      e.target.type !== 'submit'
                    ) {
                      e.preventDefault();
                    }
                  }}
                >
                  <div className="crm-form-grid">
                    <label>
                      <span>
                        Contact name <span className="crm-required-star">*</span>
                      </span>
                      {errors.name && <em className="crm-field-error">{errors.name}</em>}
                      <input
                        type="text"
                        placeholder="e.g. Eleanor Whitfield"
                        value={formData.name}
                        onChange={(event) => updateFormField('name', event.target.value)}
                      />
                    </label>
                    <label>
                      <span>Job title </span>
                      {errors.jobTitle && <em className="crm-field-error">{errors.jobTitle}</em>}
                      <input
                        type="text"
                        placeholder="e.g. Chief Technology Officer"
                        value={formData.jobTitle}
                        onChange={(event) => updateFormField('jobTitle', event.target.value)}
                      />
                    </label>
                    <label className="crm-form-fullwidth">
                      <span>
                        Company <span className="crm-required-star">*</span>
                      </span>
                      {errors.company && <em className="crm-field-error">{errors.company}</em>}
                      <input
                        type="text"
                        placeholder="e.g. Northwind Labs"
                        value={formData.company}
                        onChange={(event) => updateFormField('company', event.target.value)}
                      />
                    </label>
                    <label className="crm-form-fullwidth">
                      <span>
                        Email{' '}
                        {formData.source === 'Email' && (
                          <span className="crm-required-star">*</span>
                        )}
                      </span>
                      <MultiEmailField
                        emails={formData.emails || [formData.email || '']}
                        onChange={(next) => updateFormField('emails', next)}
                        errors={errors}
                      />
                    </label>
                    <label className="crm-form-fullwidth">
                      <span>Phone & country</span>
                      <MultiCountryPhoneField
                        phones={formData.phones || [formData.phone || '']}
                        country={formData.country}
                        onChange={(next) => updateFormField('phones', next)}
                        onCountryChange={(v) => updateFormField('country', v)}
                        errors={errors}
                      />
                    </label>
                    <label>
                      <span>Source</span>
                      <select
                        value={normalizeSource(formData.source)}
                        onChange={(event) => updateFormField('source', event.target.value)}
                      >
                        <option value="Website">Website</option>
                        <option value="LinkedIn">LinkedIn</option>
                        <option value="Email">Email</option>
                      </select>
                    </label>
                    <label>
                      <span>Stage</span>
                      <select
                        value={
                          STAGES.find(
                            (s) => s.toLowerCase() === String(formData.stage || '').toLowerCase()
                          ) ||
                          formData.stage ||
                          'New'
                        }
                        onChange={(event) => {
                          const nextStage = event.target.value;
                          const autoScore = calculateLeadScore(
                            {
                              ...formData,
                              stage: nextStage,
                            },
                            customStages
                          ).totalScore;
                          updateFormField('stage', nextStage);
                          updateFormField('leadScore', autoScore);
                        }}
                      >
                        {STAGES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>
                        LinkedIn URL{' '}
                        {formData.source === 'LinkedIn' && (
                          <span className="crm-required-star">*</span>
                        )}
                      </span>
                      {errors.linkedinUrl && (
                        <em className="crm-field-error">{errors.linkedinUrl}</em>
                      )}
                      <input
                        type="text"
                        placeholder="https://linkedin.com/in/name"
                        value={formData.linkedinUrl}
                        onChange={(event) => updateFormField('linkedinUrl', event.target.value)}
                      />
                    </label>
                    <label>
                      <span>
                        Company website{' '}
                        {formData.source === 'Website' && (
                          <span className="crm-required-star">*</span>
                        )}
                      </span>
                      {errors.companyWebsite && (
                        <em className="crm-field-error">{errors.companyWebsite}</em>
                      )}
                      <input
                        type="text"
                        placeholder="https://company.com"
                        value={formData.companyWebsite}
                        onChange={(event) => updateFormField('companyWebsite', event.target.value)}
                        onBlur={(event) => {
                          const v = event.target.value.trim();
                          if (v && !/^https?:\/\//i.test(v))
                            updateFormField('companyWebsite', `https://${v}`);
                        }}
                      />
                    </label>
                    <label>
                      <span>Industry</span>
                      <input
                        type="text"
                        placeholder="e.g. SaaS"
                        value={formData.industry}
                        onChange={(event) => updateFormField('industry', event.target.value)}
                      />
                    </label>
                    <label>
                      <span>Currency</span>
                      <select
                        value={formData.currency || 'USD'}
                        onChange={(event) => updateFormField('currency', event.target.value)}
                      >
                        {CURRENCIES.map((c) => (
                          <option key={c} value={c}>
                            {c} ({CURRENCY_SYMBOLS[c].trim()})
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Deal value</span>
                      <input
                        type="number"
                        min="0"
                        placeholder="0"
                        value={formData.dealValue}
                        onWheel={(e) => e.currentTarget.blur()}
                        onChange={(event) => updateFormField('dealValue', event.target.value)}
                      />
                    </label>
                    <label>
                      <span>Expected close date</span>
                      <input
                        type="date"
                        min={todayMin}
                        value={formData.closeDate}
                        onChange={(event) => updateFormField('closeDate', event.target.value)}
                      />
                    </label>
                  </div>
                  {submitError && <p className="crm-form-error">{submitError}</p>}
                  <div className="crm-modal-actions">
                    <button type="button" className="crm-modal-cancel" onClick={closeModal}>
                      Cancel
                    </button>
                    <button type="submit" className="crm-modal-submit">
                      Add lead
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Field Locking Modal */}
      <LockFieldModal
        isOpen={isLockModalOpen}
        onClose={() => setIsLockModalOpen(false)}
        fieldName={lockModalTarget.fieldName}
        fieldLabel={lockModalTarget.fieldLabel}
        currentValue={lockModalTarget.currentValue}
        onConfirm={handleConfirmLock}
      />

      {pendingDeleteCard && (
        <div className="crm-modal-overlay" onMouseDown={backdropDismiss(handleCancelDelete)}>
          <div className="crm-confirm-modal" onClick={(event) => event.stopPropagation()}>
            <div className="crm-confirm-icon">
              <i className="fas fa-trash" />
            </div>
            <h3>Delete this lead?</h3>
            <p className="crm-confirm-name">{pendingDeleteCard.name || 'Unnamed lead'}</p>
            <p className="crm-confirm-warning">
              This will permanently remove the lead from your list and database.
            </p>
            <div className="crm-confirm-actions">
              <button type="button" className="crm-modal-cancel" onClick={handleCancelDelete}>
                Cancel
              </button>
              <button type="button" className="crm-confirm-delete" onClick={handleConfirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingBulkDelete && (
        <div
          className="crm-modal-overlay"
          onMouseDown={backdropDismiss(() => setPendingBulkDelete(false))}
        >
          <div className="crm-confirm-modal" onClick={(event) => event.stopPropagation()}>
            <div className="crm-confirm-icon">
              <i className="fas fa-trash" />
            </div>
            <h3>Delete {selectedRowIds.length} leads?</h3>
            <p className="crm-confirm-warning">
              This will permanently remove the selected leads from your list and database.
            </p>
            <div className="crm-confirm-actions">
              <button
                type="button"
                className="crm-modal-cancel"
                onClick={() => setPendingBulkDelete(false)}
              >
                Cancel
              </button>
              <button type="button" className="crm-confirm-delete" onClick={handleBulkDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingBulkAssign && (
        <div
          className="crm-modal-overlay"
          onMouseDown={backdropDismiss(() => setPendingBulkAssign(false))}
        >
          <div className="crm-confirm-modal" onClick={(event) => event.stopPropagation()}>
            <div className="crm-confirm-icon">
              <i className="fas fa-user-plus" />
            </div>
            <h3>Assign {selectedRowIds.length} leads</h3>
            <p className="crm-confirm-warning">Choose a member to assign the selected leads to.</p>
            <select
              className="crm-bulk-select"
              value={bulkAssignTo}
              onChange={(event) => setBulkAssignTo(event.target.value)}
            >
              <option value="">Unassigned</option>
              {salesMemberOptions.map((m) => {
                const isViewer = m.workspace_role === 'org_viewer' || m.workspace_role === 'viewer';
                const roleBadge = isViewer ? ' (Viewer)' : ' (Sales Member)';
                const uname = cleanUsername(m.username || m.email || m.display_name);
                return (
                  <option key={m.user_id} value={m.user_id}>
                    {uname + roleBadge}
                  </option>
                );
              })}
            </select>
            <div className="crm-confirm-actions">
              <button
                type="button"
                className="crm-modal-cancel"
                onClick={() => setPendingBulkAssign(false)}
              >
                Cancel
              </button>
              <button type="button" className="crm-modal-submit" onClick={handleBulkAssign}>
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingBulkFollowUp && (
        <div
          className="crm-modal-overlay"
          onMouseDown={backdropDismiss(() => setPendingBulkFollowUp(false))}
        >
          <div className="crm-confirm-modal" onClick={(event) => event.stopPropagation()}>
            <div className="crm-confirm-icon">
              <i className="fas fa-calendar-plus" />
            </div>
            <h3>Set follow-up date for {selectedRowIds.length} leads</h3>
            <p className="crm-confirm-warning">
              Pick a date for the next follow-up of the selected leads.
            </p>
            <input
              type="date"
              className="crm-bulk-select"
              min={todayMin}
              value={bulkFollowUpDate}
              onChange={(event) => setBulkFollowUpDate(event.target.value)}
            />
            <textarea
              className="crm-task-text"
              placeholder="Follow-up task (optional)"
              rows={3}
              value={bulkFollowUpTask}
              onChange={(event) => setBulkFollowUpTask(event.target.value)}
            />
            <select
              className="crm-task-select"
              value={bulkFollowUpPriority}
              onChange={(event) => setBulkFollowUpPriority(event.target.value)}
            >
              <option>Low</option>
              <option>Medium</option>
              <option>High</option>
            </select>
            <div className="crm-confirm-actions">
              <button
                type="button"
                className="crm-modal-cancel"
                onClick={() => setPendingBulkFollowUp(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="crm-modal-submit"
                disabled={!bulkFollowUpDate}
                onClick={handleBulkFollowUp}
              >
                Set date
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingBulkStage && (
        <div
          className="crm-modal-overlay"
          onMouseDown={backdropDismiss(() => setPendingBulkStage(false))}
        >
          <div className="crm-confirm-modal" onClick={(event) => event.stopPropagation()}>
            <div className="crm-confirm-icon">
              <i className="fas fa-tag" />
            </div>
            <h3>Change stage for {selectedRowIds.length} leads</h3>
            <p className="crm-confirm-warning">Pick the stage to move the selected leads to.</p>
            <select
              className="crm-bulk-select"
              value={bulkStage}
              onChange={(event) => setBulkStage(event.target.value)}
            >
              <option value="">Select a stage</option>
              {STAGES.filter((stage) => canMarkWon || stage !== 'Won').map((stage) => (
                <option key={stage} value={stage}>
                  {stage}
                </option>
              ))}
            </select>
            <div className="crm-confirm-actions">
              <button
                type="button"
                className="crm-modal-cancel"
                onClick={() => setPendingBulkStage(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="crm-modal-submit"
                disabled={!bulkStage}
                onClick={handleBulkStage}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkStageModal === 'won' && (
        <div
          className="crm-modal-overlay"
          onMouseDown={backdropDismiss(() => setBulkStageModal(''))}
        >
          <div className="crm-won-modal" onClick={(event) => event.stopPropagation()}>
            <div className="crm-lost-header">
              <h3>Mark {selectedRowIds.length} leads as won</h3>
              <button
                type="button"
                className="crm-modal-close"
                onClick={() => setBulkStageModal('')}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <label className="crm-lost-field">
              <span>Won date</span>
              <input
                type="date"
                value={wonForm.wonDate}
                onChange={(e) => setWonForm((f) => ({ ...f, wonDate: e.target.value }))}
              />
            </label>
            <label className="crm-lost-field">
              <span>Final deal value</span>
              <input
                type="number"
                min="0"
                step="any"
                placeholder="e.g. 25000"
                value={wonForm.value}
                onChange={(e) => setWonForm((f) => ({ ...f, value: e.target.value }))}
              />
            </label>
            {wonError && <em className="crm-field-error">{wonError}</em>}
            <div className="crm-lost-actions">
              <button
                type="button"
                className="crm-modal-cancel"
                onClick={() => setBulkStageModal('')}
              >
                Cancel
              </button>
              <button type="button" className="crm-won-confirm" onClick={handleBulkConfirmWon}>
                Mark as won
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkStageModal === 'lost' && (
        <div
          className="crm-modal-overlay"
          onMouseDown={backdropDismiss(() => setBulkStageModal(''))}
        >
          <div className="crm-lost-modal" onClick={(event) => event.stopPropagation()}>
            <div className="crm-lost-header">
              <h3>Mark {selectedRowIds.length} leads as lost</h3>
              <button
                type="button"
                className="crm-modal-close"
                onClick={() => setBulkStageModal('')}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <label className="crm-lost-field">
              <span>Lost date</span>
              <input
                type="date"
                value={lostForm.lostDate}
                onChange={(e) => setLostForm((f) => ({ ...f, lostDate: e.target.value }))}
              />
            </label>
            <label className="crm-lost-field">
              <span>Reason of loss</span>
              <select
                value={lostForm.reason}
                onChange={(e) => setLostForm((f) => ({ ...f, reason: e.target.value }))}
              >
                <option value="">Select a reason…</option>
                <option value="Timing out">No response</option>
                <option value="Timing out">Not interested</option>
                <option value="Timing out">No budget</option>
                <option value="Timing out">Wrong timing</option>
                <option value="Timing out">Chose competitor</option>
                <option value="Timing out">Not qualified</option>
                <option value="Timing out">Invalid contact</option>
                <option value="Out of budget">Duplicate lead</option>
                <option value="Timing out">Other</option>
              </select>
            </label>
            {lostError && <em className="crm-field-error">{lostError}</em>}
            <label className="crm-lost-field">
              <span>Additional context</span>
              <textarea
                rows="3"
                placeholder="e.g. Decision maker moved on, budget freeze…"
                value={lostForm.context}
                onChange={(e) => setLostForm((f) => ({ ...f, context: e.target.value }))}
              />
            </label>
            <div className="crm-lost-actions">
              <button
                type="button"
                className="crm-modal-cancel"
                onClick={() => setBulkStageModal('')}
              >
                Cancel
              </button>
              <button type="button" className="crm-confirm-delete" onClick={handleBulkConfirmLost}>
                Mark as lost
              </button>
            </div>
          </div>
        </div>
      )}

      {showLostModal && selectedCard && (
        <div
          className="crm-modal-overlay crm-top-modal-overlay"
          onMouseDown={backdropDismiss(closeLostModal)}
        >
          <div className="crm-lost-modal" onClick={(event) => event.stopPropagation()}>
            <div className="crm-lost-header">
              <h3>Mark as lost</h3>
              <button
                type="button"
                className="crm-modal-close"
                onClick={closeLostModal}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <p className="crm-lost-name">{formData.name || selectedCard.name || 'Unnamed lead'}</p>
            <p className="crm-lost-owner">
              Owner: {formData.owner || selectedCard.owner || currentUsername}
            </p>
            <label className="crm-lost-field">
              <span>Lost date</span>
              <input
                type="date"
                value={lostForm.lostDate}
                onChange={(e) => setLostForm((f) => ({ ...f, lostDate: e.target.value }))}
              />
            </label>
            <label className="crm-lost-field">
              <span>Reason of loss</span>
              <select
                value={lostForm.reason}
                onChange={(e) => setLostForm((f) => ({ ...f, reason: e.target.value }))}
              >
                <option value="">Select a reason…</option>
                <option value="No response">No response</option>
                <option value="Not interested">Not interested</option>
                <option value="No budget">No budget</option>
                <option value="Wrong timing">Wrong timing</option>
                <option value="Chose competitor">Chose competitor</option>
                <option value="Not qualified">Not qualified</option>
                <option value="Invalid contact">Invalid contact</option>
                <option value="Duplicate lead">Duplicate lead</option>
                <option value="Other">Other</option>
              </select>
            </label>
            {lostError && <em className="crm-field-error">{lostError}</em>}
            <label className="crm-lost-field">
              <span>Additional context</span>
              <textarea
                rows="3"
                placeholder="e.g. Decision maker moved on, budget freeze…"
                value={lostForm.context}
                onChange={(e) => setLostForm((f) => ({ ...f, context: e.target.value }))}
              />
            </label>
            <div className="crm-lost-actions">
              <button type="button" className="crm-modal-cancel" onClick={closeLostModal}>
                Cancel
              </button>
              <button type="button" className="crm-confirm-delete" onClick={handleConfirmLost}>
                Mark as lost
              </button>
            </div>
          </div>
        </div>
      )}

      {showWonModal && selectedCard && (
        <div
          className="crm-modal-overlay crm-top-modal-overlay"
          onMouseDown={backdropDismiss(closeWonModal)}
        >
          <div className="crm-won-modal" onClick={(event) => event.stopPropagation()}>
            <div className="crm-lost-header">
              <h3>Mark as won</h3>
              <button
                type="button"
                className="crm-modal-close"
                onClick={closeWonModal}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <p className="crm-lost-name">{formData.name || selectedCard.name || 'Unnamed lead'}</p>
            <p className="crm-lost-owner">
              Owner: {formData.owner || selectedCard.owner || currentUsername}
            </p>
            <label className="crm-lost-field">
              <span>Won date</span>
              <input
                type="date"
                value={wonForm.wonDate}
                onChange={(e) => setWonForm((f) => ({ ...f, wonDate: e.target.value }))}
              />
            </label>
            <label className="crm-lost-field">
              <span>Final deal value</span>
              <input
                type="number"
                min="0"
                step="any"
                placeholder="e.g. 25000"
                value={wonForm.value}
                onChange={(e) => setWonForm((f) => ({ ...f, value: e.target.value }))}
              />
            </label>
            {wonError && <em className="crm-field-error">{wonError}</em>}
            <div className="crm-lost-actions">
              <button type="button" className="crm-modal-cancel" onClick={closeWonModal}>
                Cancel
              </button>
              <button type="button" className="crm-won-confirm" onClick={handleConfirmWon}>
                Mark as won
              </button>
            </div>
          </div>
        </div>
      )}

      {showNoteHistory && (
        <div className="crm-modal-overlay" onMouseDown={backdropDismiss(closeNoteHistory)}>
          <div className="crm-note-history-modal" onClick={(event) => event.stopPropagation()}>
            <div className="crm-lost-header">
              <h3>Deleted notes</h3>
              <button
                type="button"
                className="crm-modal-close"
                onClick={closeNoteHistory}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            {noteHistoryLoading ? (
              <p className="crm-note-history-empty">Loading…</p>
            ) : deletedNotes.length === 0 ? (
              <p className="crm-note-history-empty">No deleted notes.</p>
            ) : (
              <div className="crm-note-history-list">
                {deletedNotes.map((n) => (
                  <div className="crm-note-history-item" key={n.id}>
                    <p className="crm-note-history-text">{n.text}</p>
                    <span className="crm-note-history-meta">
                      {n.createdByName || ''} · {new Date(n.createdAt).toLocaleString('en-GB')}
                      {n.deletedAt && (
                        <> · deleted {new Date(n.deletedAt).toLocaleString('en-GB')}</>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="crm-modal-overlay" onMouseDown={backdropDismiss(closeImportModal)}>
          <div className="crm-import-modal" onClick={(event) => event.stopPropagation()}>
            <div className="crm-lost-header">
              <h3>Import from Prospect Finder</h3>
              <button
                type="button"
                className="crm-modal-close"
                onClick={closeImportModal}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <p className="crm-lost-name">Choose saved prospects to add to your lead pipeline.</p>

            {savedProspects.length > 0 && (
              <div className="crm-import-search-bar">
                <i className="fas fa-search crm-import-search-icon" />
                <input
                  type="text"
                  className="crm-import-search-input"
                  placeholder="Search by name, company, title or email..."
                  value={importSearchTerm}
                  onChange={(e) => setImportSearchTerm(e.target.value)}
                  autoFocus
                />
                {importSearchTerm && (
                  <button
                    type="button"
                    className="crm-import-search-clear"
                    onClick={() => setImportSearchTerm('')}
                    aria-label="Clear search"
                  >
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>
            )}

            {savedProspects.length > 0 &&
              filteredImportProspects.length > 0 &&
              (() => {
                const selectableProspects = filteredImportProspects.filter(
                  (sp) => !isProspectAlreadyInPipeline(sp)
                );
                const allSelectableChecked =
                  selectableProspects.length > 0 &&
                  selectableProspects.every((sp) => selectedImportIds.includes(sp.importId));

                return (
                  <div className="crm-import-header-controls">
                    <label className="crm-import-select-all">
                      <input
                        type="checkbox"
                        checked={allSelectableChecked}
                        disabled={selectableProspects.length === 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const allIds = Array.from(
                              new Set([
                                ...selectedImportIds,
                                ...selectableProspects.map((sp) => sp.importId),
                              ])
                            );
                            setSelectedImportIds(allIds);
                          } else {
                            const visibleIds = new Set(
                              selectableProspects.map((sp) => sp.importId)
                            );
                            setSelectedImportIds(
                              selectedImportIds.filter((id) => !visibleIds.has(id))
                            );
                          }
                        }}
                      />
                      <span>
                        Select all{' '}
                        {importSearchTerm
                          ? `matching (${selectableProspects.length})`
                          : `(${selectableProspects.length})`}
                      </span>
                    </label>
                    {selectedImportIds.length > 0 && (
                      <span className="crm-import-selected-count">
                        {selectedImportIds.length} selected
                      </span>
                    )}
                  </div>
                );
              })()}

            {savedProspects.length === 0 ? (
              <p className="crm-import-empty">
                No prospects available to import yet. Save prospects from Prospect Finder first.
              </p>
            ) : filteredImportProspects.length === 0 ? (
              <div className="crm-import-empty-search">
                <i className="fas fa-search" />
                <p>No prospects found matching "{importSearchTerm}"</p>
                <button
                  type="button"
                  className="crm-import-clear-search-btn"
                  onClick={() => setImportSearchTerm('')}
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className="crm-import-list">
                {filteredImportProspects.map((sp) => {
                  const alreadyInPipeline = isProspectAlreadyInPipeline(sp);
                  const isChecked = selectedImportIds.includes(sp.importId);
                  const coName = sp.company_name || sp.companyName || sp.company || '';
                  return (
                    <label
                      className={`crm-import-row${isChecked && !alreadyInPipeline ? ' is-selected' : ''}${alreadyInPipeline ? ' is-disabled' : ''}`}
                      key={sp.id}
                      title={
                        alreadyInPipeline ? 'This lead already exists in your pipeline' : undefined
                      }
                    >
                      <input
                        type="checkbox"
                        checked={alreadyInPipeline ? false : isChecked}
                        disabled={alreadyInPipeline}
                        onChange={() => {
                          if (!alreadyInPipeline) {
                            toggleImportSelection(sp.importId);
                          }
                        }}
                      />
                      <div className="crm-import-row-body">
                        <div className="crm-import-row-title-row">
                          <span className="crm-import-row-name">{sp.name}</span>
                          {alreadyInPipeline && (
                            <span className="crm-import-already-badge">
                              <i className="fas fa-check-circle" /> Already in pipeline
                            </span>
                          )}
                        </div>
                        <span className="crm-import-row-meta">
                          {[
                            sp.job_title || sp.jobTitle || sp.title,
                            coName,
                            emailToString(sp.email || sp.email_addresses),
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            {importMessage && <p className="crm-import-message">{importMessage}</p>}
            <div className="crm-lost-actions">
              <button type="button" className="crm-modal-cancel" onClick={closeImportModal}>
                Cancel
              </button>
              <button
                type="button"
                className="crm-modal-submit"
                disabled={selectedImportIds.length === 0 || importLoading}
                onClick={handleImportSelected}
              >
                {importLoading ? 'Importing…' : `Import (${selectedImportIds.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showConflictModal && importConflicts.length > 0 && (
        <div className="crm-modal-overlay" onMouseDown={backdropDismiss(closeConflictModal)}>
          <div className="crm-lost-modal" onClick={(event) => event.stopPropagation()}>
            <div className="crm-lost-header">
              <h3>Possible duplicates</h3>
              <button
                type="button"
                className="crm-modal-close"
                onClick={closeConflictModal}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <p className="crm-lost-name" style={{ marginBottom: '12px' }}>
              {!canConfirmDuplicate
                ? 'Possible duplicate found. This lead appears to match an existing lead in this workspace. You do not have permission to create a possible duplicate. Please contact the lead owner or an authorised user to proceed.'
                : (() => {
                    const hasNameMatch = importConflicts.some(
                      (c) =>
                        c.matchedBy === 'name' ||
                        c.matchedBy === 'both' ||
                        (c.matchReason && c.matchReason.toLowerCase().includes('name'))
                    );
                    const hasEmailMatch = importConflicts.some(
                      (c) =>
                        c.matchedBy === 'email' ||
                        c.matchedBy === 'both' ||
                        (c.matchReason && c.matchReason.toLowerCase().includes('email')) ||
                        (!c.matchedBy && !c.matchReason && c.email)
                    );

                    if (pendingAddCustomer) {
                      if (hasNameMatch && hasEmailMatch) {
                        return (
                          <>
                            A customer with this <strong>name and email</strong> already exists in
                            your pipeline. Adding it again would create a duplicate.
                          </>
                        );
                      }
                      if (hasNameMatch) {
                        return (
                          <>
                            A customer with this <strong>name</strong> already exists in your
                            pipeline. Adding it again would create a duplicate.
                          </>
                        );
                      }
                      return (
                        <>
                          A customer with this <strong>email</strong> already exists in your
                          pipeline. Adding it again would create a duplicate.
                        </>
                      );
                    }

                    if (hasNameMatch && hasEmailMatch) {
                      return (
                        <>
                          These leads already match existing <strong>names or emails</strong> in
                          your pipeline. Importing them again would create duplicates.
                        </>
                      );
                    }
                    if (hasNameMatch) {
                      return (
                        <>
                          These leads already have matching <strong>names</strong> in your pipeline.
                          Importing them again would create duplicates.
                        </>
                      );
                    }
                    return (
                      <>
                        These leads already have matching <strong>emails</strong> in your pipeline.
                        Importing them again would create duplicates.
                      </>
                    );
                  })()}
            </p>
            <div className="crm-import-list" style={{ maxHeight: '240px', overflowY: 'auto' }}>
              {importConflicts.map((c, i) => {
                const matchText = (() => {
                  if (c.matchReason) return c.matchReason;
                  if (c.matchedBy === 'name' && c.existingName) {
                    return `Matches existing: ${c.existingName}`;
                  }
                  if (c.matchedBy === 'email' && (c.existingEmail || c.email)) {
                    return `Matches existing: ${c.existingEmail || emailToString(c.email)}`;
                  }
                  if (c.matchedBy === 'both') {
                    const namePart = c.existingName || c.name;
                    const emailPart = c.existingEmail || emailToString(c.email);
                    return `Matches existing: ${namePart}${emailPart ? ` (${emailPart})` : ''}`;
                  }
                  if (c.existingName) {
                    return `Matches existing: ${c.existingName}`;
                  }
                  if (c.existingEmail || c.email) {
                    return `Matches existing: ${c.existingEmail || emailToString(c.email)}`;
                  }
                  return 'Matches existing record';
                })();

                return (
                  <div
                    className="crm-import-row"
                    key={`${c.id}-${i}`}
                    style={{ cursor: 'default' }}
                  >
                    <div className="crm-import-row-body">
                      <span className="crm-import-row-name">{c.name || 'Unnamed lead'}</span>
                      <span className="crm-import-row-meta">{matchText}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            {importMessage && <p className="crm-import-message">{importMessage}</p>}
            {canConfirmDuplicate ? (
              <div className="crm-confirm-actions">
                <button type="button" className="crm-modal-cancel" onClick={closeConflictModal}>
                  {pendingAddCustomer ? 'Decline' : 'Skip duplicate'}
                </button>
                <button
                  type="button"
                  className="crm-modal-submit"
                  disabled={confirmingImport}
                  onClick={handleConfirmImport}
                >
                  {confirmingImport
                    ? 'Importing…'
                    : pendingAddCustomer
                      ? 'Add anyway'
                      : `Import anyway (${importConflicts.length})`}
                </button>
              </div>
            ) : (
              <div className="crm-confirm-actions">
                <button type="button" className="crm-modal-submit" onClick={closeConflictModal}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      <PipelineStageModal
        isOpen={showStageModal}
        onClose={() => setShowStageModal(false)}
        cards={cards}
        onStagesUpdated={(updatedStages) => {
          setCustomStages(updatedStages);
          fetchCards(false);
          if (Array.isArray(updatedStages)) {
            const newNames = updatedStages.map((s) => (typeof s === 'string' ? s : s.name));
            setCards((prev) =>
              prev.map((c) => {
                const matched = newNames.find(
                  (n) => n.toLowerCase() === String(c.stage || '').toLowerCase()
                );
                return matched ? { ...c, stage: matched } : c;
              })
            );
            setFormData((prev) => {
              if (!prev.stage) return prev;
              const matched = newNames.find(
                (n) => n.toLowerCase() === String(prev.stage).toLowerCase()
              );
              return matched ? { ...prev, stage: matched } : prev;
            });
          }
        }}
        currentStages={
          customStages.length > 0
            ? customStages
            : DEFAULT_STAGES.map((s, i) => ({
                id: `default-${i}`,
                name: s,
                is_compulsory: ['New', 'Contacted', 'Qualified', 'Won', 'Lost'].includes(s),
                stage_order: i,
              }))
        }
        isOwner={isEffectiveOwner || isEffectiveAdmin}
      />
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast((t) => ({ ...t, show: false }))}
        />
      )}
    </div>
  );
};

export default CustomerManagementSystem;
